import React, { useEffect, useState, useRef } from "react";
import Layout from "../../Component/Layout";
import axios from "axios";
import Select from "react-select";
import CharacteristicsModal from "../../Component/CharacteristicsModal ";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PropTypes from 'prop-types';
import { PURCHASE_API, MASTER_API } from "../../apiConfig";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useFieldPermissions } from "../../utils/useFieldPermissions";
import MaskedValue from "../../Component/MaskedValue";
import { FileText } from 'lucide-react';
import { authHeaders } from "../../utils/authHeaders";

// Native <input type="date"> renders its picker/text in the OS/browser's
// regional date format (mm-dd-yyyy on a US-locale machine, dd-mm-yyyy on
// others, etc.). This component always displays and accepts dd-mm-yyyy
// regardless of locale, while still emitting a plain yyyy-mm-dd string via
// onChange (e.target.value), so it's a drop-in replacement for the places
// that used <input type="date" value={...} onChange={...} />.
const DmyDateInput = ({ value, onChange, disabled, readOnly, style, showCalendar = false, ...rest }) => {
    const toDmy = (iso) => {
        if (!iso) return "";
        const m = String(iso).substring(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
        return m ? `${m[3]}-${m[2]}-${m[1]}` : "";
    };
    const toIso = (dmy) => {
        const m = dmy.match(/^(\d{2})-(\d{2})-(\d{4})$/);
        return m ? `${m[3]}-${m[2]}-${m[1]}` : "";
    };

    const [text, setText] = React.useState(toDmy(value));
    const pickerRef = React.useRef(null);

    React.useEffect(() => {
        setText(toDmy(value));
    }, [value]);

    const handleChange = (e) => {
        const digits = e.target.value.replace(/[^\d]/g, "").substring(0, 8); // ddmmyyyy
        let formatted = digits;
        if (digits.length > 4) {
            formatted = `${digits.substring(0, 2)}-${digits.substring(2, 4)}-${digits.substring(4, 8)}`;
        } else if (digits.length > 2) {
            formatted = `${digits.substring(0, 2)}-${digits.substring(2, 4)}`;
        }
        setText(formatted);

        if (digits.length === 8) {
            const iso = toIso(formatted);
            if (iso && onChange) onChange({ target: { value: iso } });
        } else if (digits.length === 0 && onChange) {
            onChange({ target: { value: "" } });
        }
    };

    const openCalendar = () => {
        const el = pickerRef.current;
        if (!el || disabled || readOnly) return;
        try {
            if (typeof el.showPicker === "function") el.showPicker();
            else { el.focus(); el.click(); }
        } catch (err) {
            el.focus();
        }
    };

    const textInput = (
        <input
            type="text"
            inputMode="numeric"
            placeholder="dd-mm-yyyy"
            value={text}
            disabled={disabled}
            readOnly={readOnly}
            onChange={handleChange}
            maxLength={10}
            style={showCalendar ? { ...style, width: "100%", paddingRight: 24, boxSizing: "border-box" } : style}
            {...rest}
        />
    );

    if (!showCalendar || disabled || readOnly) return textInput;

    // Calendar popup: a hidden native date input drives the picker; its
    // yyyy-mm-dd value is passed to onChange and shown here as dd-mm-yyyy.
    return (
        <span style={{ position: "relative", display: "inline-block", width: style?.width || "100%" }}>
            {textInput}
            <button
                type="button"
                onClick={openCalendar}
                title="Select date"
                tabIndex={-1}
                style={{
                    position: "absolute", right: 4, top: "50%", transform: "translateY(-50%)",
                    border: "none", background: "transparent", padding: 0, cursor: "pointer",
                    display: "flex", alignItems: "center", color: "#64748b"
                }}
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
            </button>
            <input
                ref={pickerRef}
                type="date"
                tabIndex={-1}
                aria-hidden="true"
                value={/^\d{4}-\d{2}-\d{2}/.test(String(value || "")) ? String(value).substring(0, 10) : ""}
                onChange={(e) => { if (onChange) onChange({ target: { value: e.target.value } }); }}
                style={{ position: "absolute", left: 0, bottom: 0, width: 1, height: 1, opacity: 0, pointerEvents: "none", border: 0, padding: 0 }}
            />
        </span>
    );
};

// Hide the native up/down spin buttons on all number inputs (Chrome/Safari/Edge
// use the webkit pseudo-elements below; Firefox uses the moz-appearance rule).
if (typeof document !== "undefined" && !document.getElementById("no-number-spinner-style")) {
    const styleTag = document.createElement("style");
    styleTag.id = "no-number-spinner-style";
    styleTag.innerHTML = `
        input[type="number"]::-webkit-outer-spin-button,
        input[type="number"]::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }
        input[type="number"] {
            -moz-appearance: textfield;
        }
    `;
    document.head.appendChild(styleTag);
}

const USER_MANUAL_SCOPE = {
    moduleName: "Production",
    process: "Shop Floor Control",
    subProcess: "Confirmation"
};
const USER_MANUAL_LIST_API = `${MASTER_API}/user-manual/list`;

// Reads module-level permissions (can_view / can_create / can_edit /
// can_convert) for SFC_CONFIRMATION from the logged-in user's stored
// permissions — same array authController.js returns at login, same
// pattern PlannedOrder.js / ProductionOrder.js use. Admins get an empty
// `permissions` array from the backend by design, so they're granted full
// access here rather than being locked out by an "empty permissions"
// default.
const getModulePermissions = (moduleId) => {
    try {
        const user = JSON.parse(localStorage.getItem("user"));
        if (!user) return { canView: false, canCreate: false, canEdit: false, canConvert: false };
        if (user.role === "admin") return { canView: true, canCreate: true, canEdit: true, canConvert: true };

        const perm = (user.permissions || []).find((p) => p.module_id === moduleId);
        return {
            canView: perm?.can_view === 1,
            canCreate: perm?.can_create === 1,
            canEdit: perm?.can_edit === 1,
            canConvert: perm?.can_convert === 1
        };
    } catch (e) {
        console.error("Error reading user permissions:", e);
        return { canView: false, canCreate: false, canEdit: false, canConvert: false };
    }
};

// Compact react-select styles shared by the Storage Location dropdowns.
const compactSelectStyles = ({ disabled = false, invalid = false } = {}) => ({
    control: (base, state) => ({
        ...base, minHeight: 28, height: 28, fontSize: 11, cursor: "pointer",
        borderColor: invalid ? "#dc2626" : (state.isFocused ? "#94a3b8" : "#d1d5db"),
        boxShadow: "none",
        background: disabled ? "#f1f5f9" : (invalid ? "#fef2f2" : "#fff"),
    }),
    valueContainer: (base) => ({ ...base, padding: "0 6px", flexWrap: "nowrap", overflow: "hidden" }),
    placeholder: (base) => ({ ...base, fontSize: 11, whiteSpace: "nowrap", margin: 0 }),
    singleValue: (base) => ({ ...base, fontSize: 11, margin: 0, whiteSpace: "nowrap" }),
    input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: 11 }),
    indicatorsContainer: (base) => ({ ...base, height: 26 }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (base) => ({ ...base, padding: 3 }),
    clearIndicator: (base) => ({ ...base, padding: 3 }),
    option: (base) => ({ ...base, fontSize: 11, padding: "5px 8px" }),
    noOptionsMessage: (base) => ({ ...base, fontSize: 11, padding: "6px 8px" }),
    menu: (base) => ({ ...base, minWidth: 170, fontSize: 11 }),
    menuList: (base) => ({ ...base, maxHeight: 200 }),
    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
});

/* ─────────────────────────────────────────────
   STORAGE LOCATION LABEL HELPER
───────────────────────────────────────────── */
const getStorageLocationName = (loc) =>
    (typeof loc === "string" ? loc : (loc?.storage_location || loc?.storage_location_name || loc?.storage_bin_name || ""));

const getStorageLocationLabel = (loc) => {
    if (typeof loc === "string") return loc;
    const name = getStorageLocationName(loc);
    const code = loc?.storage_code || loc?.storageCode;
    return code ? `${code} - ${name}` : name;
};

/* ─────────────────────────────────────────────
   INSPECTION AUTO-CHECK HELPER
   Compares specified value vs entered actual and
   returns "Passed" / "NC" / "" (when not determinable)
───────────────────────────────────────────── */
const getAutoCheckedValue = (specifiedValue, actualValue) => {
    const spec = (specifiedValue ?? "").toString().trim();
    const actual = (actualValue ?? "").toString().trim();

    if (!actual) return "";
    if (!spec || spec.toLowerCase() === "null") return "";

    const specNum = parseFloat(spec);
    const actualNum = parseFloat(actual);
    const bothNumeric = !isNaN(specNum) && !isNaN(actualNum) && /^-?\d*\.?\d+$/.test(spec) && /^-?\d*\.?\d+$/.test(actual);

    const matches = bothNumeric
        ? specNum === actualNum
        : spec.toLowerCase() === actual.toLowerCase();

    return matches ? "Passed" : "NC";
};

/* ─────────────────────────────────────────────
   BATCH SELECTION MODAL  
───────────────────────────────────────────── */
const BatchSelectionModal = ({
    visible,
    onClose,
    onConfirm,
    batches = [],
    selectedBatchNumbers = []
}) => {
    const [tempSelected, setTempSelected] = useState([]);

    useEffect(() => {
        if (visible) setTempSelected(selectedBatchNumbers || []);
    }, [visible, selectedBatchNumbers]);

    const handleToggle = (batchNo) => {
        setTempSelected(prev =>
            prev.includes(batchNo) ? prev.filter(b => b !== batchNo) : [...prev, batchNo]
        );
    };

    const handleSelectAll = (e) => {
        setTempSelected(e.target.checked ? batches.map(b => b.batch_number) : []);
    };

    if (!visible) return null;

    return (
        <div style={styles.modalBackdrop}>
            <div style={{ ...styles.modal, width: "450px", maxWidth: "90vw", height: "auto", maxHeight: "85vh", padding: 0, display: "flex", flexDirection: "column" }}>
                <div style={{ padding: "16px 20px", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center", background: "#f9fafb", borderRadius: "8px 8px 0 0" }}>
                    <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "700", color: "#111827" }}>Select Batches</h3>
                    <button onClick={onClose} style={{ background: "transparent", border: "none", fontSize: "20px", color: "#6b7280", cursor: "pointer", lineHeight: "1" }}>×</button>
                </div>
                <div style={{ padding: "10px 20px", borderBottom: "1px solid #e5e7eb", background: "#fff", fontSize: "13px", display: "flex", justifyContent: "space-between", color: "#374151" }}>
                    <label style={{ display: "flex", alignItems: "center", cursor: "pointer", fontWeight: "600" }}>
                        <input type="checkbox" checked={batches.length > 0 && tempSelected.length === batches.length} onChange={handleSelectAll} style={{ marginRight: "8px", accentColor: "#2563eb" }} />
                        Select All
                    </label>
                    <span style={{ color: "#6b7280" }}>{tempSelected.length} selected</span>
                </div>
                <div style={{ padding: "10px 20px", overflowY: "auto", flex: 1, background: "#f9fafb" }}>
                    {batches.length === 0 ? (
                        <div style={{ textAlign: "center", padding: "30px", color: "#9ca3af", fontStyle: "italic" }}>No batches available for this product.</div>
                    ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            {batches.map((b, i) => {
                                const isSelected = tempSelected.includes(b.batch_number);
                                return (
                                    <label key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: "#fff", border: isSelected ? "1px solid #2563eb" : "1px solid #e5e7eb", borderRadius: "6px", cursor: "pointer", boxShadow: "0 1px 2px rgba(0,0,0,0.05)" }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                            <input type="checkbox" checked={isSelected} onChange={() => handleToggle(b.batch_number)} style={{ accentColor: "#2563eb", width: "16px", height: "16px" }} />
                                            <div style={{ fontSize: "14px", fontWeight: "600", color: "#1f2937" }}>{b.batch_number}</div>
                                        </div>
                                        <div style={{ textAlign: "right" }}>
                                            <div style={{ fontSize: "11px", color: "#6b7280", textTransform: "uppercase", fontWeight: "600" }}>Available</div>
                                            <div style={{ fontSize: "14px", fontWeight: "600", color: isSelected ? "#2563eb" : "#374151" }}>{b.quantity}</div>
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    )}
                </div>
                <div style={{ padding: "16px 20px", borderTop: "1px solid #e5e7eb", background: "#fff", display: "flex", justifyContent: "flex-end", gap: "10px", borderRadius: "0 0 8px 8px" }}>
                    <button onClick={onClose} style={{ padding: "8px 16px", background: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db", borderRadius: "6px", cursor: "pointer", fontSize: "13px", fontWeight: "500" }}>Cancel</button>
                    <button onClick={() => onConfirm(tempSelected)} style={{ padding: "8px 20px", background: "#2563eb", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}>Confirm Selection</button>
                </div>
            </div>
        </div>
    );
};

BatchSelectionModal.propTypes = {
    visible: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    batches: PropTypes.array,
    selectedBatchNumbers: PropTypes.array
};

/* ─────────────────────────────────────────────
   MULTI-BATCH PICKER
───────────────────────────────────────────── */
const MultiBatchPicker = ({ batches = [], consumed = 0, selectedBatches = [], onChange, isViewMode = false, uom = "" }) => {
    const [open, setOpen] = React.useState(false);
    const [draft, setDraft] = React.useState([]);         
    const [qtyInputs, setQtyInputs] = React.useState({}); 
    const [error, setError] = React.useState("");

    // Batch numbers are NOT guaranteed unique on their own — the same
    // batch_number can appear as multiple distinct rows in `stock` (e.g.
    // split across storage locations). stock_id (the stock table's PK) is
    // the only reliable identity for a row, so all selection state below is
    // keyed by rowKey(b), which falls back to batch_number only if a row is
    // somehow missing a stock_id.
    const rowKey = (b) => (b.stock_id !== undefined && b.stock_id !== null) ? `id:${b.stock_id}` : `bn:${b.batch_number}`;

    const totalSelected = draft.reduce((s, b) => s + (parseFloat(qtyInputs[rowKey(b)]) || 0), 0);

    const openPanel = () => {
        const initDraft = selectedBatches.length > 0 ? [...selectedBatches] : [];
        const initQtys = {};

        if (initDraft.length === 1) {
            // Single batch already selected: keep it in sync with whatever
            // Consumed currently is (capped by what's actually available),
            // instead of replaying the stale qty from when it was first picked.
            const b = initDraft[0];
            const available = parseFloat(b.quantity) || 0;
            const synced = available > 0 ? Math.min(consumed, available) : consumed;
            initQtys[rowKey(b)] = String(synced || "");
        } else {
            // Multiple batches: preserve individual splits as-is, but clamp the
            // total down to the current Consumed value if it now exceeds it.
            let runningTotal = 0;
            initDraft.forEach(b => {
                const q = parseFloat(b.qty ?? b.quantity ?? 0) || 0;
                const capped = Math.max(0, Math.min(q, consumed - runningTotal));
                runningTotal += capped;
                initQtys[rowKey(b)] = String(capped || "");
            });
        }

        setDraft(initDraft);
        setQtyInputs(initQtys);
        setError("");
        setOpen(true);
    };

    const toggleBatch = (batch) => {
        const key = rowKey(batch);
        const exists = draft.find(d => rowKey(d) === key);
        if (exists) {
            setDraft(prev => prev.filter(d => rowKey(d) !== key));
            setQtyInputs(prev => { const n = { ...prev }; delete n[key]; return n; });
        } else {
            setDraft(prev => [...prev, batch]);
            const used = draft.reduce((s, d) => s + (parseFloat(qtyInputs[rowKey(d)]) || 0), 0);
            const remaining = Math.max(0, consumed - used);
            const defaultQty = Math.min(parseFloat(batch.quantity) || 0, remaining);
            setQtyInputs(prev => ({ ...prev, [key]: String(defaultQty || "") }));
        }
        setError("");
    };

    const handleQtyChange = (key, val) => {
        setQtyInputs(prev => ({ ...prev, [key]: val }));
        setError("");
    };

    const handleConfirm = () => {
        // eslint-disable-next-line no-unused-vars
        let sum = 0;
        for (const b of draft) {
            const q = parseFloat(qtyInputs[rowKey(b)]) || 0;
            //eslint-disable-next-line no-unused-vars
            sum += q;
        }
        const result = draft.map(b => ({
            stock_id: b.stock_id,
            batch_number: b.batch_number,
            qty: parseFloat(qtyInputs[rowKey(b)]),
            quantity: b.quantity,
            storage_location: b.storage_location || b.storage_bin || "",
        }));
        onChange(result);
        setOpen(false);
    };

    const label = selectedBatches.length === 0
        ? "Select Batch"
        : selectedBatches.map(b => `${b.batch_number}(${b.qty})`).join(", ");

    const btnStyle = {
        width: "100%", padding: "3px 7px", fontSize: 11, border: "1px solid #cbd5e1",
        borderRadius: 4, background: selectedBatches.length ? "#eff6ff" : "#f8fafc",
        color: selectedBatches.length ? "#1d4ed8" : "#64748b",
        cursor: "pointer", textAlign: "left", whiteSpace: "nowrap",
        overflow: "hidden", textOverflow: "ellipsis", height: 28, boxSizing: "border-box",
    };

    if (isViewMode) {
        if (selectedBatches.length === 0) {
            return <span style={{ fontSize: 11, color: "#94a3b8" }}>—</span>;
        }
        if (selectedBatches.length === 1) {
            return <span style={{ fontSize: 11, color: "#334155", fontWeight: 600 }}>{selectedBatches[0].batch_number}</span>;
        }
        return (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 11, color: "#334155", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {selectedBatches[0].batch_number} +{selectedBatches.length - 1}
                </span>
                <button
                    type="button"
                    title="View all batches"
                    onClick={() => setOpen(true)}
                    style={{ flexShrink: 0, width: 22, height: 22, padding: 0, border: "1px solid #cbd5e1", borderRadius: 4, background: "#f8fafc", color: "#475569", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12 }}
                >
                    ⋯
                </button>

                {open && (
                    <div style={{
                        position: "fixed", inset: 0, zIndex: 9999,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        background: "rgba(0,0,0,0.35)"
                    }} onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
                        <div style={{
                            background: "#fff", borderRadius: 8, width: 420, maxWidth: "95vw",
                            maxHeight: "80vh", display: "flex", flexDirection: "column",
                            boxShadow: "0 8px 32px rgba(0,0,0,0.18)", overflow: "hidden"
                        }}>
                            <div style={{ padding: "12px 16px", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center", background: "#f8fafc" }}>
                                <div style={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>Selected Batches</div>
                                <button onClick={() => setOpen(false)} style={{ background: "none", border: "none", fontSize: 18, color: "#94a3b8", cursor: "pointer" }}>×</button>
                            </div>
                            <div style={{ overflowY: "auto", flex: 1, padding: "8px 16px" }}>
                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                                    <thead>
                                        <tr style={{ background: "#f1f5f9" }}>
                                            <th style={{ padding: "6px 8px", textAlign: "left", fontWeight: 600, color: "#64748b", borderBottom: "1px solid #e2e8f0" }}>Batch No</th>
                                            <th style={{ padding: "6px 8px", textAlign: "right", fontWeight: 600, color: "#64748b", borderBottom: "1px solid #e2e8f0" }}>Qty</th>
                                            <th style={{ padding: "6px 8px", textAlign: "left", fontWeight: 600, color: "#64748b", borderBottom: "1px solid #e2e8f0" }}>UOM</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedBatches.map((b, i) => (
                                            <tr key={i} style={{ borderBottom: "1px solid #f1f5f9", background: i % 2 === 0 ? "#fff" : "#fafbfc" }}>
                                                <td style={{ padding: "6px 8px", fontWeight: 600, color: "#1e293b" }}>{b.batch_number}</td>
                                                <td style={{ padding: "6px 8px", textAlign: "right", color: "#16a34a", fontWeight: 600 }}>{b.qty ?? b.quantity ?? ""}</td>
                                                <td style={{ padding: "6px 8px", color: "#475569" }}>{uom || "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div style={{ padding: "10px 16px", borderTop: "1px solid #e5e7eb", display: "flex", justifyContent: "flex-end", background: "#f8fafc" }}>
                                <button onClick={() => setOpen(false)} style={{ padding: "6px 14px", background: "#1e293b", color: "#fff", border: "none", borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Close</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div style={{ position: "relative" }}>
            <button style={btnStyle} onClick={openPanel} title={label}>{label}</button>

            {open && (
                <div style={{
                    position: "fixed", inset: 0, zIndex: 9999,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    background: "rgba(0,0,0,0.35)"
                }} onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
                    <div style={{
                        background: "#fff", borderRadius: 8, width: 520, maxWidth: "95vw",
                        maxHeight: "80vh", display: "flex", flexDirection: "column",
                        boxShadow: "0 8px 32px rgba(0,0,0,0.18)", overflow: "hidden"
                    }}>
                        <div style={{ padding: "12px 16px", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center", background: "#f8fafc" }}>
                            <div>
                                <div style={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>Select Batches</div>
                                <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                                    Planned Qty: <b>{consumed}</b> &nbsp;|&nbsp; Selected total: <b style={{ color: "#16a34a" }}>{totalSelected}</b>
                                </div>
                            </div>
                            <button onClick={() => setOpen(false)} style={{ background: "none", border: "none", fontSize: 18, color: "#94a3b8", cursor: "pointer" }}>×</button>
                        </div>

                        <div style={{ overflowY: "auto", flex: 1, padding: "8px 16px" }}>
                            {batches.length === 0 ? (
                                <div style={{ textAlign: "center", padding: 30, color: "#94a3b8", fontStyle: "italic" }}>No batches available in stock</div>
                            ) : (
                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                                    <thead>
                                        <tr style={{ background: "#f1f5f9" }}>
                                            <th style={{ padding: "6px 8px", textAlign: "left", fontWeight: 600, color: "#64748b", borderBottom: "1px solid #e2e8f0" }}></th>
                                            <th style={{ padding: "6px 8px", textAlign: "left", fontWeight: 600, color: "#64748b", borderBottom: "1px solid #e2e8f0" }}>Batch No</th>
                                            <th style={{ padding: "6px 8px", textAlign: "right", fontWeight: 600, color: "#64748b", borderBottom: "1px solid #e2e8f0" }}>Available</th>
                                            <th style={{ padding: "6px 8px", textAlign: "left", fontWeight: 600, color: "#64748b", borderBottom: "1px solid #e2e8f0" }}>Storage Location</th>
                                            <th style={{ padding: "6px 8px", textAlign: "right", fontWeight: 600, color: "#64748b", borderBottom: "1px solid #e2e8f0" }}>Use Qty</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {batches.map((b) => {
                                            const key = rowKey(b);
                                            const isChecked = !!draft.find(d => rowKey(d) === key);
                                            return (
                                                <tr key={key} style={{ background: isChecked ? "#eff6ff" : (batches.indexOf(b) % 2 === 0 ? "#fff" : "#fafbfc"), borderBottom: "1px solid #f1f5f9" }}>
                                                    <td style={{ padding: "6px 8px" }}>
                                                        <input type="checkbox" checked={isChecked} onChange={() => toggleBatch(b)}
                                                            style={{ accentColor: "#2563eb", width: 14, height: 14 }} />
                                                    </td>
                                                    <td style={{ padding: "6px 8px", fontWeight: 600, color: "#1e293b" }}>{b.batch_number}</td>
                                                    <td style={{ padding: "6px 8px", textAlign: "right", color: "#16a34a", fontWeight: 600 }}>{b.quantity}</td>
                                                    <td style={{ padding: "6px 8px", color: "#475569" }}>{b.storage_location ? getStorageLocationLabel(b) : "—"}</td>
                                                    <td style={{ padding: "6px 8px", textAlign: "right" }}>
                                                        {isChecked ? (
                                                            <input
                                                                type="number"
                                                                min={0}
                                                                max={b.quantity}
                                                                value={qtyInputs[key] ?? ""}
                                                                onChange={(e) => handleQtyChange(key, e.target.value)}
                                                                style={{ width: 72, padding: "2px 5px", fontSize: 12, border: "1px solid #93c5fd", borderRadius: 4, height: 26, textAlign: "right", outline: "none" }}
                                                            />
                                                        ) : (
                                                            <span style={{ color: "#cbd5e1" }}>—</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        {error && (
                            <div style={{ padding: "6px 16px", background: "#fef2f2", color: "#dc2626", fontSize: 12, borderTop: "1px solid #fecaca" }}>{error}</div>
                        )}
                        <div style={{ padding: "10px 16px", borderTop: "1px solid #e5e7eb", display: "flex", justifyContent: "flex-end", gap: 8, background: "#f8fafc" }}>
                            <button onClick={() => setOpen(false)} style={{ padding: "6px 14px", background: "#f1f5f9", border: "1px solid #cbd5e1", borderRadius: 5, fontSize: 12, cursor: "pointer", color: "#374151" }}>Cancel</button>
                            <button onClick={handleConfirm} style={{ padding: "6px 16px", background: "#1e293b", color: "#fff", border: "none", borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Confirm</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

/* ─────────────────────────────────────────────
   COMPONENTS TABLE 
───────────────────────────────────────────── */
const ComponentsTable = ({ rows, baseUnit = 1, actualQty = 0, componentAttributes = {}, onConsumeChange, onScrapChange, onBatchChange, onComponentAttributeChange, batchData = {}, storageBins = [], onStorageLocationChange, isViewMode = false, resetKey, scrapHeadings = [] }) => {
    // Scrap headings come from Scrap Configuration (matched by the PO's
    // process). If the process has none configured, the three legacy columns are kept.
    const dynamicScrap = Array.isArray(scrapHeadings) && scrapHeadings.length > 0;
    const scrapFields = dynamicScrap
        ? scrapHeadings.map(n => ({ key: n, label: n }))
        : [{ key: "setup_scrap", label: "Setup Scrap" }, { key: "process_scrap", label: "Process Scrap" }, { key: "wirebreak_scrap", label: "Wirebreak Scrap" }];
    const getScrap = (r, k) => dynamicScrap ? (r.scraps || {})[k] : r[k];
    const [expandedAttrs, setExpandedAttrs] = React.useState({});
    // eslint-disable-next-line no-undef
    const compLocation = useLocation();

    React.useEffect(() => {
        setExpandedAttrs({});
    }, [resetKey]);

    if (!rows || rows.length === 0) return (
        <div style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8', fontSize: 13 }}>
            No components found.
        </div>
    );

    const groupedRows = [];
    const seen = new Set();
    if (rows && rows.length > 0) {
        rows.forEach((row) => {
            const key = `${row.item}-${row.item_category}-${row.product_code}`;
            if (!seen.has(key)) { seen.add(key); groupedRows.push(row); }
        });
    }

    const thStyle = {
        background: "#f8fafc", fontSize: 12, fontWeight: 600, padding: "9px 10px",
        borderBottom: "2px solid #e5e7eb", color: "#1e293b", whiteSpace: "nowrap", textAlign: "left"
    };
    const tdStyle = {
        fontSize: 12, padding: "8px 10px", borderBottom: "1px solid #f1f5f9",
        color: "#334155", verticalAlign: "middle"
    };
    const attrThStyle = {
        background: "#f1f5f9", fontSize: 11, fontWeight: 600, padding: "5px 10px",
        borderBottom: "1px solid #e2e8f0", color: "#64748b", whiteSpace: "nowrap", textAlign: "left"
    };
    const attrTdStyle = {
        fontSize: 11, padding: "5px 10px", borderBottom: "1px solid #f1f5f9",
        color: "#475569", verticalAlign: "middle", background: "#f8fafc"
    };

    const inputStyle = { width: "100%", padding: "4px 6px", fontSize: 12, border: "1px solid #d1d5db", borderRadius: 4, height: 28, boxSizing: "border-box" };
    const attrInputStyle = { width: "100%", padding: "4px 6px", fontSize: 11, border: "1px solid #d1d5db", borderRadius: 4, height: 26, boxSizing: "border-box" };

    return (
        <div style={{ marginTop: 10, overflowX: "auto", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", minWidth: 1520 - 355 + 120 * scrapFields.length }}>
                <colgroup>
                    <col style={{ width: 45 }} />   {/* Item */}
                    <col style={{ width: 100 }} />  {/* Product Type */}
                    <col style={{ width: 130 }} />  {/* Product Code */}
                    <col style={{ width: "15%" }} />{/* Description */}
                    <col style={{ width: 55 }} />   {/* UOM */}
                    <col style={{ width: 80 }} />   {/* Total Qty */}
                    <col style={{ width: 80 }} />   {/* Consumed */}
                    {scrapFields.map(f => <col key={f.key} style={{ width: 120 }} />)}  {/* Scrap columns (from Scrap Configuration) */}
                    <col style={{ width: 105 }} />  {/* Total Scrap */}
                    <col style={{ width: 100 }} />  {/* Total Consumed */}
                    <col style={{ width: 130 }} />  {/* Storage Location */}
                    <col style={{ width: 130 }} />  {/* Batch Number */}
                    <col style={{ width: 60 }} />   {/* Attrs expand */}
                </colgroup>
                <thead>
                    <tr>
                        <th style={{ ...thStyle, textAlign: "center" }}>Item</th>
                        <th style={thStyle}>Product Type</th>
                        <th style={thStyle}>Product Code</th>
                        <th style={thStyle}>Description</th>
                        <th style={thStyle}>UOM</th>
                        <th style={{ ...thStyle, textAlign: "right" }}>Total Qty</th>
                        <th style={{ ...thStyle, textAlign: "right" }}>Consumed</th>
                        {scrapFields.map(f => <th key={f.key} style={{ ...thStyle, textAlign: "right" }}>{f.label}</th>)}
                        <th style={{ ...thStyle, textAlign: "right" }}>Total Scrap</th>
                        <th style={{ ...thStyle, textAlign: "right" }}>Total Consumed</th>
                        <th style={thStyle}>Storage Location</th>
                        <th style={thStyle}>Batch Number</th>
                        <th style={{ ...thStyle, textAlign: "center" }}></th>
                    </tr>
                </thead>
                <tbody>
                    {groupedRows.map((row, idx) => {
                        const totalQtyRaw = parseFloat(row.total_qty || row.quantity || 0);
                        const totalQty = parseFloat(totalQtyRaw.toFixed(3));
                        // Rework orders pre-assign a batch to certain input rows at creation
                        // time (po_material_details.batch_number already set). That row is
                        // fully locked — no editing of consumed/scrap/location/batch.
                        const hasPreAssignedBatch = !!(row.batch_number && String(row.batch_number).trim());
                        const attrs = (componentAttributes || {})[row.product_code] || [];
                        const rowKey = `${row.item}-${row.product_code}-${idx}`;
                        const isExpanded = !!expandedAttrs[rowKey];
                        const batches = batchData[row.product_code] || [];

                        // Setup/Process/Wirebreak Scrap captured per Input row here.
                        // total scrap = setup scrap + process scrap + wirebreak scrap
                        // total consumed = consumed + total scrap, capped by batch qty
                        // (see effectiveMax below).
                        const consumed = parseFloat(row.consumed) || 0;
                        const totalScrap = scrapFields.reduce((sum, f) => sum + (parseFloat(getScrap(row, f.key)) || 0), 0);
                        const totalConsumed = consumed + totalScrap;

                        // Shared clamp used by Consumed + all three scrap inputs so no
                        // combination of them can push Total Consumed past the batch cap.
                        const clampAgainstCap = (raw, otherFieldsTotal, cap) => {
                            if (raw === "" || raw === "." || raw.endsWith(".")) return raw;
                            const val = parseFloat(raw);
                            if (isNaN(val)) return raw;
                            if (otherFieldsTotal + val > cap + 0.0001) {
                                const allowed = Math.max(0, cap - otherFieldsTotal);
                                toast.warn(`Total Consumed (Consumed + Total Scrap) cannot exceed batch qty of ${cap}.`);
                                return String(allowed);
                            }
                            return raw;
                        };

                        // ── Selected batch(es) for this row: Consumed can never exceed
                        // the quantity actually picked from the batch(es), even if the
                        // order's open_qty is larger. Lifted to row scope (not just inside
                        // the Consumed cell's IIFE) so the Setup/Process/Wirebreak Scrap
                        // cells and the Total Consumed cell can also see effectiveMax etc.
                        const openQty = row.open_qty !== undefined && row.open_qty !== null
                            ? parseFloat(row.open_qty)
                            : totalQty;

                        const selectedBatchesArr = row._selectedBatches && row._selectedBatches.length > 0
                            ? row._selectedBatches
                            : Array.isArray(row.batches) && row.batches.length > 0
                                ? row.batches
                                : (row.component_batch ? [{ batch_number: row.component_batch, qty: row.total_consumed || row.consumed || totalQty }] : []);
                        const hasBatchSelection = selectedBatchesArr.length > 0;
                        const isSingleBatch = selectedBatchesArr.length === 1;

                        // Single batch: cap against what's actually AVAILABLE in stock
                        // for that batch (b.quantity), since we auto-sync its Use Qty to
                        // match Consumed as you type — capping against the previously
                        // synced Use Qty instead would block you from ever typing a
                        // second digit.
                        // Multiple batches: cap against the sum already picked, since
                        // there's no single unambiguous batch to auto-adjust.
                        const batchCap = isSingleBatch
                            ? (parseFloat(selectedBatchesArr[0].quantity) || Infinity)
                            : selectedBatchesArr.reduce((sum, b) => sum + (parseFloat(b.qty) || 0), 0);

                        const effectiveMax = hasBatchSelection ? Math.min(openQty, batchCap) : openQty;

                        const overOpen = !isViewMode && row.consumed !== "" && row.consumed !== undefined
                            && totalConsumed > effectiveMax + 0.0001;
                        const overBatchOnly = overOpen && hasBatchSelection && batchCap < openQty;

                        return (
                            <React.Fragment key={rowKey}>
                                <tr
                                    style={{ background: idx % 2 === 0 ? "#fff" : "#fafbfc" }}
                                    onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"}
                                    onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? "#fff" : "#fafbfc"}
                                >
                                    <td style={{ ...tdStyle, textAlign: "center", fontWeight: 600, color: "#475569" }}>{row.item}</td>
                                    <td style={tdStyle}>{row.product_type || row.item_category || ""}</td>
                                    <td style={{ ...tdStyle, fontWeight: 500, color: "#1e293b" }}>
                                        {row.product_code ? (
                                            <Link
                                                to={`/cable/product/product-code?view=${encodeURIComponent(row.product_code)}&returnTo=${encodeURIComponent(compLocation.pathname + compLocation.search)}`}
                                                style={{ color: "#2563eb", fontWeight: 500, textDecoration: "underline", textUnderlineOffset: 3 }}
                                                onMouseEnter={e => e.currentTarget.style.color = "#1d4ed8"}
                                                onMouseLeave={e => e.currentTarget.style.color = "#2563eb"}
                                            >
                                                {row.product_code}
                                            </Link>
                                        ) : ""}
                                    </td>
                                    <td style={{ ...tdStyle, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={row.description || row.component_description}>
                                        {row.description || row.component_description}
                                    </td>
                                    <td style={tdStyle}>{row.uom}</td>
                                    <td style={{ ...tdStyle, textAlign: "right", fontWeight: 600, color: "#0f172a" }}>{totalQty}</td>

                                    {(() => {
                                        return (
                                            <td style={{ ...tdStyle, textAlign: "right" }}>
                                                <input
                                                    type="number"
                                                    max={effectiveMax}
                                                    style={{
                                                        ...inputStyle,
                                                        background: (isViewMode || hasPreAssignedBatch) ? "#f1f5f9" : "#fff",
                                                        cursor: (isViewMode || hasPreAssignedBatch) ? "not-allowed" : "text",
                                                        textAlign: "right",
                                                        borderColor: overOpen ? "#ef4444" : "#d1d5db",
                                                    }}
                                                    title={
                                                        hasPreAssignedBatch
                                                            ? "Locked — batch already assigned for this rework order"
                                                            : overBatchOnly
                                                                ? `Cannot exceed selected batch qty of ${batchCap}`
                                                                : (overOpen ? `Cannot exceed open qty of ${openQty}` : undefined)
                                                    }
                                                    value={row.consumed !== undefined ? row.consumed : ""}
                                                    readOnly={isViewMode || hasPreAssignedBatch}
                                                    disabled={isViewMode || hasPreAssignedBatch}
                                                    onChange={(e) => {
                                                        if (isViewMode || hasPreAssignedBatch) return;
                                                        const raw = e.target.value;
                                                        if (raw === "" || raw === "." || raw.endsWith(".")) {
                                                            if (onConsumeChange) onConsumeChange(row.product_code, raw);
                                                            return;
                                                        }
                                                        const val = parseFloat(raw);
                                                        if (isNaN(val)) return;

                                                        // Cap Consumed + Total Scrap together against the batch cap,
                                                        // not Consumed alone.
                                                        if (hasBatchSelection && (val + totalScrap) > batchCap + 0.0001) {
                                                            const allowedConsumed = Math.max(0, batchCap - totalScrap);
                                                            toast.warn(
                                                                `Selected batch(es) only have ${batchCap} ${row.uom || ""} available — Consumed + Total Scrap cannot exceed it.`
                                                            );
                                                            if (onConsumeChange) onConsumeChange(row.product_code, String(allowedConsumed));
                                                            if (isSingleBatch && onBatchChange) {
                                                                onBatchChange(row.product_code, [{ ...selectedBatchesArr[0], qty: allowedConsumed + totalScrap }]);
                                                            }
                                                            return;
                                                        }

                                                        if (onConsumeChange) onConsumeChange(row.product_code, raw);

                                                        // ── Keep a single already-selected batch's Use Qty in sync
                                                        // with Consumed + Total Scrap as it's typed, so the Batch
                                                        // Number label reflects the new value immediately — no need
                                                        // to reopen the picker and hit Confirm.
                                                        if (isSingleBatch && onBatchChange) {
                                                            const b = selectedBatchesArr[0];
                                                            const newTotalConsumed = val + totalScrap;
                                                            if (parseFloat(b.qty) !== newTotalConsumed) {
                                                                onBatchChange(row.product_code, [{ ...b, qty: newTotalConsumed }]);
                                                            }
                                                        }
                                                    }}
                                                />
                                            </td>
                                        );
                                    })()}

                                    {/* Setup Scrap / Process Scrap / Wirebreak Scrap */}
                                    {scrapFields.map((f) => {
                                        const field = f.key;
                                        const fieldVal = parseFloat(getScrap(row, field)) || 0;
                                        const otherFieldsTotal = consumed + totalScrap - fieldVal;
                                        return (
                                            <td key={field} style={{ ...tdStyle, textAlign: "right" }}>
                                                <input
                                                    type="number"
                                                    min={0}
                                                    style={{
                                                        ...inputStyle,
                                                        textAlign: "right",
                                                        background: (isViewMode || hasPreAssignedBatch) ? "#f1f5f9" : "#fff",
                                                        cursor: (isViewMode || hasPreAssignedBatch) ? "not-allowed" : "text",
                                                    }}
                                                    value={getScrap(row, field) ?? ""}
                                                    readOnly={isViewMode || hasPreAssignedBatch}
                                                    disabled={isViewMode || hasPreAssignedBatch}
                                                    onChange={(e) => {
                                                        if (isViewMode || hasPreAssignedBatch) return;
                                                        const clamped = clampAgainstCap(e.target.value, otherFieldsTotal, effectiveMax);
                                                        if (onScrapChange) onScrapChange(row.product_code, field, clamped);

                                                        // Keep a single selected batch's Use Qty in sync with the
                                                        // new Total Consumed (Consumed + Total Scrap), same as Consumed does.
                                                        if (isSingleBatch && onBatchChange && clamped !== "" && !clamped.endsWith(".")) {
                                                            const newFieldVal = parseFloat(clamped) || 0;
                                                            const updatedTotalConsumed = consumed + (totalScrap - fieldVal + newFieldVal);
                                                            const b = selectedBatchesArr[0];
                                                            if (b && parseFloat(b.qty) !== updatedTotalConsumed) {
                                                                onBatchChange(row.product_code, [{ ...b, qty: updatedTotalConsumed }]);
                                                            }
                                                        }
                                                    }}
                                                />
                                            </td>
                                        );
                                    })}

                                    {/* Total Scrap (Read Only) */}
                                    <td style={{ ...tdStyle, textAlign: "right", fontWeight: 600, color: "#475569" }}>
                                        {totalScrap}
                                    </td>

                                    {/* Total Consumed (Read Only) = Consumed + Total Scrap, capped at batch qty */}
                                    <td style={{ ...tdStyle, textAlign: "right", fontWeight: 700, color: totalConsumed > effectiveMax + 0.0001 ? "#dc2626" : "#0f172a" }}
                                        title={totalConsumed > effectiveMax + 0.0001 ? `Exceeds available batch qty of ${effectiveMax}` : undefined}
                                    >
                                        {totalConsumed}
                                    </td>

                                    {(() => {
                                        return (
                                            <>
                                                <td style={tdStyle}>
                                                    {hasPreAssignedBatch ? (
                                                        <div style={{ ...inputStyle, display: "flex", alignItems: "center", background: "#f1f5f9", color: "#475569" }}>
                                                            {row.storage_location || "—"}
                                                        </div>
                                                    ) : (
                                                        (() => {
                                                            // Only storage locations that actually hold stock (a batch with qty > 0)
                                                            // for THIS product are offered.
                                                            const seen = new Set();
                                                            const locOptions = [];
                                                            batches.forEach((b) => {
                                                                const qtyAvail = parseFloat(b.quantity ?? b.qty) || 0;
                                                                const name = b.storage_location || b.storage_bin || "";
                                                                if (!name || qtyAvail <= 0 || seen.has(name)) return;
                                                                seen.add(name);
                                                                const bin = (storageBins || []).find(sb =>
                                                                    getStorageLocationName(sb) === name || sb?.storage_code === name || sb?.storageCode === name
                                                                );
                                                                locOptions.push({
                                                                    value: bin ? getStorageLocationName(bin) : name,
                                                                    label: bin ? getStorageLocationLabel(bin) : name,
                                                                });
                                                            });
                                                            // Keep an already-chosen value visible even if it has no stock listed.
                                                            const currentVal = row.storage_location || "";
                                                            let selectedOpt = locOptions.find(o => o.value === currentVal) || null;
                                                            if (!selectedOpt && currentVal) {
                                                                selectedOpt = { value: currentVal, label: currentVal };
                                                            }
                                                            return (
                                                                <Select
                                                                    options={locOptions}
                                                                    value={selectedOpt}
                                                                    isDisabled={isViewMode}
                                                                    isClearable
                                                                    placeholder="Select Location"
                                                                    noOptionsMessage={() => "No stock available"}
                                                                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                                                    menuPosition="fixed"
                                                                    onChange={(opt) => !isViewMode && onStorageLocationChange && onStorageLocationChange(row.product_code, row.item, opt ? opt.value : "")}
                                                                    styles={compactSelectStyles({ disabled: isViewMode })}
                                                                />
                                                            );
                                                        })()
                                                    )}
                                                </td>
                                                <td style={tdStyle}>
                                                    {hasPreAssignedBatch ? (
                                                        <div style={{ ...inputStyle, display: "flex", alignItems: "center", background: "#f1f5f9", color: "#475569", fontWeight: 600 }}
                                                             title="Batch already assigned for this rework order — locked">
                                                            {row.batch_number}
                                                        </div>
                                                    ) : (
                                                        <MultiBatchPicker
                                                            batches={batches}
                                                            consumed={totalConsumed > 0 ? totalConsumed : totalQty}
                                                            selectedBatches={
                                                                row._selectedBatches && row._selectedBatches.length > 0
                                                                    ? row._selectedBatches
                                                                    : Array.isArray(row.batches) && row.batches.length > 0
                                                                        ? row.batches
                                                                        : (row.component_batch ? [{ batch_number: row.component_batch, qty: row.total_consumed || row.consumed || totalQty }] : [])
                                                            }
                                                            isViewMode={isViewMode}
                                                            uom={row.uom || row.unit || ""}
                                                            onChange={(selected) => {
                                                                if (selected.length > 0 && selected[0].storage_location && onStorageLocationChange) {
                                                                    onStorageLocationChange(row.product_code, row.item, selected[0].storage_location);
                                                                }
                                                                if (onBatchChange) onBatchChange(row.product_code, selected);
                                                            }}
                                                        />
                                                    )}
                                                </td>
                                            </>
                                        );
                                    })()}
                                    <td style={{ ...tdStyle, textAlign: "center" }}>
                                        {attrs.length > 0 && (
                                            <button
                                                onClick={() => setExpandedAttrs(prev => ({ ...prev, [rowKey]: !prev[rowKey] }))}
                                                style={{
                                                    fontSize: 10, padding: "2px 7px", border: "1px solid #cbd5e1", borderRadius: 4,
                                                    background: isExpanded ? "#e2e8f0" : "#f8fafc", color: "#475569", cursor: "pointer",
                                                    display: "inline-flex", alignItems: "center", gap: 3, whiteSpace: "nowrap",
                                                }}
                                            >
                                                <span style={{ display: "inline-block", transition: "transform 0.2s", transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)" }}>▼</span>
                                                {attrs.length} attr
                                            </button>
                                        )}
                                    </td>
                                </tr>

                                {attrs.length > 0 && isExpanded && (
                                    <tr>
                                        <td colSpan={13} style={{ padding: "0 0 6px 40px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                                <thead>
                                                    <tr>
                                                        <th style={{ ...attrThStyle, width: 36, textAlign: "center" }}>Sl</th>
                                                        <th style={attrThStyle}>Text Parameter</th>
                                                        <th style={attrThStyle}>Job TP</th>
                                                        <th style={{ ...attrThStyle, width: 250 }}>Actuals</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {attrs.map((attr, aIdx) => (
                                                        <tr key={aIdx}>
                                                            <td style={{ ...attrTdStyle, textAlign: "center", fontWeight: 600, color: "#94a3b8" }}>{aIdx + 1}</td>
                                                            <td style={attrTdStyle}>{attr.characteristic}</td>
                                                            <td style={{ ...attrTdStyle, fontWeight: 500, color: "#1e293b" }}>{attr.value || ""}</td>
                                                            <td style={attrTdStyle}>
                                                                <input
                                                                    type="text"
                                                                    style={{ ...attrInputStyle, background: isViewMode ? "#f1f5f9" : "#fff", cursor: isViewMode ? "not-allowed" : "text" }}
                                                                    value={attr.actual_value !== undefined ? attr.actual_value : ""}
                                                                    placeholder={isViewMode ? "" : "Enter actuals"}
                                                                    readOnly={isViewMode}
                                                                    disabled={isViewMode}
                                                                    onChange={(e) => {
                                                                        if (isViewMode) return;
                                                                        if (onComponentAttributeChange) {
                                                                            onComponentAttributeChange(row.product_code, attr.characteristic, e.target.value);
                                                                        }
                                                                    }}
                                                                />
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

ComponentsTable.propTypes = {
    rows: PropTypes.array,
    baseUnit: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    actualQty: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    componentAttributes: PropTypes.object,
    onConsumeChange: PropTypes.func,
    onScrapChange: PropTypes.func,
    onBatchChange: PropTypes.func,
    onComponentAttributeChange: PropTypes.func,
    batchData: PropTypes.object,
    storageBins: PropTypes.array,
    onStorageLocationChange: PropTypes.func,
    isViewMode: PropTypes.bool,
    resetKey: PropTypes.string,
};

/* ─────────────────────────────────────────────
   OPERATIONS TABLE 
───────────────────────────────────────────── */
const OperationsTable = ({ 
    rows, 
    actualDates, setActualDates, 
    actualActivities, setActualActivities, 
    actualActivityDates, setActualActivityDates, 
    actualIdle = {}, setActualIdle = () => {}, 
    isViewMode = false, 
    onHeaderDateChange, 
    qty = 1, baseUnit = 1 
}) => {
    // Idle Reason options — fetched once from the Idle Reason configuration
    // (Configuration > Idle Reason), used to drive the react-select dropdown
    // in the Idle Reason column below.
    const [idleReasonOptions, setIdleReasonOptions] = useState([]);
    useEffect(() => {
        axios.get(`${MASTER_API}/configuration/dynamic/idle-reason`)
            .then((res) => {
                const list = res.data?.data || res.data || [];
                setIdleReasonOptions(
                    list.map((r) => ({ value: r.name, label: r.name }))
                );
            })
            .catch(console.error);
    }, []);

    if (!rows || rows.length === 0) return (
        <div style={{ textAlign: "center", padding: "32px 0", color: "#94a3b8", fontSize: 13 }}>
            No operations found.
        </div>
    );

    const formatDateForInput = (isoDate) => {
        if (!isoDate) return "";
        const s = String(isoDate);
        if (/^\d{4}-\d{2}-\d{2}$/.test(s.trim())) return s.trim();
        if (s.includes("T")) return s.split("T")[0];
        return s.substring(0, 10);
    };
    const formatTimeForInput = (timeStr) => timeStr ? String(timeStr).substring(0, 5) : "";
    // Display-only dd-mm-yyyy for read-only dates in the Machine Time tables.
    const toDmyDisplay = (d) => {
        const iso = formatDateForInput(d);
        const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})$/);
        return m ? `${m[3]}-${m[2]}-${m[1]}` : (iso || "");
    };

    // Order in which activities appear in the table: Setup -> Run Time (machine) -> Labour.
    // The overall row Start Date/Time mirrors the FIRST activity present's start,
    // and the overall row End Date/Time mirrors the LAST activity present's end.
    const hasActivityType = (t) => rows.some(r => (r.activities || []).some(a => (a.activity_type || "").toLowerCase() === t));
    const activityOrder = ["setup", "machine", "labour"].filter(hasActivityType);
    const firstActivityType = activityOrder[0];
    const lastActivityType = activityOrder[activityOrder.length - 1];

    // Handles individual start/end dates for Setup, Run Time, Labour
    const handleActivityDateChange = (opId, type, field, val) => {
        const key = `${opId}-${type}`;
        const current = actualActivityDates[key] || {};
        const updated = { ...current, [field]: val };

        setActualActivityDates(prev => ({ ...prev, [key]: updated }));

        // Auto-calculate the duration in minutes if all 4 fields are filled
        if (updated.start_date && updated.start_time && updated.end_date && updated.end_time) {
            const start = new Date(`${updated.start_date}T${updated.start_time}`);
            const end = new Date(`${updated.end_date}T${updated.end_time}`);
            const diffMs = end - start;
            if (diffMs >= 0) {
                const diffMins = diffMs / 60000;
                setActualActivities(prev => ({ ...prev, [key]: diffMins.toFixed(2) }));
            }
        }

        // Keep the overall row Start/End in sync with the first/last activity,
        // and bubble it up to the header if needed.
        if (type === firstActivityType && field === "start_date") {
            setActualDates(prev => ({ ...prev, [opId]: { ...prev[opId], actual_start_date: val } }));
            if (onHeaderDateChange) onHeaderDateChange("startDate", val);
        }
        if (type === firstActivityType && field === "start_time") {
            setActualDates(prev => ({ ...prev, [opId]: { ...prev[opId], actual_start_time: val } }));
            if (onHeaderDateChange) onHeaderDateChange("startTime", val);
        }
        if (type === lastActivityType && field === "end_date") {
            setActualDates(prev => ({ ...prev, [opId]: { ...prev[opId], actual_end_date: val } }));
            if (onHeaderDateChange) onHeaderDateChange("endDate", val);
        }
        if (type === lastActivityType && field === "end_time") {
            setActualDates(prev => ({ ...prev, [opId]: { ...prev[opId], actual_end_time: val } }));
            if (onHeaderDateChange) onHeaderDateChange("endTime", val);
        }
    };

    // User types in the Actual qty (if they want to override the auto-calculation)
    const handleActivityChange = (opId, type, val) => {
        setActualActivities(prev => ({ ...prev, [`${opId}-${type}`]: val }));
    };

    // Idle Time — recorded against the Run Time ("machine") activity only.
    // field is one of "flag" ("Yes"/"No"), "value" (minutes idle), "reason".
    const handleIdleChange = (opId, field, val) => {
        const key = `${opId}-machine`;
        setActualIdle(prev => {
            const current = prev[key] || { flag: "", value: "", reason: "" };
            const updated = { ...current, [field]: val };
            // Clearing the flag to "No" (or blank) clears any previously entered value/reason.
            if (field === "flag" && val !== "Yes") {
                updated.value = "";
                updated.reason = "";
            }
            return { ...prev, [key]: updated };
        });
    };

    // General start/end dates are purely manually driven now
    // eslint-disable-next-line no-unused-vars
    const handleDateChange = (opId, field, val) => {
        setActualDates(prev => ({
            ...prev,
            [opId]: { ...prev[opId], [field]: val }
        }));

        if (onHeaderDateChange) {
            if (field === "actual_start_date") onHeaderDateChange("startDate", val);
            if (field === "actual_start_time") onHeaderDateChange("startTime", val);
            if (field === "actual_end_date") onHeaderDateChange("endDate", val);
            if (field === "actual_end_time") onHeaderDateChange("endTime", val);
        }
    };

    const distinctRows = rows.map((row) => ({
        ...row,
        activities: Array.from(new Map((row.activities || []).map((act) => [`${act.activity_type}-${act.activity_value}-${act.activity_unit}`, act])).values())
    }));

    const hasSubProcess = distinctRows.some(r => r.sub_process_name && r.sub_process_name.toString().trim() !== "");
    const getAct = (row, type) => (row.activities || []).find(a => (a.activity_type || "").toLowerCase() === type.toLowerCase());
    // Display-only helpers — the value SAVED/SENT to the backend is always
    // raw minutes (e.g. 143). These control what's shown in the field:
    // hours.minutes format always ("0.48" = 0h 48m, "2.23" = 2h 23m),
    // so it's unambiguous whether a number is minutes or hours.
    const formatDurationDisplay = (mins) => {
        const val = parseFloat(mins);
        if (isNaN(val)) return "";
        const h = Math.floor(val / 60);
        const m = Math.round(val % 60);
        return `${h}.${String(m).padStart(2, "0")}`;
    };
    // Same raw-minutes -> hours/minutes math as formatDurationDisplay, but
    // returned as a zero-padded "HH:MM" clock string, e.g. 263.74 -> "04:24".
    const minsToHHMM = (mins) => {
        const val = parseFloat(mins);
        if (isNaN(val)) return "";
        const isNeg = val < 0;
        const absVal = Math.abs(val);
        let h = Math.floor(absVal / 60);
        let m = Math.round(absVal % 60);
        if (m === 60) { h += 1; m = 0; }
        return `${isNeg ? "-" : ""}${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    };
    // Converts what the user typed back into raw minutes before it's
    // stored — "2.23" (2h 23m) -> 143, "0.48" (0h 48m) -> 48.
    const parseDurationInput = (str) => {
        if (str === "" || str === null || str === undefined) return "";
        const strVal = String(str);
        if (strVal.includes(".")) {
            const [hPart, mPart] = strVal.split(".");
            const h = parseInt(hPart, 10);
            if (!isNaN(h)) {
                const mDigits = (mPart || "0").padEnd(2, "0").slice(0, 2);
                const m = parseInt(mDigits, 10) || 0;
                return String(h * 60 + m);
            }
        }
        return strVal;
    };

    const fmtActValue = (act) => {
        if (!act || act.activity_value === undefined || act.activity_value === null) return "";
        let val = parseFloat(act.activity_value ?? 0);
        if (isNaN(val)) return "";
        const type = (act.activity_type || "").toLowerCase();
        if (type !== "setup") {
            const bUnit = Number(baseUnit) || 1;
            const prodQty = Number(qty) || 1;
            val = (val * prodQty) / bUnit;
        }
        // val is raw total minutes (same convention as the Actual side) —
        // e.g. 263.74 raw minutes = 4h 24m, NOT "263 hours 74 minutes".
        return minsToHHMM(val);
    };

    // Planned Setup / Run Time schedule — same logic as the Production Order
    // screen: Setup starts at the operation's planned Start Date/Time, Run Time
    // starts when Setup ends. Durations use the same scaled values as fmtActValue.
    const getPlannedSchedule = (row) => {
        const out = { setup: null, machine: null };
        if (!row || !row.start_date) return out;
        const bUnit = Number(baseUnit) || 1;
        const prodQty = Number(qty) || 1;
        const pad = (n) => String(n).padStart(2, "0");
        const minsOf = (act) => {
            if (!act || act.activity_value === undefined || act.activity_value === null || act.activity_value === "") return null;
            let v = parseFloat(act.activity_value);
            if (isNaN(v)) return null;
            const type = (act.activity_type || "").toLowerCase();
            if (type !== "setup") v = (v * prodQty) / bUnit;
            const u = (act.activity_unit || "").toLowerCase();
            return (u === "hr" || u === "hrs" || u === "hour" || u === "hours") ? v * 60 : v;
        };
        const addMins = (d, t, m) => {
            const st = new Date(`${d}T${t}`);
            if (isNaN(st.getTime())) return null;
            const en = new Date(st.getTime() + m * 60000);
            return {
                date: `${en.getFullYear()}-${pad(en.getMonth() + 1)}-${pad(en.getDate())}`,
                time: `${pad(en.getHours())}:${pad(en.getMinutes())}`,
            };
        };
        let curDate = formatDateForInput(row.start_date);
        let curTime = row.start_time ? String(row.start_time).substring(0, 5) : "00:00";
        ["setup", "machine"].forEach((type) => {
            const m = minsOf(getAct(row, type));
            if (m === null) return;
            const end = addMins(curDate, curTime, m);
            if (!end) return;
            out[type] = { start_date: curDate, start_time: curTime, end_date: end.date, end_time: end.time };
            curDate = end.date; curTime = end.time;
        });
        return out;
    };

    const thBase = { background: "#f8fafc", fontSize: 12, fontWeight: 600, padding: "7px 10px", color: "#1e293b", whiteSpace: "nowrap", textAlign: "left", borderBottom: "2px solid #e5e7eb" };
    const tdStyle = { fontSize: 12, padding: "8px 10px", borderBottom: "1px solid #f1f5f9", color: "#334155", verticalAlign: "middle", whiteSpace: "nowrap" };
    const inputSt = (disabled) => ({ fontSize: 12, height: 28, padding: "2px 6px", border: "1px solid #ced4da", borderRadius: 4, background: disabled ? "#e9ecef" : "#fff", color: "#1e293b", outline: "none", cursor: disabled ? "not-allowed" : "auto", width: "100%", boxSizing: "border-box" });

    const hasSetup = distinctRows.some(r => getAct(r, "setup"));
    const hasRunTime = distinctRows.some(r => getAct(r, "machine"));
    const hasLabour = distinctRows.some(r => getAct(r, "labour"));

    // Dynamic Helper for Activity Headers
    const renderActivityHeaders = (hasType, label) => {
        if (!hasType) return null;
        return (
            <>
                <th style={thBase}>{label} Start Date</th>
                <th style={thBase}>{label} Start Time</th>
                <th style={thBase}>{label} End Date</th>
                <th style={thBase}>{label} End Time</th>
                <th style={{ ...thBase, textAlign: "right" }}>{label} (HH:MM)</th>
            </>
        );
    };

    const renderActivityColgroup = (hasType) => {
        if (!hasType) return null;
        return (
            <>
                <col style={{ width: 125 }} />
                <col style={{ width: 100 }} />
                <col style={{ width: 125 }} />
                <col style={{ width: 100 }} />
                <col style={{ width: 85 }} />
            </>
        );
    };

    // Dynamic Helper for Activity Columns
    const renderActivityColumns = (isActual, hasType, actObj, actVal, actDates, typeStr, opId, plannedDates = null) => {
        if (!hasType) return null;
        return (
            <>
                <td style={tdStyle}>
                    {isActual ? (
                        isViewMode ? <span>{toDmyDisplay(actDates?.start_date)}</span> :
                        <DmyDateInput showCalendar value={actDates?.start_date || ""} onChange={e => handleActivityDateChange(opId, typeStr, "start_date", e.target.value)} style={{ ...inputSt(false), width: 110 }} />
                    ) : <span>{toDmyDisplay(plannedDates?.start_date || actObj?.activity_start_date)}</span>}
                </td>
                <td style={tdStyle}>
                    {isActual ? (
                        isViewMode ? <span>{actDates?.start_time || ""}</span> :
                        <input type="time" value={actDates?.start_time || ""} onChange={e => handleActivityDateChange(opId, typeStr, "start_time", e.target.value)} style={{ ...inputSt(false), width: 85 }} />
                    ) : <span>{formatTimeForInput(plannedDates?.start_time || actObj?.activity_start_time)}</span>}
                </td>
                <td style={tdStyle}>
                    {isActual ? (
                        isViewMode ? <span>{toDmyDisplay(actDates?.end_date)}</span> :
                        <DmyDateInput showCalendar value={actDates?.end_date || ""} onChange={e => handleActivityDateChange(opId, typeStr, "end_date", e.target.value)} style={{ ...inputSt(false), width: 110 }} />
                    ) : <span>{toDmyDisplay(plannedDates?.end_date || actObj?.activity_end_date)}</span>}
                </td>
                <td style={tdStyle}>
                    {isActual ? (
                        isViewMode ? <span>{actDates?.end_time || ""}</span> :
                        <input type="time" value={actDates?.end_time || ""} onChange={e => handleActivityDateChange(opId, typeStr, "end_time", e.target.value)} style={{ ...inputSt(false), width: 85 }} />
                    ) : <span>{formatTimeForInput(plannedDates?.end_time || actObj?.activity_end_time)}</span>}
                </td>
                <td style={{ ...tdStyle, textAlign: "right" }}>
                    {isActual ? (
                        isViewMode ? (
                            <span style={{ fontWeight: 600 }}>
                                {(() => {
                                    const raw = actVal !== "" ? actVal : actObj?.activity_value;
                                    if (raw === undefined || raw === null || raw === "") return fmtActValue(actObj);
                                    return minsToHHMM(raw);
                                })()}
                            </span>
                        ) : (
                            <input
                                type="text"
                                inputMode="decimal"
                                placeholder="0"
                                value={actVal !== "" ? formatDurationDisplay(actVal) : ""}
                                onChange={(e) => handleActivityChange(opId, typeStr, parseDurationInput(e.target.value))}
                                style={{ ...inputSt(false), width: 70, textAlign: "right" }}
                            />
                        )
                    ) : <span style={{ fontWeight: 600 }}>{fmtActValue(actObj)}</span>}
                </td>
            </>
        );
    };

    const renderTable = (isActual) => (
        <div style={{ marginTop: isActual ? 0 : 10 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", background: "#f3f4f6", padding: "5px 12px", borderRadius: "4px 4px 0 0", display: "inline-block", letterSpacing: "0.04em", textTransform: "uppercase" }}>
                {isActual ? "Actual" : "Planned"}
            </div>
            <div style={{ overflowX: "auto", background: "#fff" }}>
                <table style={{ borderCollapse: "collapse", tableLayout: "fixed" }}>
                    <colgroup>
                        <col style={{ width: 150 }} />  {/* Process */}
                        {hasSubProcess && <col style={{ width: 120 }} />}  {/* Sub Process */}
                        <col style={{ width: 120 }} />  {/* Workcenter */}
                        <col style={{ width: 120 }} />   {/* Machine */}
                        
                        {renderActivityColgroup(hasSetup)}
                        {renderActivityColgroup(hasRunTime)}
                        {renderActivityColgroup(hasLabour)}

                        <col style={{ width: 125 }} />   {/* Gen Start Date */}
                        <col style={{ width: 100 }} />    {/* Gen Start Time */}
                        <col style={{ width: 125 }} />   {/* Gen End Date */}
                        <col style={{ width: 100 }} />    {/* Gen End Time */}
                        {isActual && hasRunTime && <col style={{ width: 80 }} />}   {/* Idle Time */}
                        {isActual && hasRunTime && <col style={{ width: 80 }} />}   {/* Idle */}
                        {isActual && hasRunTime && <col style={{ width: 150 }} />}  {/* Idle Reason */}
                    </colgroup>
                    <thead>
                        <tr>
                            <th style={thBase}>Process</th>
                            {hasSubProcess && <th style={thBase}>Sub Process</th>}
                            <th style={thBase}>Workcenter</th>
                            <th style={thBase}>Machine</th>
                            
                            {renderActivityHeaders(hasSetup, "Setup")}
                            {renderActivityHeaders(hasRunTime, "Run Time")}
                            {renderActivityHeaders(hasLabour, "Labour")}

                            <th style={thBase}>Start Date</th>
                            <th style={thBase}>Start Time</th>
                            <th style={thBase}>End Date</th>
                            <th style={thBase}>End Time</th>
                            {isActual && hasRunTime && <th style={thBase}>Idle Time</th>}
                            {isActual && hasRunTime && <th style={{ ...thBase, textAlign: "right" }}>Idle Time (HH:MM)</th>}
                            {isActual && hasRunTime && <th style={thBase}>Idle Reason</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {distinctRows.map((row, idx) => {
                            const opId = row.operation_id;
                            // eslint-disable-next-line no-unused-vars
                            const dates = actualDates[opId] || {};
                            
                            const setupAct = getAct(row, "setup");
                            const machineAct = getAct(row, "machine");
                            const labourAct = getAct(row, "labour");

                            const actSetup = actualActivities[`${opId}-setup`] ?? "";
                            const actRun = actualActivities[`${opId}-machine`] ?? "";
                            const actLabour = actualActivities[`${opId}-labour`] ?? "";

                            const actSetupDates = actualActivityDates[`${opId}-setup`] || {};
                            const actRunDates = actualActivityDates[`${opId}-machine`] || {};
                            const actLabourDates = actualActivityDates[`${opId}-labour`] || {};
                            const plannedSched = getPlannedSchedule(row);

                            return (
                                <tr key={idx}
                                    style={{ background: idx % 2 === 0 ? "#fff" : "#fafafa" }}
                                    onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"}
                                    onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? "#fff" : "#fafafa"}
                                >
                                    <td style={{ ...tdStyle, fontWeight: 500 }}>{row.process_name || ""}</td>
                                    {hasSubProcess && <td style={tdStyle}>{row.sub_process_name || ""}</td>}
                                    <td style={tdStyle}>{row.workcenter_name || ""}</td>
                                    <td style={{ ...tdStyle, fontWeight: 600 }}>{row.machine || "-"}</td>

                                    {renderActivityColumns(isActual, hasSetup, setupAct, actSetup, actSetupDates, "setup", opId, plannedSched.setup)}
                                    {renderActivityColumns(isActual, hasRunTime, machineAct, actRun, actRunDates, "machine", opId, plannedSched.machine)}
                                    {renderActivityColumns(isActual, hasLabour, labourAct, actLabour, actLabourDates, "labour", opId)}

                                    {/* General Dates - derived automatically: Start = first activity's start, End = last activity's end */}
                                    <td style={tdStyle}>
                                        {isActual ? (
                                            <span>{actSetupDates && firstActivityType === "setup" ? toDmyDisplay(actSetupDates.start_date) : firstActivityType === "machine" ? toDmyDisplay(actRunDates.start_date) : firstActivityType === "labour" ? toDmyDisplay(actLabourDates.start_date) : ""}</span>
                                        ) : (
                                            <span>{toDmyDisplay(row.start_date)}</span>
                                        )}
                                    </td>
                                    <td style={tdStyle}>
                                        {isActual ? (
                                            <span>{firstActivityType === "setup" ? actSetupDates.start_time || "" : firstActivityType === "machine" ? actRunDates.start_time || "" : firstActivityType === "labour" ? actLabourDates.start_time || "" : ""}</span>
                                        ) : (
                                            <span>{formatTimeForInput(row.start_time)}</span>
                                        )}
                                    </td>
                                    <td style={tdStyle}>
                                        {isActual ? (
                                            <span>{lastActivityType === "setup" ? toDmyDisplay(actSetupDates.end_date) : lastActivityType === "machine" ? toDmyDisplay(actRunDates.end_date) : lastActivityType === "labour" ? toDmyDisplay(actLabourDates.end_date) : ""}</span>
                                        ) : (
                                            <span>{toDmyDisplay(row.end_date)}</span>
                                        )}
                                    </td>
                                    <td style={tdStyle}>
                                        {isActual ? (
                                            <span>{lastActivityType === "setup" ? actSetupDates.end_time || "" : lastActivityType === "machine" ? actRunDates.end_time || "" : lastActivityType === "labour" ? actLabourDates.end_time || "" : ""}</span>
                                        ) : (
                                            <span>{formatTimeForInput(row.end_time)}</span>
                                        )}
                                    </td>
                                    {isActual && hasRunTime && (() => {
                                        const idleKey = `${opId}-machine`;
                                        const idle = actualIdle[idleKey] || { flag: "", value: "", reason: "" };
                                        return (
                                            <>
                                                <td style={tdStyle}>
                                                    {isViewMode ? <span>{idle.flag || "—"}</span> : (
                                                        <select
                                                            value={idle.flag || ""}
                                                            onChange={(e) => handleIdleChange(opId, "flag", e.target.value)}
                                                            style={{ ...inputSt(false), width: 70 }}
                                                        >
                                                            <option value="">—</option>
                                                            <option value="No">No</option>
                                                            <option value="Yes">Yes</option>
                                                        </select>
                                                    )}
                                                </td>
                                                <td style={{ ...tdStyle, textAlign: "right" }}>
                                                    {idle.flag === "Yes" ? (
                                                        isViewMode ? (
                                                            <span>
                                                                {idle.value !== "" && idle.value !== undefined && idle.value !== null
                                                                    ? minsToHHMM(idle.value)
                                                                    : ""}
                                                            </span>
                                                        ) :
                                                        <input type="number" placeholder="0" value={idle.value || ""} onChange={(e) => handleIdleChange(opId, "value", e.target.value)} style={{ ...inputSt(false), width: 70, textAlign: "right" }} />
                                                    ) : <span>—</span>}
                                                </td>
                                                <td style={tdStyle}>
                                                    {idle.flag === "Yes" ? (
                                                        isViewMode ? <span>{idle.reason || ""}</span> :
                                                        <Select
                                                            options={idleReasonOptions}
                                                            value={idle.reason ? { value: idle.reason, label: idle.reason } : null}
                                                            onChange={(opt) => handleIdleChange(opId, "reason", opt ? opt.value : "")}
                                                            isClearable
                                                            placeholder="Reason"
                                                            menuPortalTarget={document.body} menuPosition="fixed"
                                                            styles={{
                                                                container: (base) => ({ ...base, width: 140 }),
                                                                control: (base, state) => ({ ...base, minHeight: 28, height: 28, fontSize: 12, borderColor: state.isFocused ? "#64748b" : "#e2e8f0", borderRadius: 6, boxShadow: "none", cursor: "pointer", "&:hover": { borderColor: "#94a3b8" } }),
                                                                valueContainer: (base) => ({ ...base, padding: "0 6px", height: 28, flexWrap: "nowrap" }),
                                                                input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: 12 }),
                                                                indicatorsContainer: (base) => ({ ...base, height: 28 }),
                                                                dropdownIndicator: (base) => ({ ...base, padding: "0 4px", color: "#94a3b8", "svg": { width: 12, height: 12 } }),
                                                                clearIndicator: (base) => ({ ...base, padding: "0 2px", color: "#94a3b8", "svg": { width: 12, height: 12 } }),
                                                                indicatorSeparator: () => ({ display: "none" }),
                                                                placeholder: (base) => ({ ...base, color: "#94a3b8", fontSize: 12, whiteSpace: "nowrap" }),
                                                                singleValue: (base) => ({ ...base, fontSize: 12, color: "#1e293b" }),
                                                                menu: (base) => ({ ...base, zIndex: 9999, fontSize: 12, borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.12)" }),
                                                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                                                option: (base, state) => ({ ...base, fontSize: 12, padding: "6px 10px", backgroundColor: state.isSelected ? "#1e293b" : state.isFocused ? "#f1f5f9" : "#fff", color: state.isSelected ? "#fff" : "#334155" }),
                                                            }}
                                                        />
                                                    ) : <span>—</span>}
                                                </td>
                                            </>
                                        );
                                    })()}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );

    return (
        <div style={{ marginTop: 10, borderRadius: 8, border: "1px solid #e5e7eb", overflow: "hidden", background: "#fff" }}>
            {renderTable(false)}
            <div style={{ borderTop: "2px solid #e2e8f0" }} />
            {renderTable(true)}
        </div>
    );
};

OperationsTable.propTypes = {
    rows: PropTypes.array.isRequired,
    actualDates: PropTypes.object.isRequired,
    setActualDates: PropTypes.func.isRequired,
    actualActivities: PropTypes.object.isRequired,
    setActualActivities: PropTypes.func.isRequired,
    actualActivityDates: PropTypes.object.isRequired,
    setActualActivityDates: PropTypes.func.isRequired,
    actualIdle: PropTypes.object,
    setActualIdle: PropTypes.func,
};

/* ─────────────────────────────────────────────
   MAIN CONFIRMATION COMPONENT
───────────────────────────────────────────── */
// Fixed dd/mm/yyyy hh:mm:ss formatter, always in Indian Standard Time
// (Asia/Kolkata), regardless of the viewer's device timezone/locale or the
// UTC value sent by the server. Used for the Change Log "Date & Time".
const formatChangeLogDateTime = (dateInput) => {
    if (!dateInput) return "-";
    const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
    if (isNaN(d.getTime())) return "-";
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Asia/Kolkata',
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false
    }).formatToParts(d).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
    const hh = parts.hour === '24' ? '00' : parts.hour;
    return `${parts.day}/${parts.month}/${parts.year} ${hh}:${parts.minute}:${parts.second}`;
};

const Confirmation = () => {
    const mLabel = {
        display: "block",
        fontSize: "12px",
        fontWeight: 600,
        marginBottom: "3px",
        color: "#475569",
    };
    const mInput = {
        width: "100%",
        height: "30px",
        fontSize: "12px",
        padding: "3px 8px",
        border: "1px solid #dee2e6",
        borderRadius: 4,
        background: "#fff",
        color: "#1e293b",
        boxSizing: "border-box",
        outline: "none",
    };

    // Formats a yyyy-mm-dd / ISO date string to dd-mm-yyyy for display.
    // Pure string parsing (no Date object) so it never shifts a day due to timezone.
    const formatDMY = (dateStr) => {
        if (!dateStr) return "";
        const s = String(dateStr).substring(0, 10);
        const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (!m) return s;
        const [, y, mo, d] = m;
        return `${d}-${mo}-${y}`;
    };

    const soThStyle = {
        background: "#f8fafc",
        fontSize: 11,
        fontWeight: 600,
        padding: "7px 10px",
        borderBottom: "2px solid #e5e7eb",
        color: "#475569",
        whiteSpace: "nowrap",
        textAlign: "left",
    };

    const soTdStyle = {
        fontSize: 12,
        padding: "7px 10px",
        borderBottom: "1px solid #f1f5f9",
        color: "#1e293b",
        verticalAlign: "middle",
    };

    const location = useLocation();
    const navigate = useNavigate();

    const [showManual, setShowManual] = useState(false);
    const [manualExists, setManualExists] = useState(null);
    const [manualFile, setManualFile] = useState(null);

    // Module-level access for this page. Confirmation has no standalone
    // Create form — everything originates from an existing Production
    // Order row — so this module only ever exposes three real actions:
    //  - canView    -> DISPLAY/REVIEW: can browse/view the list & modal
    //  - canEdit    -> CHANGE: gates BOTH the "Confirm" action on an open
    //                  row and Edit/Save on an existing confirmation.
    //  - canConvert -> CONVERT: can perform the "Inspect" action (Quality
    //                  release step), same idea as Production Order's
    //                  Release — advancing a record's status forward.
    // Confirm/Inspect both originate from a row in the list (there's no
    // standalone "+Create" button like Production Order), so the list
    // itself must stay visible for CONVERT-only roles too, not just canView.
    const { canView, canEdit, canConvert } = React.useMemo(
        () => getModulePermissions("SFC_CONFIRMATION"),
        []
    );
    const canSeePage = canView || canEdit || canConvert;

    // Field-level access for this module. Falls open (true) for admins,
    // users with no role assigned yet, or any non-DISPLAY permission type —
    // matching useFieldPermissions' own documented behavior.
    const { isFieldVisible } = useFieldPermissions("SFC_CONFIRMATION");

    const [orders, setOrders] = useState([]);

    // eslint-disable-next-line no-unused-vars
    const [qtyErrors, setQtyErrors] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [isConfirmMode, setIsConfirmMode] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [expandedSections, setExpandedSections] = useState({
        material_details: false, process_parameter: false, machine_parameter: false,
        machine_time: false, inspection_parameter: false, special_remarks: false, documents: false, logs: false,
    });
    
    const toggleSection = (key) => {
        if (key === "logs") fetchLogs(modalData.production_order_number);
        setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const [modalData, setModalData] = useState({});
    const [actualQty, setActualQty] = useState("");
    const [deliveredActual, setDeliveredActual] = useState("");
    // eslint-disable-next-line no-unused-vars
    const [startDate, setStartDate] = useState("");
    // eslint-disable-next-line no-unused-vars
    const [endDate, setEndDate] = useState("");
    const [startTime, setStartTime] = useState("");
    const [endTime, setEndTime] = useState("");
    // eslint-disable-next-line no-unused-vars
    const [quantity, setQuantity] = useState("");
    // eslint-disable-next-line no-unused-vars
    const [delivered, setDelivered] = useState("");
    // eslint-disable-next-line no-unused-vars
    const [scrapQty, setScrapQty] = useState("");
    // eslint-disable-next-line no-unused-vars
    const [scrapPercent, setScrapPercent] = useState("");
    // eslint-disable-next-line no-unused-vars
    const [upperTolerance, setUpperTolerance] = useState("");
    // eslint-disable-next-line no-unused-vars
    const [lowerTolerance, setLowerTolerance] = useState("");
    // eslint-disable-next-line no-unused-vars
    const [receipt, setReceipt] = useState("");
    // eslint-disable-next-line no-unused-vars
    const [storageLocation, setStorageLocation] = useState("");
    const [routingRows, setRoutingRows] = useState([]);
    const [bomRows, setBomRows] = useState([]);
    const [showClassModal, setShowClassModal] = useState(false);
    const [viewSalesOrderRows, setViewSalesOrderRows] = useState([]);    // all SO lines for this production order
    const [activeCharRow, setActiveCharRow] = useState(null);           // which SO line's characteristics to show
    // eslint-disable-next-line no-unused-vars
    const [maxActivities, setMaxActivities] = useState(0);
    const [dashboardData, setDashboardData] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [confirmQtys, setConfirmQtys] = useState({});
    const [selectedBatches, setSelectedBatches] = useState({});
    const [selectedWorkcenter, setSelectedWorkcenter] = useState("");
    // eslint-disable-next-line no-unused-vars
    const [selectedLocation, setSelectedLocation] = useState("");
    const [selectedProdStartDate, setSelectedProdStartDate] = useState("");
    const [selectedProdEndDate, setSelectedProdEndDate] = useState("");
    const [selectedProductionNo, setSelectedProductionNo] = useState("");
    const [productionOrderNumbers, setProductionOrderNumbers] = useState([]);
    const [selectedSalesOrderNoFilter, setSelectedSalesOrderNoFilter] = useState("");
    const [salesOrderNumbersFilter, setSalesOrderNumbersFilter] = useState([]);
    const [workcenterNames, setWorkcenterNames] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [locations, setLocations] = useState([]);
    const [storageBins, setStorageBins] = useState([]);
    const [selectedHeaderBin, setSelectedHeaderBin] = useState("");
    const [changeLogs, setChangeLogs] = useState([]);
    
    // Activity Dates State
    const [actualActivities, setActualActivities] = useState({});
    const [actualDates, setActualDates] = useState({});
    const [actualActivityDates, setActualActivityDates] = useState({});
    // Idle Time — keyed by `${opId}-machine`, e.g. { "12-machine": { flag: "Yes", value: "15", reason: "Power cut" } }
    const [actualIdle, setActualIdle] = useState({});

    const [genActualStartDate, setGenActualStartDate] = useState("");
    const [genActualEndDate, setGenActualEndDate] = useState("");
    const [genActualStartTime, setGenActualStartTime] = useState("");
    const [genActualEndTime, setGenActualEndTime] = useState("");
    const [showProducedCharModal, setShowProducedCharModal] = useState(false);
    const [producedCharacteristics, setProducedCharacteristics] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [hasCharacteristics, setHasCharacteristics] = useState(false);
    const [outputAttributes, setOutputAttributes] = useState([]);
    const [processParameters, setProcessParameters] = useState([]);
    const [machineParameters, setMachineParameters] = useState([]);
    const [processParamActuals, setProcessParamActuals] = useState({});
    const [machineParamActuals, setMachineParamActuals] = useState({});
    const [inspectionParameters, setInspectionParameters] = useState([]);
    const [inspectionParamActuals, setInspectionParamActuals] = useState({});
    const [inspectionChecked, setInspectionChecked] = useState({});
    const [componentAttributes, setComponentAttributes] = useState({});
    const [statusOptions, setStatusOptions] = useState([]);
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [checkedBy, setCheckedBy] = useState("");
    // Operator Name — user-entered on confirm (NOT auto-filled from the
    // logged-in user's name/email). Persisted to confirmations.confirmed_by
    // and re-populated from getConfirmationDetails (modalData.confirmed_by)
    // when viewing/editing an existing confirmation.
    const [operatorName, setOperatorName] = useState("");
    // Tracks whether the user has attempted to Confirm at least once —
    // used to show red-highlighted missing required fields (Operator Name,
    // Output Storage Location) only after a failed attempt.
    const [confirmAttemptedSave, setConfirmAttemptedSave] = useState(false);
    const [storageLocations, setStorageLocations] = useState([]);
    const [outputProduced, setOutputProduced] = useState("");
    const [outputScrap, setOutputScrap] = useState("");
    // ── Group-driven Output capture (process_configuration.group_id) ──────
    // When the PO's process has a group set, Output captures No. of Bobbins /
    // Length / Weight / Bobbin No, and scrap moves from the Input table to
    // three Output fields: Setup Scrap, Process Scrap, Wirebreak Scrap.
    const [hasScrapGroupLogic, setHasScrapGroupLogic] = useState(false);
    // Scrap headings for this PO's process(es), from Scrap Configuration:
    // [{ process_id, process_name, scrap_name }]
    const [scrapHeadings, setScrapHeadings] = useState([]);
    // ── Group-driven Output rows — ONE row per bobbin. Setup/Process/
    // Wirebreak Scrap are NOT per-bobbin — they live at the order/output
    // level (see outputSetupScrap/outputProcessScrap/outputWirebreakScrap
    // below), shown in the Output table's Scrap column(s) instead.
    const makeEmptyGroupRow = () => ({
        no_of_bobbins: "", length: "", weight: "", bobbin_no: "",
    });
    const [outputGroupRows, setOutputGroupRows] = useState([makeEmptyGroupRow()]);
    // Planned Bobbins — read-only, sourced from the Production Order itself
    // (po_material_details), rendered above the editable Actual table below,
    // mirroring the Machine Time Planned/Actual layout.
    const [plannedBobbinRows, setPlannedBobbinRows] = useState([]);
    // Order-level Setup/Process/Wirebreak Scrap for group processes —
    // rendered in the Output table's Scrap column(s), not per-bobbin.
    const [outputSetupScrap, setOutputSetupScrap] = useState("");
    const [outputProcessScrap, setOutputProcessScrap] = useState("");
    const [outputWirebreakScrap, setOutputWirebreakScrap] = useState("");
    const resetOutputGroupDetails = () => {
        setOutputGroupRows([makeEmptyGroupRow()]);
        setOutputSetupScrap("");
        setOutputProcessScrap("");
        setOutputWirebreakScrap("");
    };

    // Conversion rate between Length (M) and Weight (Kg) for this order's
    // product, snapshotted on po_header at confirm time:
    //   weight = length * (conversion_weight_kg / conversion_length_m)
    //   length = weight * (conversion_length_m / conversion_weight_kg)
    const convLengthM = parseFloat(modalData.conversion_length_m);
    const convWeightKg = parseFloat(modalData.conversion_weight_kg);
    const hasConversionRate = !isNaN(convLengthM) && convLengthM > 0 && !isNaN(convWeightKg) && convWeightKg > 0;

    const updateGroupRowField = (idx, field, value) => {
        setOutputGroupRows(prev => {
            const updated = [...prev];
            const row = { ...updated[idx], [field]: value };

            // Auto-convert Length <-> Weight using the order's conversion rate.
            if (hasConversionRate && field === "length") {
                const lenVal = parseFloat(value);
                row.weight = !isNaN(lenVal) ? String(+(lenVal * (convWeightKg / convLengthM)).toFixed(3)) : "";
            } else if (hasConversionRate && field === "weight") {
                const wtVal = parseFloat(value);
                row.length = !isNaN(wtVal) ? String(+(wtVal * (convLengthM / convWeightKg)).toFixed(3)) : "";
            }

            updated[idx] = row;
            return updated;
        });
    };

    const addGroupRow = () => setOutputGroupRows(prev => [...prev, makeEmptyGroupRow()]);
    const removeGroupRow = (idx) => setOutputGroupRows(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);

    // Merges the order-level Setup/Process/Wirebreak Scrap into bobbin row 0
    // right before sending to the API — the backend still stores scrap
    // against the first confirmation_material_details row it wrote for this
    // confirmation, even though the UI now captures it in the Output table's
    // Scrap column(s) rather than on the bobbin rows themselves.
    const buildOutputGroupRowsPayload = () => {
        if (!outputGroupRows.length) return [];
        return outputGroupRows.map((r, i) => i === 0
            ? { ...r, setup_scrap: outputSetupScrap, process_scrap: outputProcessScrap, wirebreak_scrap: outputWirebreakScrap }
            : r
        );
    };

    // Produced (Output table) is derived, not typed, for group processes:
    // for each bobbin row, multiply No. of Bobbins by Weight (if UOM is Kg)
    // or by Length (if UOM is M), then sum across all rows.
    useEffect(() => {
        if (!hasScrapGroupLogic) return;
        const uom = (modalData.header_uom || "").toString().trim().toUpperCase();
        const field = uom === "KG" ? "weight" : uom === "M" ? "length" : null;
        if (!field) return;
        const total = outputGroupRows.reduce((sum, r) => {
            const bobbins = parseFloat(r.no_of_bobbins) || 0;
            const perBobbin = parseFloat(r[field]) || 0;
            return sum + (bobbins * perBobbin);
        }, 0);
        const rounded = String(+total.toFixed(3));
        setOutputProduced(rounded);
        setDeliveredActual(rounded);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasScrapGroupLogic, outputGroupRows, modalData.header_uom]);
    const [outputStorageBin, setOutputStorageBin] = useState("");
    const [outputActuals, setOutputActuals] = useState({});
    const [showOutputCharModal, setShowOutputCharModal] = useState(false);
    const [outputBatchCharacteristics, setOutputBatchCharacteristics] = useState([]);

    // ── Finished Goods: dynamic per-drum/coil "Produced" modal ─────────────
    // fgCharacteristics: characteristic names resolved via
    //   productdetails.productionClass -> product_configuration.class_code
    //   -> product_configuration_characteristics.characteristic
    // fgBatchRows: one row PER DRUM/COIL (one row = one output batch), e.g.
    //   { values: { "Length of the Drum/Coil": "100" } }
    // The number of rows defaults to the order's "No. of Drum/Coil" value.
    const [showFgBatchModal, setShowFgBatchModal] = useState(false);
    const [fgCharacteristics, setFgCharacteristics] = useState([]);
    const [fgBatchRows, setFgBatchRows] = useState([]);
    const fgRowsInitialized = useRef(false);
    // Qty this confirmation ALREADY recorded (0 for a brand-new confirmation).
    // Produced shown/sent = this base + the NEW drum/coil rows in the modal.
    const [confBaseQty, setConfBaseQty] = useState(0);
    // Bumped every time a modal is (re)opened so the drum/coil rows are
    // rebuilt even when the same production order is opened again.
    const [fgResetKey, setFgResetKey] = useState(0);
    // All output batches saved to `stock` for this production order (each
    // Finished Goods drum/coil is its own batch), returned by
    // getProductionDetails as `outputBatches`, each with its own
    // characteristics (No. of Drum/Coil, Length of the Drum/Coil, ...).
    const [savedOutputBatches, setSavedOutputBatches] = useState([]);
    const [componentStorageLocations, setComponentStorageLocations] = useState({});
    const [batchFactor, setBatchFactor] = useState(1);

    const [poFilterMasterData, setPoFilterMasterData] = useState({
        productTypes: [], constructions: [], codes: [], cableTypes: [], categories: [],
        cores: [], colours: [], nominalCrossSections: [], referenceOdStandards: [], voltages: [],
    });
    const [listingFilters, setListingFilters] = useState({
        productType: null, construction: null, code: null, cableType: null, category: null,
        noOfCores: null, color: null, nominalCrossSection: null,
        referenceOfStandard: null, voltage: null,
    });

    const getCurrentUser = () => {
        const userObj = localStorage.getItem("user");
        if (userObj) {
            try {
                const parsedUser = JSON.parse(userObj);
                // Prefer userId now instead of name/email
                if (parsedUser.userId) return parsedUser.userId;
                if (parsedUser.loggedInAs) return parsedUser.loggedInAs;
                // Fallback to name fields, then email, if userId isn't present
                const fullName = `${parsedUser.firstName || ''} ${parsedUser.lastName || ''}`.trim();
                if (fullName) return fullName;
                if (parsedUser.email) return parsedUser.email;
            } catch (e) { console.error("Error parsing user data:", e); }
        }
        const userId = localStorage.getItem("userId");
        if (userId && userId !== "undefined" && userId !== "null") return userId;
        const email = localStorage.getItem("email");
        if (email && email !== "undefined" && email !== "null") return email;
        const storedName = localStorage.getItem("userName");
        if (storedName && storedName !== "undefined" && storedName !== "null") return storedName;
        return "User";
    };

    useEffect(() => {
        const fetchStatuses = async () => {
            try {
                const response = await axios.get(`${PURCHASE_API}/confirmation/confirmed-statuses`);
                if (Array.isArray(response.data)) {
                    const formattedOptions = response.data
                        .filter(item => item && (item.release_status || item.confirmed_status))
                        .map(item => {
                            const val = item.release_status || item.confirmed_status;
                            return { value: val, label: val };
                        });
                    const uniqueOptions = Array.from(new Set(formattedOptions.map(o => o.value)))
                        .map(val => formattedOptions.find(o => o.value === val));
                    setStatusOptions(uniqueOptions);
                    const defaultLabels = ["in stock", "released"];
                    const defaults = uniqueOptions.filter(
                        o => defaultLabels.includes(String(o.value).trim().toLowerCase())
                    );
                    setSelectedStatuses(defaults);
                }
            } catch (error) { console.error("Error fetching statuses:", error); }
        };
        fetchStatuses();
    }, []);

    useEffect(() => {
        axios.get(`${PURCHASE_API}/confirmation/confirm-list`, {
            params: {
                productType: listingFilters.productType?.value || undefined,
                productionNo: selectedProductionNo || undefined,
                salesOrderNo: selectedSalesOrderNoFilter || undefined,
                workcenter: selectedWorkcenter || undefined,
                location: selectedLocation || undefined,
                prodStartDate: selectedProdStartDate || undefined,
                prodEndDate: selectedProdEndDate || undefined,
                construction: listingFilters.construction?.value || undefined,
                code: listingFilters.code?.value || undefined,
                cableType: listingFilters.cableType?.value || undefined,
                category: listingFilters.category?.value || undefined,
                noOfCores: listingFilters.noOfCores?.value || undefined,
                color: listingFilters.color?.value || undefined,
                nominalCrossSection: listingFilters.nominalCrossSection?.value || undefined,
                referenceOfStandard: listingFilters.referenceOfStandard?.value || undefined,
                voltage: listingFilters.voltage?.value || undefined,
                releaseStatus: (selectedStatuses && selectedStatuses.length > 0)
                    ? selectedStatuses.map(s => s.value).join(",")
                    : undefined,
            },
        }).then((res) => setOrders(res.data)).catch(console.error);
    }, [selectedProductionNo, selectedSalesOrderNoFilter, selectedWorkcenter, selectedLocation, selectedProdStartDate, selectedProdEndDate, listingFilters, selectedStatuses]);

    useEffect(() => {
        axios.get(`${PURCHASE_API}/confirmation/order-numbers`).then(res => setProductionOrderNumbers((res.data || []).map(r => r.production_order_number))).catch(console.error);
    }, []);

    useEffect(() => {
        axios.get(`${PURCHASE_API}/confirmation/confirmation-sales-order-numbers`).then(res => setSalesOrderNumbersFilter(res.data || [])).catch(console.error);
    }, []);

    useEffect(() => {
        axios.get(`${PURCHASE_API}/confirmation/storage-bins`).then(res => setStorageBins(res.data || [])).catch(console.error);
    }, []);

    useEffect(() => {
        axios.get(`${PURCHASE_API}/confirmation/storage-locations`).then(res => {
            if (res && res.data) {
                const locs = Array.isArray(res.data) ? res.data : (res.data.data || []);
                setStorageLocations(locs);
            }
        }).catch(console.error);
    }, []);

    useEffect(() => {
        if (!routingRows || routingRows.length === 0) return;
        const sortedOps = [...routingRows].sort((a, b) => Number(a.operation_id) - Number(b.operation_id));
        const firstOp = sortedOps[0];
        const lastOp = sortedOps[sortedOps.length - 1];
        setStartDate(firstOp.start_date ? firstOp.start_date.substring(0, 10) : "");
        setStartTime(firstOp.start_time || "");
        setEndDate(lastOp.end_date ? lastOp.end_date.substring(0, 10) : "");
        setEndTime(lastOp.end_time || "");
    }, [routingRows]);

    useEffect(() => {
        axios.get(`${PURCHASE_API}/confirmation/workcentres`).then((res) => setWorkcenterNames(res.data || [])).catch(console.error);
        fetchPoFilterMasterData();
    }, []);

    useEffect(() => {
        axios.get(`${MASTER_API}/configuration/location`).then((res) => setLocations(res.data.data || res.data)).catch(console.error);
    }, []);

    const isFinishedGoodType = (modalData.productType || "").trim().toUpperCase() === "FINISHED GOODS";

    useEffect(() => {
        if (!isFinishedGoodType || !modalData.header_product_code) {
            setFgCharacteristics([]);
            return;
        }
        axios
            .get(`${PURCHASE_API}/confirmation/finished-good-characteristics/${encodeURIComponent(modalData.header_product_code)}`)
            .then((res) => setFgCharacteristics(res.data.characteristics || []))
            .catch((err) => {
                console.error("Finished Good Characteristics Error:", err);
                setFgCharacteristics([]);
            });
    }, [isFinishedGoodType, modalData.header_product_code]);

    // Columns shown in the modal = ALL fetched characteristics (including
    // "No. of Drum/Coil" itself), since the user now edits both the drum
    // count AND the length per row. Each row is a GROUP: "No. of Drum/Coil"
    // batches will be generated from that row, each with qty = "Length of
    // the Drum/Coil" for that row.
    const fgDisplayColumns = fgCharacteristics;
    const fgDrumColumn =
        fgDisplayColumns.find((c) => (c || "").toLowerCase().includes("no. of drum") || (c || "").toLowerCase().includes("no of drum"))
        || "No. of Drum/Coil";
    const fgLengthColumn =
        fgDisplayColumns.find((c) => (c || "").toLowerCase().includes("length")) || fgDisplayColumns[0];

    // Over/Under Tolerance columns in the Produced modal are never typed by
    // the user — they're auto-filled from the Production Order's own Range
    // (e.g. "95 - 105"), split into its lower/upper bound. Not hardcoded:
    // whatever modalData.range currently holds is parsed every time.
    const parseFgRangeBounds = (rangeStr) => {
        if (!rangeStr) return { over: "", under: "" };
        const match = String(rangeStr).match(/(-?\d+(?:\.\d+)?)\s*-\s*(-?\d+(?:\.\d+)?)/);
        if (!match) return { over: "", under: "" };
        return { over: match[1], under: match[2] };
    };
    const fgToleranceBounds = parseFgRangeBounds(modalData.range);
    const isFgOverToleranceCol = (col) => (col || "").toLowerCase().includes("over tolerance");
    const isFgUnderToleranceCol = (col) => (col || "").toLowerCase().includes("under tolerance");
    const fgDefaultValueFor = (col) =>
        isFgOverToleranceCol(col) ? fgToleranceBounds.over
            : isFgUnderToleranceCol(col) ? fgToleranceBounds.under
                : "";

    // ── Tolerance enforcement (No. of Drum/Coil ceiling + Length must sit
    // inside the order's Range) — ON only when characteristics.tolerance_applied
    // = 1 for this production order (not a hardcoded switch).
    const fgOrderDrumCharRow = producedCharacteristics.find(
        (c) => (c.characteristic || "").trim().toLowerCase() === "no. of drum/coil"
    );
    const fgOrderDrumCount = Math.max(1, parseInt(fgOrderDrumCharRow?.standard_value, 10) || 1);
    const fgToleranceEnabled = producedCharacteristics.some(
        (c) => c.tolerance_applied === 1 || c.tolerance_applied === true || c.tolerance_applied === "1"
    );
    // Sum of "No. of Drum/Coil" across a given set of rows.
    const fgDrumSum = (rows) => rows.reduce((sum, r) => sum + (parseFloat(r?.[fgDrumColumn]) || 0), 0);

    // How many drums/coils were already produced in EARLIER confirmations
    // for this order (each saved output batch carries its own "No. of
    // Drum/Coil" as a characteristic). Partial confirmations are allowed —
    // e.g. 5 now, 5 later — so the ceiling for THIS confirmation is
    // whatever's left, not the order's full total.
    const fgAlreadyProducedDrums = savedOutputBatches.reduce((sum, b) => {
        const match = (b.characteristics || []).find((ch) => ch.characteristic === fgDrumColumn);
        return sum + (match ? parseFloat(match.value) || 0 : 0);
    }, 0);
    const fgRemainingDrumCount = Math.max(0, fgOrderDrumCount - fgAlreadyProducedDrums);

    // Reset the "already initialized" flag whenever a different production
    // order is opened, so its default row gets (re)built.
    useEffect(() => {
        fgRowsInitialized.current = false;
        setFgBatchRows([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalData.production_order_number, modalData.confirmationId, fgResetKey]);

    // Build the default rows — one row PER drum/coil, count coming from the
    // order's "No. of Drum/Coil" characteristic (e.g. 11 -> 11 empty rows).
    // Nothing is pre-filled; the user types the length (and batch number is
    // auto-generated on Confirm, not entered here).
    useEffect(() => {
        if (!isFinishedGoodType) return;
        if (fgCharacteristics.length === 0) return;
        if (fgRowsInitialized.current) return;

        fgRowsInitialized.current = true;
        setFgBatchRows(
            Array.from({ length: Math.max(1, fgRemainingDrumCount) }, () =>
                Object.fromEntries(fgDisplayColumns.map((c) => [c, fgDefaultValueFor(c)]))
            )
        );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFinishedGoodType, fgCharacteristics, producedCharacteristics, modalData.range, savedOutputBatches]);

    // Per-row qty = No. of Drum/Coil * Length of the Drum/Coil for that row
    // (blank "No. of Drum/Coil" defaults to 1). Each row is still exactly
    // ONE batch record — the drum count no longer multiplies how many
    // batches get created, it only multiplies the quantity on that one batch.
    const fgRowQty = (row) =>
        (parseFloat(row?.[fgDrumColumn]) || 1) * (parseFloat(row?.[fgLengthColumn]) || 0);

    const fgTotalProduced = fgBatchRows.reduce((sum, row) => sum + fgRowQty(row), 0);

    useEffect(() => {
        if (!isFinishedGoodType) return;
        // Produced = what this confirmation already recorded (confBaseQty,
        // 0 for a new confirmation) + the NEW drum/coil rows entered now.
        // Previously the effect only ran when the new rows summed > 0 and
        // REPLACED Produced with that sum, so adding a line never added to
        // the existing qty (and an empty modal left a stale cumulative qty).
        if (!(isConfirmMode || isEditMode)) return;
        const total = (parseFloat(confBaseQty) || 0) + fgTotalProduced;
        setOutputProduced(total > 0 ? String(+total.toFixed(3)) : "");
        setDeliveredActual(total > 0 ? String(+total.toFixed(3)) : "");
    }, [isFinishedGoodType, fgTotalProduced, confBaseQty, isConfirmMode, isEditMode]);

    // New drum/coil rows (qty > 0) as the backend expects them: one row =
    // one NEW batch, every typed column except the auto Batch Number is
    // stored as a characteristic of that batch.
    const buildFgBatchesPayload = () =>
        fgBatchRows
            .filter(r => (parseFloat(r[fgLengthColumn]) || 0) > 0)
            .map(r => ({
                qty: fgRowQty(r),
                characteristics: Object.entries(r)
                    .filter(([characteristic]) => !(characteristic || "").toLowerCase().includes("batch"))
                    .map(([characteristic, value]) => ({ characteristic, value })),
            }));

    const confirmBatch = async (productionNo) => {
        if (!operatorName || !operatorName.trim()) {
            setConfirmAttemptedSave(true);
            toast.warn("Please enter Operator Name");
            return;
        }

        const pendingCalc = Math.max(0, (parseFloat(modalData.quantity) || 0) - (parseFloat(modalData.total_produced_qty) || 0) - (parseFloat(outputProduced) || 0));

        const operationsData = Object.keys(actualDates).map(opId => {
            const routingRow = routingRows.find(r => String(r.operation_id) === String(opId));
            return {
                operation_id: opId,
                workcenter_name: routingRow?.workcenter_name || "",
                actual_start_date: actualDates[opId].actual_start_date,
                actual_start_time: actualDates[opId].actual_start_time,
                actual_end_date: actualDates[opId].actual_end_date,
                actual_end_time: actualDates[opId].actual_end_time,
                activities: Object.keys(actualActivities)
                    .filter(key => key.startsWith(`${opId}-`))
                    .map(key => {
                        const type = key.split('-')[1];
                        const datesObj = actualActivityDates[key] || {};
                        const idle = type === "machine" ? (actualIdle[key] || {}) : null;
                        return {
                            activity_type: type,
                            actual_value: actualActivities[key],
                            actual_start_date: datesObj.start_date || null,
                            actual_start_time: datesObj.start_time || null,
                            actual_end_date: datesObj.end_date || null,
                            actual_end_time: datesObj.end_time || null,
                            idle_time_flag: idle ? (idle.flag || null) : null,
                            idle_time_value: idle && idle.flag === "Yes" ? (idle.value || null) : null,
                            idle_time_reason: idle && idle.flag === "Yes" ? (idle.reason || null) : null
                        };
                    })
            };
        });

        // Input rows now carry their own Setup/Process/Wirebreak Scrap.
        // total scrap = setup scrap + process scrap + wirebreak scrap
        // total consumed = consumed + total scrap
        const componentsData = bomRows
            .filter(r => r.type !== 'output' && parseFloat(r.consumed) > 0)
            .map((row) => {
                const rowId = row.id || `${row.item}-${row.product_code}`;
                const batchesArr = Array.isArray(selectedBatches[rowId]) ? selectedBatches[rowId] : [];
                const consumed = parseFloat(row.consumed) || 0;
                const useDynamicScrap = scrapHeadings.length > 0;
                const setupScrap = useDynamicScrap ? 0 : (parseFloat(row.setup_scrap) || 0);
                const processScrap = useDynamicScrap ? 0 : (parseFloat(row.process_scrap) || 0);
                const wirebreakScrap = useDynamicScrap ? 0 : (parseFloat(row.wirebreak_scrap) || 0);
                // Dynamic scraps: { "<scrap name>": qty } saved per component
                const scrapsObj = {};
                const scrapProcessIds = {};
                const scrapProcessNames = {};
                scrapHeadings.forEach(h => {
                    scrapsObj[h.scrap_name] = parseFloat((row.scraps || {})[h.scrap_name]) || 0;
                    scrapProcessIds[h.scrap_name] = h.process_id;
                    scrapProcessNames[h.scrap_name] = h.process_name;
                });
                const totalScrap = useDynamicScrap
                    ? Object.values(scrapsObj).reduce((a, b) => a + b, 0)
                    : setupScrap + processScrap + wirebreakScrap;
                const totalConsumed = consumed + totalScrap;

                return {
                    scraps: useDynamicScrap ? scrapsObj : null,
                    scrap_process_ids: useDynamicScrap ? scrapProcessIds : null,
                    scrap_process_names: useDynamicScrap ? scrapProcessNames : null,
                    rowId,
                    product_code: row.product_code,
                    product_name: row.description || row.component_description,
                    consumed_qty: consumed,
                    setup_scrap: setupScrap,
                    process_scrap: processScrap,
                    wirebreak_scrap: wirebreakScrap,
                    total_scrap: totalScrap,
                    total_consumed: totalConsumed,
                    batches: batchesArr,
                    storage_bin: batchesArr[0]?.storage_location || null,
                };
            });

        // ── VALIDATION: Input section — every row must have a consumed value ──
        const inputRows = bomRows.filter(r => r.type !== 'output');
        const missingInputRow = inputRows.find(r => !(parseFloat(r.consumed) > 0));
        if (inputRows.length > 0 && missingInputRow) {
            setConfirmAttemptedSave(true);
            toast.warn(`Enter Consumed quantity for "${missingInputRow.description || missingInputRow.component_description || missingInputRow.product_code || 'component'}" in the Input section`);
            return;
        }

        // ── VALIDATION: Output section — produced quantity/batches are required ──
        if (!isFinishedGoodType) {
            if (!(parseFloat(outputProduced) > 0)) {
                setConfirmAttemptedSave(true);
                toast.warn("Enter Produced quantity in the Output section");
                return;
            }
        }

        // ── VALIDATION: Output section — Storage Location is required ──
        if (!outputStorageBin) {
            setConfirmAttemptedSave(true);
            toast.warn("Please select Storage Location in the Output section");
            return;
        }

        let fgValidRows = [];
        if (isFinishedGoodType) {
            fgValidRows = fgBatchRows.filter(r => (parseFloat(r[fgLengthColumn]) || 0) > 0);
            if (fgValidRows.length === 0) {
                setConfirmAttemptedSave(true);
                toast.warn("Enter Length of the Drum/Coil in the Produced modal first");
                return;
            }

            if (fgToleranceEnabled) {
                // Partial confirmations are allowed (e.g. 5 drums now, 5 more
                // later) — this confirmation just can't push the cumulative
                // total (already produced + this batch) past what the order
                // defines.
                const totalDrums = fgDrumSum(fgValidRows);
                if (totalDrums > fgRemainingDrumCount) {
                    toast.warn(
                        `Only ${fgRemainingDrumCount} Drum/Coil remaining to produce (order requires ${fgOrderDrumCount} total, ${fgAlreadyProducedDrums} already produced) — you entered ${totalDrums}.`
                    );
                    return;
                }

                // Every row's Length of the Drum/Coil must sit inside the
                // order's Range (Over Tolerance – Under Tolerance).
                if (fgToleranceBounds.over !== "" && fgToleranceBounds.under !== "") {
                    const lower = parseFloat(fgToleranceBounds.over);
                    const upper = parseFloat(fgToleranceBounds.under);
                    const outOfRange = fgValidRows.some(r => {
                        const len = parseFloat(r[fgLengthColumn]);
                        return isNaN(len) || len < lower || len > upper;
                    });
                    if (outOfRange) {
                        toast.warn(`Length of the Drum/Coil must be between ${lower} and ${upper} (this order's Range).`);
                        return;
                    }
                }
            }
        }

        const componentActualsPayload = Object.fromEntries(
            Object.entries(componentAttributes).map(([pc, attrs]) => [
                pc,
                (attrs || []).map(a => ({
                    characteristic: a.characteristic,
                    value: a.value,
                    actual_value: a.actual_value,
                }))
            ])
        );

        const processParamActualsPayload = {};
        Object.entries(processParamActuals).forEach(([k, v]) => { processParamActualsPayload[k] = v; });

        const machineParamActualsPayload = {};
        Object.entries(machineParamActuals).forEach(([k, v]) => { machineParamActualsPayload[k] = v; });

        const inspectionParamActualsPayload = {};
        Object.entries(inspectionParamActuals).forEach(([k, v]) => { inspectionParamActualsPayload[k] = v; });

        const inspectionCheckedPayload = {};
        Object.entries(inspectionChecked).forEach(([k, v]) => { inspectionCheckedPayload[k] = v; });

        const checkedValues = Object.values(inspectionCheckedPayload).filter(v => v !== "");
        const inspectionStatus = checkedValues.length > 0 && checkedValues.every(v => v === "Passed")
            ? "Passed" : checkedValues.some(v => v === "NC") ? "NC" : "";

        try {
            const res = await axios.post(`${PURCHASE_API}/confirmation/confirm-batch`, {
                productionNo,
                batchFactor,
                actualQty: pendingCalc,
                deliveredActual: outputProduced,
                outputScrap: outputScrap,
                startDate: null, endDate: null, startTime: null, endTime: null,
                characteristics: outputBatchCharacteristics,
                // Each row entered by the user IS exactly one batch. Its qty
                // is "No. of Drum/Coil" x "Length of the Drum/Coil" for that
                // row. Every other field typed on the row — including
                // "No. of Drum/Coil" itself — is stored as a characteristic
                // against that batch (only the auto-generated Batch Number
                // column is excluded).
                finishedGoodBatches: isFinishedGoodType
                    ? fgValidRows.map(r => {
                        const qty = fgRowQty(r);
                        const rowCharacteristics = Object.entries(r)
                            .filter(([characteristic]) =>
                                !(characteristic || "").toLowerCase().includes("batch")
                            )
                            .map(([characteristic, value]) => ({ characteristic, value }));
                        return {
                            qty,
                            characteristics: rowCharacteristics,
                        };
                    })
                    : [],
                outputActuals,
                outputStorageBin,
                operations: operationsData,
                components: componentsData,
                headerStorageBin: outputStorageBin || selectedHeaderBin,
                updatedBy: getCurrentUser(),
                checkedBy,
                // Operator Name — whatever the user typed into the field,
                // never the logged-in user's own name/email.
                confirmedBy: operatorName,
                componentActuals: componentActualsPayload,
                processParamActuals: processParamActualsPayload,
                machineParamActuals: machineParamActualsPayload,
                inspectionParamActuals: inspectionParamActualsPayload,
                inspectionChecked: inspectionCheckedPayload,
                inspectionStatus,
                // Group-driven Output fields — only sent when this
                // production order's process has process_configuration
                // .group_id set (see hasScrapGroupLogic).
                outputGroupRows: hasScrapGroupLogic ? buildOutputGroupRowsPayload() : null,
                // Output-level scrap — sent for EVERY process. The server uses it
                // only when no bobbin rows are sent (non-group processes).
                outputScraps: {
                    setup_scrap: outputSetupScrap,
                    process_scrap: outputProcessScrap,
                    wirebreak_scrap: outputWirebreakScrap,
                },
            });

            if (res.data.success) {
                toast.success(`Batch Confirmed Successfully: ${res.data.batchNo}`);

                // Freeze this batch's cost right away, so the Product Costing
                // dashboard has actual costs to show for it. Non-blocking:
                // if this fails, the batch confirmation itself has already
                // succeeded, so we just log it instead of showing an error.
                try {
                    await axios.post(`${PURCHASE_API}/product-costing/calculate-batch-cost`, {
                        productionOrderNumber: productionNo,
                        batchNo: res.data.batchNo,
                        confirmationId: res.data.confirmationId,
                    });
                } catch (costErr) {
                    console.error("Failed to calculate/store batch cost:", costErr);
                }

                setConfirmQtys({});
                setSelectedBatches({});
                setQtyErrors({});
                setOutputProduced("");
                setOutputScrap("");
                setOutputStorageBin("");
                resetOutputGroupDetails();
                setOutputActuals({});
                setOutputBatchCharacteristics([]);
                setProducedCharacteristics([]);
                setHasCharacteristics(false);
                setFgBatchRows([]);
                fgRowsInitialized.current = false;
                setInspectionChecked({});
                setBatchFactor(1);
                setTimeout(() => { setShowModal(false); window.location.reload(); }, 2000);
            } else {
                toast.error(res.data.message || "Failed to confirm batch.");
            }
        } catch (err) {
            console.error(err);
            toast.error("Server Error: Could not confirm batch.");
        }
    };

    const saveInspection = async (productionNo) => {
        const inspectionParamActualsPayload = {};
        Object.entries(inspectionParamActuals).forEach(([k, v]) => { inspectionParamActualsPayload[k] = v; });
        const inspectionCheckedPayload = {};
        Object.entries(inspectionChecked).forEach(([k, v]) => { inspectionCheckedPayload[k] = v; });

        // eslint-disable-next-line no-unused-vars
        const filledChecked = Object.values(inspectionCheckedPayload).filter(v => v !== "");
        // Validation removed: inspection can now be saved even with nothing filled in yet.

        try {
            const res = await axios.post(`${PURCHASE_API}/confirmation/save-inspection`, {
                productionNo,
                confirmationId: modalData.confirmationId || null,
                inspectionParamActuals: inspectionParamActualsPayload,
                inspectionChecked: inspectionCheckedPayload,
                checkedBy,
                updatedBy: getCurrentUser(),
                outputStorageBin,
            });
            if (res.data.success) {
                toast.success(res.data.message || "Inspection saved successfully!");
                // Use THIS confirmation's own status (confirmationStatus) when the
                // save was confirmation-scoped — res.data.releaseStatus is the
                // PO-header-level status, which intentionally does not change on
                // a per-confirmation inspection save, and would otherwise make it
                // look like nothing was saved.
                setModalData(m => ({
                    ...m,
                    release_status: modalData.confirmationId
                        ? (res.data.confirmationStatus || m.release_status)
                        : res.data.releaseStatus,
                }));
                setTimeout(() => { setShowModal(false); window.location.reload(); }, 1800);
            } else {
                toast.error(res.data.message || "Failed to save inspection.");
            }
        } catch (err) {
            console.error(err);
            toast.error("Server Error: Could not save inspection.");
        }
    };

    const saveActuals = async (productionNo) => {
        const operationsData = Object.keys(actualDates).map(opId => {
            const routingRow = routingRows.find(r => String(r.operation_id) === String(opId));
            return {
                operation_id: opId,
                workcenter_name: routingRow?.workcenter_name || "",
                actual_start_date: actualDates[opId].actual_start_date,
                actual_start_time: actualDates[opId].actual_start_time,
                actual_end_date: actualDates[opId].actual_end_date,
                actual_end_time: actualDates[opId].actual_end_time,
                activities: Object.keys(actualActivities)
                    .filter(key => key.startsWith(`${opId}-`))
                    .map(key => {
                        const type = key.split('-')[1];
                        const datesObj = actualActivityDates[key] || {};
                        const idle = type === "machine" ? (actualIdle[key] || {}) : null;
                        return {
                            activity_type: type,
                            actual_value: actualActivities[key],
                            actual_start_date: datesObj.start_date || null,
                            actual_start_time: datesObj.start_time || null,
                            actual_end_date: datesObj.end_date || null,
                            actual_end_time: datesObj.end_time || null,
                            idle_time_flag: idle ? (idle.flag || null) : null,
                            idle_time_value: idle && idle.flag === "Yes" ? (idle.value || null) : null,
                            idle_time_reason: idle && idle.flag === "Yes" ? (idle.reason || null) : null
                        };
                    })
            };
        });

        const componentActualsPayload = Object.fromEntries(
            Object.entries(componentAttributes).map(([pc, attrs]) => [
                pc,
                (attrs || []).map(a => ({
                    characteristic: a.characteristic,
                    value: a.value,
                    actual_value: a.actual_value,
                }))
            ])
        );

        const processParamActualsPayload = {};
        Object.entries(processParamActuals).forEach(([k, v]) => { processParamActualsPayload[k] = v; });

        const machineParamActualsPayload = {};
        Object.entries(machineParamActuals).forEach(([k, v]) => { machineParamActualsPayload[k] = v; });

        const inspectionParamActualsPayload = {};
        Object.entries(inspectionParamActuals).forEach(([k, v]) => { inspectionParamActualsPayload[k] = v; });

        const inspectionCheckedPayload = {};
        Object.entries(inspectionChecked).forEach(([k, v]) => { inspectionCheckedPayload[k] = v; });

        // ── Finished Goods: validate the NEW drum/coil rows added while editing ──
        let fgNewBatches = [];
        if (isFinishedGoodType) {
            fgNewBatches = buildFgBatchesPayload();
            if (fgToleranceEnabled && fgNewBatches.length > 0) {
                const validRows = fgBatchRows.filter(r => (parseFloat(r[fgLengthColumn]) || 0) > 0);
                const totalDrums = fgDrumSum(validRows);
                if (totalDrums > fgRemainingDrumCount) {
                    toast.warn(`Only ${fgRemainingDrumCount} Drum/Coil remaining to produce (order requires ${fgOrderDrumCount} total, ${fgAlreadyProducedDrums} already produced) — you entered ${totalDrums}.`);
                    return;
                }
                if (fgToleranceBounds.over !== "" && fgToleranceBounds.under !== "") {
                    const lower = parseFloat(fgToleranceBounds.over);
                    const upper = parseFloat(fgToleranceBounds.under);
                    if (validRows.some(r => { const len = parseFloat(r[fgLengthColumn]); return isNaN(len) || len < lower || len > upper; })) {
                        toast.warn(`Length of the Drum/Coil must be between ${lower} and ${upper} (this order's Range).`);
                        return;
                    }
                }
            }
        }

        try {
            const res = await axios.post(`${PURCHASE_API}/confirmation/update-actuals`, {
                productionNo,
                confirmationId: modalData.confirmationId || null,
                // NEW drum/coil rows only — each becomes its own batch on the server
                finishedGoodBatches: fgNewBatches,
                characteristics: outputBatchCharacteristics,
                outputActuals,
                deliveredActual: outputProduced,
                outputStorageBin,
                operations: operationsData,
                componentActuals: componentActualsPayload,
                processParamActuals: processParamActualsPayload,
                machineParamActuals: machineParamActualsPayload,
                inspectionParamActuals: inspectionParamActualsPayload,
                inspectionChecked: inspectionCheckedPayload,
                checkedBy,
                updatedBy: getCurrentUser(),
                // Group-driven Output fields — only sent when this production
                // order's process has process_configuration.group_id set.
                outputGroupRows: hasScrapGroupLogic ? buildOutputGroupRowsPayload() : null,
                // Output-level scrap — sent for EVERY process. The server uses it
                // only when no bobbin rows are sent (non-group processes).
                outputScraps: {
                    setup_scrap: outputSetupScrap,
                    process_scrap: outputProcessScrap,
                    wirebreak_scrap: outputWirebreakScrap,
                },
            });

            if (res.data.success) {
                toast.success(res.data.message || "Changes saved successfully");
                setIsEditMode(false);
                if (res.data.releaseStatus) {
                    setModalData(m => ({ ...m, release_status: res.data.releaseStatus }));
                }
                setTimeout(() => { setShowModal(false); window.location.reload(); }, 1800);
            } else {
                toast.error(res.data.message || "Failed to save changes.");
            }
        } catch (err) {
            console.error(err);
            toast.error("Server Error: Could not save changes.");
        }
    };

    const fetchInventory = async (allRows) => {
        const uniqueCodes = [...new Set(allRows.map(r => r.product_code))];
        const productTypes = {};
        allRows.forEach(r => {
            if (r.product_code && productTypes[r.product_code] === undefined) {
                productTypes[r.product_code] = r.product_type || r.item_category || "";
            }
        });

        try {
            const response = await fetch(`${PURCHASE_API}/confirmation/inventory-batches`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    productCodes: uniqueCodes,
                    productTypes,
                    productionNo: modalData.production_order_number,
                })
            });
            const result = await response.json();
            if (result.success) setDashboardData(result.data);
        } catch (err) {
            console.error("Inventory fetch error:", err);
        }
    };

    useEffect(() => {
        if (bomRows && bomRows.length > 0) fetchInventory(bomRows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bomRows]);

    const openDetails = async (production_order_number, confirmationId = null, mode = "view") => {
        try {
            setConfBaseQty(0);
            setFgResetKey(k => k + 1);
            const res = await axios.get(`${PURCHASE_API}/confirmation/details/${production_order_number}`);
            {
                const data = res.data.data;
                const bomRowsFromApi = res.data.bomRows || [];
                const outputAttrsFromApi = res.data.outputAttributes || [];
                const procParams = res.data.processParameters || [];
                const machParams = res.data.machineParameters || [];
                const inspParams = res.data.inspectionParameters || [];
                const charRows = res.data.characteristics || [];
                const batchChars = res.data.batchCharacteristics || [];
                setSavedOutputBatches(res.data.outputBatches || []);
                setViewSalesOrderRows(res.data.salesOrders || []);
                const header = data[0];

                setModalData({
                    location: header.location || "",
                    order_type: header.order_type || "",
                    production_order_number: header.production_order_number || "",
                    productType: header.productType || "",
                    productGroup: header.productGroup || "",
                    productBrand: header.productBrand || "",
                    productCategory: header.productCategory || "",
                    header_product_code: header.header_product_code || "",
                    productDescription: header.productDescription || header.product_description || "",
                    alternative_bom: header.alternative_bom || "",
                    header_uom: header.header_uom || "",
                    base_unit: header.base_unit,
                    construction: header.construction || "",
                    code: header.code || "",
                    cableType: header.cableType || header.cable_type || "",
                    category: header.productCategory || header.category || "",
                    noOfCores: header.noOfCores || header.no_of_cores || "",
                    color: header.color || header.colour || "",
                    nominalCrossSection: header.nominalCrossSection || header.nominal_cross_section || "",
                    referenceOfStandard: header.referenceOfStandard || header.reference_of_standard || "",
                    voltage: header.voltage || "",
                    release_status: header.release_status || "",
                    confirmed_status: header.confirmed_status || "",
                    requirement_date: header.requirement_date || "",
                    class_details: header.class_details || "",
                    quantity: header.general_qty || "",
                    range: header.range || "",
                    upper_tolerance_percent: header.upper_tolerance_percent ?? "",
                    under_tolerance_percent: header.under_tolerance_percent ?? "",
                    tolerance_range: header.tolerance_range || "",
                    qty_in_m: header.qty_in_m ?? "",
                    qty_in_kg: header.qty_in_kg ?? "",
                    conversion_length_m: header.conversion_length_m ?? "",
                    conversion_weight_kg: header.conversion_weight_kg ?? "",
                    total_produced_qty: header.total_produced_qty || 0,
                    pending_qty: (header.pending_qty !== undefined && header.pending_qty !== null && header.pending_qty !== "")
                        ? header.pending_qty
                        : (header.general_qty || 0),
                    batch_no: header.batch_no || "",
                    storage_location: header.storage_location || "",
                    checked_by: header.checked_by || "",
                    confirmed_by: header.confirmed_by || "",
                    createdBy: header.created_by || "",
                    specialRemarks: header.special_remarks || "",
                    customerName: header.customerName || header.customer_name || "",
                    customerPoRef: header.customerPoRef || header.customer_po_ref || "",
                    soQty: header.soQty || header.so_qty || "",
                    salesOrderId: header.sales_order_id || header.salesOrderId || "",
                    soItemId: header.so_item_no || header.soItemId || header.so_item_id || "",
                    productionStartDate: header.production_start_date ? header.production_start_date.substring(0, 10) : "",
                    productionEndDate: header.production_end_date ? header.production_end_date.substring(0, 10) : "",
                    releasedBy: header.released_by || "",
                });

                setQuantity(header.general_qty || "");
                setDelivered(header.delivered || "");
                setScrapQty(header.scrap_qty || "");
                setScrapPercent(header.scrap_percent || "");
                setUpperTolerance(header.upper_tolerance || "");
                setLowerTolerance(header.lower_tolerance || "");
                setReceipt(header.receipt || "");
                setStorageLocation(header.storage_location || "");
                setSelectedHeaderBin(header.storage_location || "");
                setCheckedBy(header.checked_by || "");
                setOperatorName(header.confirmed_by || "");
                setConfirmAttemptedSave(false);
                setOutputProduced(header.total_produced_qty || "");
                setOutputScrap(header.scrap_qty || "");
                setOutputStorageBin(header.storage_location || "");

                // Group-driven logic — flag from getProductionDetails, plus
                // any previously-saved group fields.
                setHasScrapGroupLogic(!!res.data.hasScrapGroupLogic);
                setScrapHeadings(Array.isArray(res.data.scrapHeadings) ? res.data.scrapHeadings : []);
                setPlannedBobbinRows(Array.isArray(res.data.plannedBobbinRows) ? res.data.plannedBobbinRows.map(g => ({
                    no_of_bobbins: g.no_of_bobbins ?? "",
                    length: g.length ?? "",
                    weight: g.weight ?? "",
                    bobbin_no: g.bobbin_no ?? "",
                })) : []);
                if (Array.isArray(res.data.outputGroupRows) && res.data.outputGroupRows.length > 0) {
                    setOutputGroupRows(res.data.outputGroupRows.map(g => ({
                        no_of_bobbins: g.no_of_bobbins ?? "",
                        length: g.length ?? "",
                        weight: g.weight ?? "",
                        bobbin_no: g.bobbin_no ?? "",
                    })));
                    // Setup/Process/Wirebreak Scrap are order-level, not per-bobbin —
                    // stored server-side against the FIRST bobbin row only.
                    const g0 = res.data.outputGroupRows[0];
                    setOutputSetupScrap(g0.setup_scrap ?? "");
                    setOutputProcessScrap(g0.process_scrap ?? "");
                    setOutputWirebreakScrap(g0.wirebreak_scrap ?? "");
                } else if (res.data.outputGroupActual) {
                    const g = res.data.outputGroupActual;
                    setOutputGroupRows([{
                        no_of_bobbins: g.no_of_bobbins ?? "",
                        length: g.length ?? "",
                        weight: g.weight ?? "",
                        bobbin_no: g.bobbin_no ?? "",
                    }]);
                    setOutputSetupScrap(g.setup_scrap ?? "");
                    setOutputProcessScrap(g.process_scrap ?? "");
                    setOutputWirebreakScrap(g.wirebreak_scrap ?? "");
                } else {
                    resetOutputGroupDetails();
                }

                setBomRows((bomRowsFromApi.length > 0 ? bomRowsFromApi : data).map(r => ({
                    ...r,
                    consumed: (() => {
                        const existing = r.confirm_qty !== undefined && r.confirm_qty !== null ? r.confirm_qty : r.consumed;
                        if ((existing === undefined || existing === null || existing === "") && r.batch_number && String(r.batch_number).trim()) {
                            // Rework order — batch pre-assigned & field is locked/greyed out,
                            // so auto-fill Consumed with that batch's qty instead of leaving it blank.
                            return r.open_qty ?? r.total_qty ?? r.quantity ?? "";
                        }
                        return existing;
                    })(),
                    total_consumed: r.total_consumed !== undefined && r.total_consumed !== null ? r.total_consumed : "",
                    storage_location: r.component_storage_bin || r.storage_location || "",
                })));
                
                setOutputAttributes(outputAttrsFromApi);
                setProcessParameters(procParams);
                setMachineParameters(machParams);
                setInspectionParameters(inspParams);

                const ppActuals = {};
                procParams.forEach(r => { ppActuals[`${r.process_name || ""}||${r.parameter_name || ""}`] = r.actual_value || ""; });
                setProcessParamActuals(ppActuals);

                const mpActuals = {};
                machParams.forEach(r => { mpActuals[`${r.workcenter_name || ""}||${r.parameter_name || ""}`] = r.actual_value || ""; });
                setMachineParamActuals(mpActuals);

                const ipActuals = {};
                const ipChecked = {};
                inspParams.forEach(r => {
                    const k = `${r.process_name || ""}||${r.characteristic || ""}`;
                    ipActuals[k] = r.actual_value || "";
                    ipChecked[k] = r.checked || "";
                });
                setInspectionParamActuals(ipActuals);
                setInspectionChecked(ipChecked);

                const outActuals = {};
                outputAttrsFromApi.forEach(r => {
                    if (r.actual_value !== undefined && r.actual_value !== null) outActuals[r.characteristic] = r.actual_value;
                });
                setOutputActuals(outActuals);

                if (batchChars.length > 0) {
                    setOutputBatchCharacteristics(batchChars.map(c => ({
                        characteristic: c.characteristic,
                        value: c.value || "",
                        standard_value: c.standard_value || "",
                    })));
                } else {
                    setOutputBatchCharacteristics([]);
                }

                if (res.data.componentAttributes && Object.keys(res.data.componentAttributes).length > 0) {
                    setComponentAttributes(res.data.componentAttributes);
                } else {
                    setComponentAttributes({});
                }

                if (charRows.length > 0) {
                    setModalData(m => ({
                        ...m,
                        class_details: charRows.map(c => ({ characteristic: c.characteristic, value: c.value, calculation_type: c.calculation_type || "" }))
                    }));
                }

                const orderQty = parseFloat(header.general_qty) || 0;
                const alreadyProduced = parseFloat(header.total_produced_qty) || 0;
                const remaining = Math.max(0, orderQty - alreadyProduced);
                setActualQty(remaining > 0 ? remaining : orderQty);

                if (header.confirmed_status === "Confirmed") setDeliveredActual(header.total_produced_qty || "");
                else setDeliveredActual("");

                setStartDate(header.startDate?.substring(0, 10) || "");
                setEndDate(header.endDate?.substring(0, 10) || "");
                setStartTime(header.startTime || "");
                setEndTime(header.endTime || "");
                setGenActualStartDate(header.actual_start_date?.substring(0, 10) || "");
                setGenActualEndDate(header.actual_end_date?.substring(0, 10) || "");
                setGenActualStartTime(header.actual_start_time || "");
                setGenActualEndTime(header.actual_end_time || "");

                const seedCharsD = charRows.length > 0 ? charRows.map(c => ({ characteristic: c.characteristic, standard_value: c.value || "", value: "", tolerance_applied: c.tolerance_applied })) : [];
                setProducedCharacteristics(seedCharsD);
                setHasCharacteristics(seedCharsD.length > 0);

                const initialQtys = {};
                const initialBatches = {};
                data.forEach(row => {
                    const rowId = row.id || `${row.item}-${row.product_code}`;
                    if (row.confirm_qty && parseFloat(row.confirm_qty) > 0) initialQtys[rowId] = row.confirm_qty;
                });
                bomRowsFromApi.forEach(row => {
                    const rowId = row.id || `${row.item}-${row.product_code}`;
                    if (Array.isArray(row.batches) && row.batches.length > 0) {
                        initialBatches[rowId] = row.batches;
                    } else if (row.component_batch) {
                        initialBatches[rowId] = [{ batch_number: row.component_batch, qty: row.total_consumed || row.consumed, storage_location: row.component_storage_bin }];
                    } else if (row.batch_number && String(row.batch_number).trim()) {
                        // Rework order — batch pre-assigned at creation time, qty is the row's own total_qty
                        initialBatches[rowId] = [{ batch_number: row.batch_number, qty: row.open_qty ?? row.total_qty ?? row.quantity, storage_location: row.storage_location || row.component_storage_bin }];
                    }
                });
                setConfirmQtys(initialQtys);
                setSelectedBatches(initialBatches);

                const groupedOperations = [];
                const initialActualDates = {};
                const initialActualActivities = {};
                const initialActualActivityDates = {};
                const initialActualIdle = {};

                data.forEach((row) => {
                    const existingOperation = groupedOperations.find(op => op.operation_id === row.process_id);
                    if (existingOperation) {
                        existingOperation.activities.push({ 
                            activity_type: row.activity_type, 
                            activity_value: row.activity_value, 
                            activity_unit: row.activity_unit,
                            activity_start_date: row.activity_start_date || "",
                            activity_start_time: row.activity_start_time || "",
                            activity_end_date: row.activity_end_date || "",
                            activity_end_time: row.activity_end_time || "", 
                        });
                    } else {
                        groupedOperations.push({
                            operation_id: row.process_id, operation_desc: row.operation_desc,
                            control_key: row.control_key, process_id: row.process_id || "",
                            process_name: row.process_name || "", sub_process_id: row.sub_process_id || "",
                            sub_process_name: row.sub_process_name || "", workcenter_name: row.workcenter_name,
                            machine: row.machine || "-", workcenter_description: row.workcenter_description,
                            start_date: row.start_date, start_time: row.start_time,
                            end_date: row.end_date, end_time: row.end_time,
                            activities: [{ 
                                activity_type: row.activity_type, 
                                activity_value: row.activity_value, 
                                activity_unit: row.activity_unit,
                                activity_start_date: row.activity_start_date || "",
                                activity_start_time: row.activity_start_time || "",
                                activity_end_date: row.activity_end_date || "",
                                activity_end_time: row.activity_end_time || "",
                            }],
                        });
                    }
                    if (row.process_id) {
                        if (!initialActualDates[row.process_id]) {
                            initialActualDates[row.process_id] = {
                                actual_start_date: row.actual_start_date?.substring(0, 10) || "",
                                actual_end_date: row.actual_end_date?.substring(0, 10) || "",
                                actual_start_time: row.actual_start_time || "",
                                actual_end_time: row.actual_end_time || ""
                            };
                        }
                        if (row.activity_type) {
                            const key = `${row.process_id}-${row.activity_type.toLowerCase()}`;
                            initialActualActivities[key] = row.actual_activity_value ?? "";
                            initialActualActivityDates[key] = {
                                start_date: row.actual_activity_start_date?.substring(0, 10) || "",
                                start_time: row.actual_activity_start_time || "",
                                end_date: row.actual_activity_end_date?.substring(0, 10) || "",
                                end_time: row.actual_activity_end_time || ""
                            };
                            if (row.activity_type.toLowerCase() === "machine") {
                                initialActualIdle[key] = {
                                    flag: row.idle_time_flag || "",
                                    value: row.idle_time_value ?? "",
                                    reason: row.idle_time_reason || ""
                                };
                            }
                        }
                    }
                });

                setRoutingRows(groupedOperations);
                setActualDates(initialActualDates);
                setActualActivities(initialActualActivities);
                setActualActivityDates(initialActualActivityDates);
                setActualIdle(initialActualIdle);
                const mxAct = Math.max(...groupedOperations.map(op => op.activities.length));
                setMaxActivities(mxAct);
                setExpandedSections({ material_details: false, process_parameter: false, machine_parameter: false, machine_time: false, inspection_parameter: false, special_remarks: false, documents: false, logs: false });
                setIsConfirmMode(false);
                setIsEditMode(false);
            }

            // ── CONFIRMATION-SCOPED OVERLAY ─────────────────────────────
            // Everything above populated the modal from the PO-wide/
            // cumulative view. If the user picked a specific confirmation
            // row (production_order_number + confirmation_id combo), pull
            // THAT confirmation's own actuals and overlay them on top —
            // this is what View/Edit/Save must reflect and update.
            if (confirmationId) {
                const res2 = await axios.get(`${PURCHASE_API}/confirmation/confirmation-details/${confirmationId}`);
                const cd = res2.data;
                const cHeader = cd.confirmation || {};

                setModalData(m => ({
                    ...m,
                    confirmationId,
                    total_produced_qty: cHeader.quantity ?? m.total_produced_qty,
                    storage_location: cHeader.storage_location || m.storage_location,
                    batch_no: cHeader.batch_no || m.batch_no,
                    // Use this confirmation's OWN release_status (not the PO
                    // header's, which no longer changes on confirm) so the
                    // Inspect-mode "In Quality" check enables the fields.
                    release_status: cHeader.release_status || m.release_status,
                }));
                setOutputProduced(cHeader.quantity ?? "");
                setConfBaseQty(parseFloat(cHeader.quantity) || 0);
                setOutputStorageBin(cHeader.storage_location || "");
                setDeliveredActual(cHeader.quantity ?? "");

                // Output-level scrap for NON-bobbin processes — this
                // confirmation's own saved values (bobbin processes keep
                // their existing behaviour untouched).
                if (!res.data.hasScrapGroupLogic) {
                    const os = cd.outputScrapRow;
                    setOutputSetupScrap(os?.setup_scrap ?? "");
                    setOutputProcessScrap(os?.process_scrap ?? "");
                    setOutputWirebreakScrap(os?.wirebreak_scrap ?? "");
                }

                // This confirmation's OWN sales-order line snapshot (one row
                // per SO line, captured at confirm time) — overrides the
                // live PO-wide salesOrders set above so the modal shows
                // exactly what THIS confirmation recorded.
                if (Array.isArray(cd.salesOrders) && cd.salesOrders.length > 0) {
                    setViewSalesOrderRows(cd.salesOrders);
                }

                // Machine time (actual op dates + activity actuals) — THIS
                // confirmation's own confirmation_machine_time rows, same
                // shape/keys as the PO-wide initial build above (keyed by
                // process_id, and by `${process_id}-${activity_type}` for
                // activities) so it binds to the same inputs.
                const mtActualDatesC = {};
                const mtActualActivitiesC = {};
                const mtActualActivityDatesC = {};
                const mtActualIdleC = {};
                (cd.machineTime || []).forEach(row => {
                    if (row.process_id == null) return;
                    if (!row.activity_type) {
                        // General operation-level actual dates row
                        mtActualDatesC[row.process_id] = {
                            actual_start_date: row.actual_start_date ? String(row.actual_start_date).substring(0, 10) : "",
                            actual_start_time: row.actual_start_time || "",
                            actual_end_date: row.actual_end_date ? String(row.actual_end_date).substring(0, 10) : "",
                            actual_end_time: row.actual_end_time || "",
                        };
                    } else {
                        const key = `${row.process_id}-${row.activity_type.toLowerCase()}`;
                        mtActualActivitiesC[key] = row.actual_activity_value ?? "";
                        mtActualActivityDatesC[key] = {
                            start_date: row.actual_activity_start_date ? String(row.actual_activity_start_date).substring(0, 10) : "",
                            start_time: row.actual_activity_start_time || "",
                            end_date: row.actual_activity_end_date ? String(row.actual_activity_end_date).substring(0, 10) : "",
                            end_time: row.actual_activity_end_time || "",
                        };
                        if (row.activity_type.toLowerCase() === "machine") {
                            mtActualIdleC[key] = {
                                flag: row.idle_time_flag || "",
                                value: row.idle_time_value ?? "",
                                reason: row.idle_time_reason || ""
                            };
                        }
                    }
                });
                if (Object.keys(mtActualDatesC).length > 0) {
                    setActualDates(prev => ({ ...prev, ...mtActualDatesC }));
                }
                if (Object.keys(mtActualActivitiesC).length > 0) {
                    setActualActivities(prev => ({ ...prev, ...mtActualActivitiesC }));
                    setActualActivityDates(prev => ({ ...prev, ...mtActualActivityDatesC }));
                }
                if (Object.keys(mtActualIdleC).length > 0) {
                    setActualIdle(prev => ({ ...prev, ...mtActualIdleC }));
                }

                // Roll up the overall "Actual" Start/End Date+Time shown at
                // the top of the modal — earliest operation start and
                // latest operation end, from THIS confirmation's own
                // general (activity_type = null) machine time rows.
                const generalMtRows = (cd.machineTime || []).filter(r => !r.activity_type && (r.actual_start_date || r.actual_end_date));
                if (generalMtRows.length > 0) {
                    const toTs = (d, t) => {
                        if (!d) return null;
                        const datePart = String(d).substring(0, 10);
                        const timePart = t || "00:00:00";
                        const parsed = new Date(`${datePart}T${timePart}`);
                        return isNaN(parsed.getTime()) ? null : parsed.getTime();
                    };

                    let earliest = null, latest = null;
                    generalMtRows.forEach(r => {
                        const startTs = toTs(r.actual_start_date, r.actual_start_time);
                        const endTs = toTs(r.actual_end_date, r.actual_end_time);
                        if (startTs !== null && (earliest === null || startTs < earliest.ts)) {
                            earliest = { ts: startTs, date: String(r.actual_start_date).substring(0, 10), time: r.actual_start_time || "" };
                        }
                        if (endTs !== null && (latest === null || endTs > latest.ts)) {
                            latest = { ts: endTs, date: String(r.actual_end_date).substring(0, 10), time: r.actual_end_time || "" };
                        }
                    });

                    if (earliest) {
                        setGenActualStartDate(earliest.date);
                        setGenActualStartTime(earliest.time);
                    }
                    if (latest) {
                        setGenActualEndDate(latest.date);
                        setGenActualEndTime(latest.time);
                    }
                }

                // Components (type='input') — replace cumulative consumed
                // with THIS confirmation's own consumption only.
                const compByCode = {};
                (cd.materialDetails || []).filter(r => r.type === "input").forEach(r => { compByCode[r.product_code] = r; });
                // Saved dynamic scraps for this confirmation, by component.
                const scrapByCode = {};
                (cd.scrapDetails || []).forEach(sd => {
                    if (!scrapByCode[sd.product_code]) scrapByCode[sd.product_code] = {};
                    scrapByCode[sd.product_code][sd.scrap_name] = sd.scrap_qty;
                });
                // Keep any saved scrap heading even if it was later removed from Scrap Configuration.
                setScrapHeadings(prev => {
                    const known = new Set(prev.map(h => h.scrap_name));
                    const extra = [];
                    (cd.scrapDetails || []).forEach(sd => {
                        if (!known.has(sd.scrap_name)) {
                            known.add(sd.scrap_name);
                            extra.push({ process_id: sd.process_id, process_name: sd.process_name, scrap_name: sd.scrap_name });
                        }
                    });
                    return extra.length ? [...prev, ...extra] : prev;
                });
                setBomRows(prev => prev.map(r => {
                    const match = compByCode[r.product_code];
                    if (!match) {
                        return { ...r, consumed: "", total_consumed: "", component_batch: "", scraps: {} };
                    }
                    return {
                        ...r,
                        scraps: scrapByCode[r.product_code] || {},
                        consumed: match.consumed_qty ?? "",
                        total_consumed: match.total_consumed ?? "",
                        open_qty: match.open_qty,
                        component_batch: match.component_batch || "",
                        storage_location: match.component_storage_bin || r.storage_location || "",
                    };
                }));

                // Output characteristics (type='output')
                const outActualsC = {};
                (cd.materialDetails || []).filter(r => r.type === "output").forEach(r => { outActualsC[r.characteristic] = r.actual_value; });
                setOutputActuals(outActualsC);

                // Process / machine / inspection parameter actuals — same
                // key format as the PO-wide overlay above so existing
                // inputs bind to them without further changes.
                const ppActualsC = {};
                (cd.processParams || []).forEach(r => { ppActualsC[`${r.process_name || ""}||${r.parameter_name || ""}`] = r.actual_value || ""; });
                setProcessParamActuals(ppActualsC);

                const mpActualsC = {};
                (cd.machineParams || []).forEach(r => { mpActualsC[`${r.workcenter_name || ""}||${r.parameter_name || ""}`] = r.actual_value || ""; });
                setMachineParamActuals(mpActualsC);

                const ipActualsC = {};
                const ipCheckedC = {};
                (cd.inspectionParams || []).forEach(r => {
                    const k = `${r.process_name || ""}||${r.characteristic || ""}`;
                    ipActualsC[k] = r.actual_value || "";
                    ipCheckedC[k] = r.checked || "";
                });
                setInspectionParamActuals(ipActualsC);
                setInspectionChecked(ipCheckedC);
            } else {
                setModalData(m => ({ ...m, confirmationId: null }));
            }

            if (mode === "inspect") {
                setIsConfirmMode(true);
            }

            setShowModal(true);
        } catch (err) {
            console.error("Failed loading details:", err);
            alert("Failed loading details");
        }
    };

    // Automatically open the confirmation details modal if the URL tells us to
    // (e.g. coming from a "Confirmation ID" hyperlink on another screen like
    // Batch Tree's stock breakdown).
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const viewProductionOrderNumber = queryParams.get("productionOrderNumber");
        const viewConfirmationId = queryParams.get("confirmationId");

        if (viewProductionOrderNumber && !showModal) {
            openDetails(viewProductionOrderNumber, viewConfirmationId || null, "view");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    // Handle navigation back to the previous screen (when this confirmation was
    // opened via a hyperlink from another screen, e.g. Batch Tree's "Document No"
    // hyperlink)
    const closeDetailsModal = () => {
        setShowModal(false);

        const queryParams = new URLSearchParams(location.search);
        const returnTo = queryParams.get("returnTo");

        if (returnTo) {
            navigate(returnTo);
        } else if (queryParams.get("productionOrderNumber")) {
            // no returnTo, but we auto-opened via URL params -- clean the URL
            navigate(location.pathname, { replace: true });
        }
    };

    const openConfirm = (production_order_number) => {
        setConfBaseQty(0);
        setFgResetKey(k => k + 1);
        axios.get(`${PURCHASE_API}/confirmation/details/${production_order_number}`)
            .then((res) => {
                const data = res.data.data;
                const bomRowsFromApi = res.data.bomRows || [];
                const outputAttrsFromApi = res.data.outputAttributes || [];
                const procParams = res.data.processParameters || [];
                const machParams = res.data.machineParameters || [];
                const charRows = res.data.characteristics || [];
                const header = data[0];

                setModalData({
                    location: header.location || "",
                    order_type: header.order_type || "",
                    production_order_number: header.production_order_number || "",
                    productType: header.productType || "",
                    header_product_code: header.header_product_code || "",
                    productDescription: header.productDescription || header.product_description || "",
                    alternative_bom: header.alternative_bom || "",
                    header_uom: header.header_uom || "",
                    base_unit: header.base_unit,
                    construction: header.construction || "",
                    code: header.code || "",
                    cableType: header.cableType || header.cable_type || "",
                    category: header.productCategory || header.category || "",
                    noOfCores: header.noOfCores || "",
                    color: header.color || header.colour || "",
                    nominalCrossSection: header.nominalCrossSection || "",
                    referenceOfStandard: header.referenceOfStandard || "",
                    voltage: header.voltage || "",
                    release_status: header.release_status || "",
                    confirmed_status: header.confirmed_status || "",
                    requirement_date: header.requirement_date || "",
                    quantity: header.general_qty || "",
                    range: header.range || "",
                    upper_tolerance_percent: header.upper_tolerance_percent ?? "",
                    under_tolerance_percent: header.under_tolerance_percent ?? "",
                    tolerance_range: header.tolerance_range || "",
                    qty_in_m: header.qty_in_m ?? "",
                    qty_in_kg: header.qty_in_kg ?? "",
                    conversion_length_m: header.conversion_length_m ?? "",
                    conversion_weight_kg: header.conversion_weight_kg ?? "",
                    total_produced_qty: header.total_produced_qty || 0,
                    pending_qty: (header.pending_qty !== undefined && header.pending_qty !== null && header.pending_qty !== "")
                        ? header.pending_qty
                        : (header.general_qty || 0),
                    batch_no: header.batch_no || "",
                    storage_location: header.storage_location || "",
                    checked_by: header.checked_by || "",
                    confirmed_by: header.confirmed_by || "",
                    createdBy: header.created_by || "",
                    specialRemarks: header.special_remarks || "",
                    customerName: header.customerName || header.customer_name || "",
                    customerPoRef: header.customerPoRef || header.customer_po_ref || "",
                    soQty: header.soQty || header.so_qty || "",
                    salesOrderId: header.sales_order_id || header.salesOrderId || "",
                    soItemId: header.so_item_no || header.soItemId || header.so_item_id || "",
                    productionStartDate: header.production_start_date ? header.production_start_date.substring(0, 10) : "",
                    productionEndDate: header.production_end_date ? header.production_end_date.substring(0, 10) : "",
                    releasedBy: header.released_by || "",
                    class_details: charRows.length > 0 ? charRows.map(c => ({ characteristic: c.characteristic, value: c.value, calculation_type: c.calculation_type || "" })) : (header.class_details || []),
                });

                setQuantity(header.general_qty || "");
                setCheckedBy(header.checked_by || "");
                setOperatorName(header.confirmed_by || "");
                setConfirmAttemptedSave(false);
                setSelectedHeaderBin(header.storage_location || "");
                // A NEW confirmation starts empty — prefilling the cumulative total here was
                // being sent back as deliveredActual and double counted earlier partials.
                setOutputProduced("");
                setOutputScrap(header.scrap_qty || "");
                setOutputStorageBin(header.storage_location || "");

                // Group-driven logic — flag from getProductionDetails, plus
                // any previously-saved group fields.
                setHasScrapGroupLogic(!!res.data.hasScrapGroupLogic);
                setScrapHeadings(Array.isArray(res.data.scrapHeadings) ? res.data.scrapHeadings : []);
                setPlannedBobbinRows(Array.isArray(res.data.plannedBobbinRows) ? res.data.plannedBobbinRows.map(g => ({
                    no_of_bobbins: g.no_of_bobbins ?? "",
                    length: g.length ?? "",
                    weight: g.weight ?? "",
                    bobbin_no: g.bobbin_no ?? "",
                })) : []);
                if (Array.isArray(res.data.outputGroupRows) && res.data.outputGroupRows.length > 0) {
                    setOutputGroupRows(res.data.outputGroupRows.map(g => ({
                        no_of_bobbins: g.no_of_bobbins ?? "",
                        length: g.length ?? "",
                        weight: g.weight ?? "",
                        bobbin_no: g.bobbin_no ?? "",
                    })));
                    // Setup/Process/Wirebreak Scrap are order-level, not per-bobbin —
                    // stored server-side against the FIRST bobbin row only.
                    const g0 = res.data.outputGroupRows[0];
                    setOutputSetupScrap(g0.setup_scrap ?? "");
                    setOutputProcessScrap(g0.process_scrap ?? "");
                    setOutputWirebreakScrap(g0.wirebreak_scrap ?? "");
                } else if (res.data.outputGroupActual) {
                    const g = res.data.outputGroupActual;
                    setOutputGroupRows([{
                        no_of_bobbins: g.no_of_bobbins ?? "",
                        length: g.length ?? "",
                        weight: g.weight ?? "",
                        bobbin_no: g.bobbin_no ?? "",
                    }]);
                    setOutputSetupScrap(g.setup_scrap ?? "");
                    setOutputProcessScrap(g.process_scrap ?? "");
                    setOutputWirebreakScrap(g.wirebreak_scrap ?? "");
                } else {
                    resetOutputGroupDetails();
                }

                setBomRows((bomRowsFromApi.length > 0 ? bomRowsFromApi : data).map(r => ({
                    ...r,
                    consumed: (() => {
                        const existing = r.confirm_qty !== undefined && r.confirm_qty !== null ? r.confirm_qty : r.consumed;
                        if ((existing === undefined || existing === null || existing === "") && r.batch_number && String(r.batch_number).trim()) {
                            // Rework order — batch pre-assigned & field is locked/greyed out,
                            // so auto-fill Consumed with that batch's qty instead of leaving it blank.
                            return r.open_qty ?? r.total_qty ?? r.quantity ?? "";
                        }
                        return existing;
                    })(),
                    total_consumed: r.total_consumed !== undefined && r.total_consumed !== null ? r.total_consumed : "",
                    storage_location: r.component_storage_bin || r.storage_location || "",
                })));
                
                setOutputAttributes(outputAttrsFromApi);
                setProcessParameters(procParams);
                setMachineParameters(machParams);
                setInspectionParameters(res.data.inspectionParameters || []);

                const ppActualsC = {};
                procParams.forEach(r => { ppActualsC[`${r.process_name || ""}||${r.parameter_name || ""}`] = r.actual_value || ""; });
                setProcessParamActuals(ppActualsC);

                const mpActualsC = {};
                machParams.forEach(r => { mpActualsC[`${r.workcenter_name || ""}||${r.parameter_name || ""}`] = r.actual_value || ""; });
                setMachineParamActuals(mpActualsC);

                const outActualsC = {};
                outputAttrsFromApi.forEach(r => { if (r.actual_value !== undefined && r.actual_value !== null) outActualsC[r.characteristic] = r.actual_value; });
                setOutputActuals(outActualsC);

                const ipActualsC = {};
                const ipCheckedC = {};
                (res.data.inspectionParameters || []).forEach(r => {
                    const k = `${r.process_name || ""}||${r.characteristic || ""}`;
                    ipActualsC[k] = r.actual_value || "";
                    ipCheckedC[k] = r.checked || "";
                });
                setInspectionParamActuals(ipActualsC);
                setInspectionChecked(ipCheckedC);

                const batchCharsC = res.data.batchCharacteristics || [];
                setSavedOutputBatches(res.data.outputBatches || []);
                setViewSalesOrderRows(res.data.salesOrders || []);
                if (batchCharsC.length > 0) {
                    setOutputBatchCharacteristics(batchCharsC.map(c => ({ characteristic: c.characteristic, value: c.value || "" })));
                } else {
                    setOutputBatchCharacteristics([]);
                }

                if (res.data.componentAttributes && Object.keys(res.data.componentAttributes).length > 0) {
                    setComponentAttributes(res.data.componentAttributes);
                } else {
                    setComponentAttributes({});
                }
                
                const seedChars = charRows.length > 0 ? charRows.map(c => ({ characteristic: c.characteristic, standard_value: c.value || "", value: "", tolerance_applied: c.tolerance_applied })) : [];
                setProducedCharacteristics(seedChars);
                setHasCharacteristics(seedChars.length > 0);
                
                setStartDate(header.startDate?.substring(0, 10) || "");
                setEndDate(header.endDate?.substring(0, 10) || "");
                setStartTime(header.startTime || "");
                setEndTime(header.endTime || "");
                setGenActualStartDate(header.actual_start_date?.substring(0, 10) || "");
                setGenActualEndDate(header.actual_end_date?.substring(0, 10) || "");
                setGenActualStartTime(header.actual_start_time || "");
                setGenActualEndTime(header.actual_end_time || "");

                const initialQtys = {};
                const initialBatches = {};
                const groupedOperations = [];
                const initialActualDates = {};
                const initialActualActivities = {};
                const initialActualActivityDates = {};
                const initialActualIdle = {};
                
                data.forEach(row => {
                    const rowId = row.id || `${row.item}-${row.product_code}`;
                    if (row.confirm_qty && parseFloat(row.confirm_qty) > 0) initialQtys[rowId] = row.confirm_qty;
                    const opKey = row.process_id || row.operation_id;
                    const existingOp = groupedOperations.find(op => op.operation_id === opKey);
                    if (existingOp) {
                        existingOp.activities.push({ 
                            activity_type: row.activity_type, 
                            activity_value: row.activity_value, 
                            activity_unit: row.activity_unit,
                            activity_start_date: row.activity_start_date || "",
                            activity_start_time: row.activity_start_time || "",
                            activity_end_date: row.activity_end_date || "",
                            activity_end_time: row.activity_end_time || "",
                        });
                    } else {
                        groupedOperations.push({
                            operation_id: opKey, process_id: row.process_id || "",
                            process_name: row.process_name || "", sub_process_id: row.sub_process_id || "",
                            sub_process_name: row.sub_process_name || "", workcenter_name: row.workcenter_name,
                            workcenter_description: row.workcenter_description, machine: row.machine || "-",
                            start_date: row.start_date, start_time: row.start_time,
                            end_date: row.end_date, end_time: row.end_time,
                            activities: [{ 
                                activity_type: row.activity_type, 
                                activity_value: row.activity_value, 
                                activity_unit: row.activity_unit,
                                activity_start_date: row.activity_start_date || "",
                                activity_start_time: row.activity_start_time || "",
                                activity_end_date: row.activity_end_date || "",
                                activity_end_time: row.activity_end_time || "",
                            }],
                        });
                    }
                    if (opKey && !initialActualDates[opKey]) {
                        initialActualDates[opKey] = {
                            actual_start_date: row.actual_start_date?.substring(0, 10) || "",
                            actual_end_date: row.actual_end_date?.substring(0, 10) || "",
                            actual_start_time: row.actual_start_time || "",
                            actual_end_time: row.actual_end_time || ""
                        };
                    }
                    if (row.activity_type) {
                        const key = `${opKey}-${row.activity_type.toLowerCase()}`;
                        initialActualActivities[key] = row.actual_activity_value ?? "";
                        initialActualActivityDates[key] = {
                            start_date: row.actual_activity_start_date?.substring(0, 10) || "",
                            start_time: row.actual_activity_start_time || "",
                            end_date: row.actual_activity_end_date?.substring(0, 10) || "",
                            end_time: row.actual_activity_end_time || ""
                        };
                        if (row.activity_type.toLowerCase() === "machine") {
                            initialActualIdle[key] = {
                                flag: row.idle_time_flag || "",
                                value: row.idle_time_value ?? "",
                                reason: row.idle_time_reason || ""
                            };
                        }
                    }
                });
                bomRowsFromApi.forEach(row => {
                    const rowId = row.id || `${row.item}-${row.product_code}`;
                    if (Array.isArray(row.batches) && row.batches.length > 0) {
                        initialBatches[rowId] = row.batches;
                    } else if (row.component_batch) {
                        initialBatches[rowId] = [{ batch_number: row.component_batch, qty: row.total_consumed || row.consumed, storage_location: row.component_storage_bin }];
                    } else if (row.batch_number && String(row.batch_number).trim()) {
                        // Rework order — batch pre-assigned at creation time, qty is the row's own total_qty
                        initialBatches[rowId] = [{ batch_number: row.batch_number, qty: row.open_qty ?? row.total_qty ?? row.quantity, storage_location: row.storage_location || row.component_storage_bin }];
                    }
                });
                
                setConfirmQtys(initialQtys);
                setSelectedBatches(initialBatches);
                setRoutingRows(groupedOperations);
                setActualDates(initialActualDates);
                setActualActivities(initialActualActivities);
                setActualActivityDates(initialActualActivityDates);
                setActualIdle(initialActualIdle);
                setExpandedSections({ material_details: false, process_parameter: false, machine_parameter: false, machine_time: false, inspection_parameter: false, special_remarks: false, documents: false, logs: false });
                setIsConfirmMode(true);
                setIsEditMode(false);
                setShowModal(true);
            })
            .catch(() => alert("Failed loading details"));
    };

    const toggleProductCodeExpansion = (productCode) => {
        setShowClassModal((prevState) => !prevState);
        if (productCode) {
            setModalData((prevState) => ({ ...prevState, class_details: modalData.class_details || [] }));
        }
    };

    const fetchLogs = (productionNo) => {
        if (!productionNo) return;
        axios.get(`${PURCHASE_API}/confirmation/logs/${productionNo}?screen=Confirmation`)
            .then(res => setChangeLogs(res.data))
            .catch(err => console.error("Logs fetch error", err));
    };

    const handleGeneralActualChange = (field, value) => {
        if (field === 'startDate') setGenActualStartDate(value);
        if (field === 'endDate') setGenActualEndDate(value);
        if (field === 'startTime') setGenActualStartTime(value);
        if (field === 'endTime') setGenActualEndTime(value);
        setActualDates(prev => {
            const newDates = { ...prev };
            routingRows.forEach(row => {
                const opId = row.operation_id;
                if (!newDates[opId]) newDates[opId] = {};
                if (field === 'startDate') newDates[opId].actual_start_date = value;
                if (field === 'endDate') newDates[opId].actual_end_date = value;
                if (field === 'startTime') newDates[opId].actual_start_time = value;
                if (field === 'endTime') newDates[opId].actual_end_time = value;
            });
            return newDates;
        });
    };

    const fetchPoFilterMasterData = async () => {
        try {
            const [resProdTypes, resCons, resCode, resCable, resCat, resCore, resCol, resCross, resRef, resVolt] = await Promise.all([
                fetch(`${MASTER_API}/configuration/product-types`), fetch(`${MASTER_API}/configuration/construction`),
                fetch(`${MASTER_API}/configuration/dynamic/code`), fetch(`${MASTER_API}/configuration/cable-type`),
                fetch(`${MASTER_API}/configuration/category`), fetch(`${MASTER_API}/configuration/core`),
                fetch(`${MASTER_API}/configuration/colour`), fetch(`${MASTER_API}/configuration/dynamic/nominal-cross-section`),
                fetch(`${MASTER_API}/configuration/dynamic/reference-of-standard`), fetch(`${MASTER_API}/configuration/dynamic/voltage`),
            ]);

            const prodTypesData = resProdTypes.ok ? await resProdTypes.json() : [];

            setPoFilterMasterData({
                productTypes: prodTypesData.map(item => ({ value: item.name, label: item.name })).filter(o => o.value),
                constructions: resCons.ok ? await resCons.json() : [], codes: resCode.ok ? await resCode.json() : [],
                cableTypes: resCable.ok ? await resCable.json() : [], categories: resCat.ok ? await resCat.json() : [],
                cores: resCore.ok ? await resCore.json() : [], colours: resCol.ok ? await resCol.json() : [],
                nominalCrossSections: resCross.ok ? await resCross.json() : [], referenceOdStandards: resRef.ok ? await resRef.json() : [],
                voltages: resVolt.ok ? await resVolt.json() : [],
            });
        } catch (err) { console.error("Failed fetching filter master data", err); }
    };

    const getPoFilterOptions = (list) => {
        if (!list || !Array.isArray(list)) return [];
        return list.map(item => {
            if (typeof item === 'string') return { value: item, label: item };
            const val = item.name || item.code || item.cable_type || item.category || item.core || item.colour || item.value || "";
            return { value: String(val).trim(), label: String(val).trim() };
        }).filter(opt => opt.value !== "");
    };

    const handleListingFilter = (key, selectedOpt) => {
        setListingFilters(prev => ({ ...prev, [key]: selectedOpt }));
    };

    const derivedInspectionStatus = (() => {
        const vals = Object.values(inspectionChecked).filter(v => v !== "");
        if (vals.length === 0) return "";
        if (vals.some(v => v === "NC")) return "NC";
        if (vals.every(v => v === "Passed")) return "Passed";
        return "";
    })();

    // A non-admin user with no DISPLAY/CHANGE/REVIEW/CREATE/CONVERT entry
    // for this module (either no role assigned at all, or a role that
    // simply doesn't include Confirmation) has canView/canEdit/
    // canConvert all false, per getModulePermissions above — same
    // convention Planned Order and Production Order use. Admins always
    // pass via the explicit user.role === "admin" branch.
    if (!canSeePage) {
        return (
            <Layout>
                <div style={{ padding: 40, textAlign: "center", color: "#64748b", fontSize: 14 }}>
                    You don't have access to Confirmation. Contact your administrator if you believe this is a mistake.
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div style={{ padding: "20px 24px", maxWidth: "100%", boxSizing: "border-box" }}>

                <div style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    flexWrap: "wrap", gap: 12, marginBottom: 20,
                    background: "#fff", borderRadius: 10, padding: "16px 20px",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                }}>
                    <div>
                        <h2 style={{ fontSize: 22, fontWeight: 700, color: "#1e293b", margin: 0 }}>Confirmation</h2>
                        <div style={{ fontSize: 13, color: "#64748b", marginTop: 3 }}>Manage and confirm production orders</div>
                    </div>
                    <button
                        onClick={() => {
                            setManualExists(null);
                            setManualFile(null);
                            setShowManual(true);
                            fetch(USER_MANUAL_LIST_API, { headers: authHeaders() })
                                .then((res) => res.json())
                                .then((data) => {
                                    const norm = (v) => String(v || "").trim().toLowerCase();
                                    const match = (data?.data || []).find((m) =>
                                        norm(m.module_name) === norm(USER_MANUAL_SCOPE.moduleName) &&
                                        norm(m.process_name) === norm(USER_MANUAL_SCOPE.process) &&
                                        norm(m.sub_process_name) === norm(USER_MANUAL_SCOPE.subProcess)
                                    );
                                    if (match && match.drive_link) {
                                        setManualFile(match);
                                        setManualExists(true);
                                    } else {
                                        setManualExists(false);
                                    }
                                })
                                .catch(() => setManualExists(false));
                        }}
                        title="View User Manual"
                        style={{
                            background: "#ffffff",
                            border: "1px solid #e2e8f0",
                            borderRadius: "10px",
                            padding: "10px 14px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            cursor: "pointer",
                            color: "#3b82f6",
                            fontSize: "14px",
                            fontWeight: "600",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#3b82f6"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; }}
                    >
                        <FileText size={18} />
                        User Manual
                    </button>

                </div>

                {/* --- User Manual Popup Modal --- */}
                {showManual && (
                    <div
                        onClick={() => setShowManual(false)}
                        style={{
                            position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
                            backgroundColor: "rgba(15, 23, 42, 0.6)", display: "flex",
                            alignItems: "center", justifyContent: "center", zIndex: 1100,
                        }}
                    >
                        <div
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                background: "#ffffff", borderRadius: "12px", width: "90vw", height: "97vh",
                                display: "flex", flexDirection: "column", overflow: "hidden",
                                boxShadow: "0 20px 25px -5px rgba(0,0,0,0.2)",
                            }}
                        >
                            <div style={{
                                display: "flex", justifyContent: "space-between", alignItems: "center",
                                padding: "14px 20px", borderBottom: "1px solid #e2e8f0",
                            }}>
                                <h4 style={{ margin: 0, color: "#0f172a", fontSize: "16px", fontWeight: "600" }}>
                                    User Manual
                                </h4>
                                <button
                                    onClick={() => setShowManual(false)}
                                    style={{
                                        border: "none", background: "#f1f5f9", borderRadius: "8px",
                                        width: "32px", height: "32px", cursor: "pointer", fontSize: "18px",
                                        color: "#475569", lineHeight: 1,
                                    }}
                                    title="Close"
                                >
                                    ×
                                </button>
                            </div>

                            {manualExists === null && (
                                <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: "14px" }}>
                                    Checking for user manual...
                                </div>
                            )}

                            {manualExists === false && (
                                <div style={{
                                    flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
                                    justifyContent: "center", color: "#64748b", gap: "10px", padding: "20px", textAlign: "center"
                                }}>
                                    <FileText size={40} color="#cbd5e1" />
                                    <div style={{ fontSize: "15px", fontWeight: 600, color: "#334155" }}>
                                        User Manual Not Available
                                    </div>
                                    <div style={{ fontSize: "13px", color: "#94a3b8", maxWidth: "320px" }}>
                                        The user manual for this module hasn't been uploaded yet. Please check back later or contact your administrator.
                                    </div>
                                </div>
                            )}

                            {manualExists === true && manualFile && (() => {
                                const MANUAL_SCALE = 1.25; // 1.0 = normal, 1.25 = 25% bigger, 1.5 = 50% bigger
                                return (
                                    <div style={{ flex: 1, overflow: "hidden", position: "relative" }}>
                                        <iframe
                                            src={manualFile.drive_link.replace("/view", "/preview")}
                                            title={manualFile.file_name || "User Manual"}
                                            allow="autoplay"
                                            style={{
                                                border: "none",
                                                width: `${100 / MANUAL_SCALE}%`,
                                                height: `${100 / MANUAL_SCALE}%`,
                                                transform: `scale(${MANUAL_SCALE})`,
                                                transformOrigin: "top left",
                                            }}
                                        />
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                )}

                <div style={{ background: "#fff", borderRadius: 10, padding: "14px 20px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)", marginBottom: 20 }}>
                    <div style={{ display: "flex", marginBottom: 10 }}>
                        <div style={{ flex: "0 0 25%", minWidth: 200 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Product Type</label>
                            <Select
                                options={poFilterMasterData.productTypes}
                                value={listingFilters.productType}
                                onChange={(opt) => handleListingFilter("productType", opt)}
                                isClearable placeholder="Select Product Type"
                                menuPortalTarget={document.body} menuPosition="fixed"
                                styles={{
                                    control: (base, state) => ({ ...base, minHeight: 32, height: 32, fontSize: 12, borderColor: state.isFocused ? "#64748b" : "#e2e8f0", borderRadius: 6, boxShadow: "none", cursor: "pointer", "&:hover": { borderColor: "#94a3b8" } }),
                                    valueContainer: (base) => ({ ...base, padding: "0 8px", height: 32, flexWrap: "nowrap" }),
                                    input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: 12 }),
                                    indicatorsContainer: (base) => ({ ...base, height: 32 }),
                                    dropdownIndicator: (base) => ({ ...base, padding: "0 4px", color: "#94a3b8", "svg": { width: 12, height: 12 } }),
                                    clearIndicator: (base) => ({ ...base, padding: "0 2px", color: "#94a3b8", "svg": { width: 12, height: 12 } }),
                                    indicatorSeparator: () => ({ display: "none" }),
                                    placeholder: (base) => ({ ...base, color: "#94a3b8", fontSize: 12, whiteSpace: "nowrap" }),
                                    singleValue: (base) => ({ ...base, fontSize: 12, color: "#1e293b" }),
                                    menu: (base) => ({ ...base, zIndex: 9999, fontSize: 12, borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.12)" }),
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                    option: (base, state) => ({ ...base, fontSize: 12, padding: "6px 10px", backgroundColor: state.isSelected ? "#1e293b" : state.isFocused ? "#f1f5f9" : "#fff", color: state.isSelected ? "#fff" : "#334155" }),
                                }}
                            />
                        </div>
                    </div>

                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 10 }}>
                        {[
                            { key: "construction", opts: poFilterMasterData.constructions, label: "Construction" },
                            { key: "code", opts: poFilterMasterData.codes, label: "Code" },
                            { key: "cableType", opts: poFilterMasterData.cableTypes, label: "Cable Type" },
                            { key: "category", opts: poFilterMasterData.categories, label: "Category" },
                            { key: "noOfCores", opts: poFilterMasterData.cores, label: "Cores" },
                            { key: "color", opts: poFilterMasterData.colours, label: "Color" },
                            { key: "nominalCrossSection", opts: poFilterMasterData.nominalCrossSections, label: "Cross Section" },
                            { key: "referenceOfStandard", opts: poFilterMasterData.referenceOdStandards, label: "Std. Ref" },
                            { key: "voltage", opts: poFilterMasterData.voltages, label: "Voltage" },
                        ].map(({ key, opts, label }) => (
                            <div key={key} style={{ flex: "1 1 0", minWidth: 100 }}>
                                <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>{label}</label>
                                <Select
                                    placeholder={label} isClearable
                                    options={getPoFilterOptions(opts)}
                                    value={listingFilters[key]}
                                    onChange={opt => handleListingFilter(key, opt)}
                                    menuPortalTarget={document.body} menuPosition="fixed"
                                    styles={{
                                        control: (base, state) => ({ ...base, minHeight: 32, height: 32, fontSize: 12, borderColor: state.isFocused ? "#64748b" : "#e2e8f0", borderRadius: 6, boxShadow: "none", cursor: "pointer", "&:hover": { borderColor: "#94a3b8" } }),
                                        valueContainer: (base) => ({ ...base, padding: "0 8px", height: 32, flexWrap: "nowrap" }),
                                        input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: 12 }),
                                        indicatorsContainer: (base) => ({ ...base, height: 32 }),
                                        dropdownIndicator: (base) => ({ ...base, padding: "0 4px", color: "#94a3b8", "svg": { width: 12, height: 12 } }),
                                        clearIndicator: (base) => ({ ...base, padding: "0 2px", color: "#94a3b8", "svg": { width: 12, height: 12 } }),
                                        indicatorSeparator: () => ({ display: "none" }),
                                        placeholder: (base) => ({ ...base, color: "#94a3b8", fontSize: 12, whiteSpace: "nowrap" }),
                                        singleValue: (base) => ({ ...base, fontSize: 12, color: "#1e293b" }),
                                        menu: (base) => ({ ...base, zIndex: 9999, fontSize: 12, borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.12)" }),
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        option: (base, state) => ({ ...base, fontSize: 12, padding: "6px 10px", backgroundColor: state.isSelected ? "#1e293b" : state.isFocused ? "#f1f5f9" : "#fff", color: state.isSelected ? "#fff" : "#334155" }),
                                    }}
                                />
                            </div>
                        ))}
                    </div>

                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
                        <div style={{ flex: "1 1 0", minWidth: 150 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Workcenter</label>
                            <Select
                                options={workcenterNames.map((w) => {
                                    const val = typeof w === "string" ? w : (w.workcenter_name || w.name || "");
                                    return { value: val, label: val };
                                })}
                                value={selectedWorkcenter ? { value: selectedWorkcenter, label: selectedWorkcenter } : null}
                                onChange={(opt) => setSelectedWorkcenter(opt ? opt.value : "")}
                                isClearable placeholder="Workcenter"
                                menuPortalTarget={document.body} menuPosition="fixed"
                                styles={{
                                    control: (base, state) => ({ ...base, minHeight: 32, height: 32, fontSize: 12, borderColor: state.isFocused ? "#64748b" : "#e2e8f0", borderRadius: 6, boxShadow: "none", cursor: "pointer", "&:hover": { borderColor: "#94a3b8" } }),
                                    valueContainer: (base) => ({ ...base, padding: "0 8px", height: 32 }),
                                    input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: 12 }),
                                    indicatorsContainer: (base) => ({ ...base, height: 32 }),
                                    dropdownIndicator: (base) => ({ ...base, padding: "0 4px", color: "#94a3b8", "svg": { width: 12, height: 12 } }),
                                    clearIndicator: (base) => ({ ...base, padding: "0 2px", color: "#94a3b8", "svg": { width: 12, height: 12 } }),
                                    indicatorSeparator: () => ({ display: "none" }),
                                    placeholder: (base) => ({ ...base, color: "#94a3b8", fontSize: 12 }),
                                    singleValue: (base) => ({ ...base, fontSize: 12, color: "#1e293b" }),
                                    menu: (base) => ({ ...base, zIndex: 9999, fontSize: 12, borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.12)" }),
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                    option: (base, state) => ({ ...base, fontSize: 12, padding: "6px 10px", backgroundColor: state.isSelected ? "#1e293b" : state.isFocused ? "#f1f5f9" : "#fff", color: state.isSelected ? "#fff" : "#334155" }),
                                }}
                            />
                        </div>
                        <div style={{ flex: "1 1 0", minWidth: 150 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Sales Order No.</label>
                            <Select
                                options={salesOrderNumbersFilter.map((s) => ({ value: s.sales_order_no, label: s.sales_order_no }))}
                                value={selectedSalesOrderNoFilter ? { value: selectedSalesOrderNoFilter, label: selectedSalesOrderNoFilter } : null}
                                onChange={(opt) => setSelectedSalesOrderNoFilter(opt ? opt.value : "")}
                                isClearable isSearchable placeholder="Sales Order No."
                                menuPortalTarget={document.body} menuPosition="fixed"
                                styles={{
                                    control: (base, state) => ({ ...base, minHeight: 32, height: 32, fontSize: 12, borderColor: state.isFocused ? "#64748b" : "#e2e8f0", borderRadius: 6, boxShadow: "none", cursor: "pointer", "&:hover": { borderColor: "#94a3b8" } }),
                                    valueContainer: (base) => ({ ...base, padding: "0 8px", height: 32 }),
                                    input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: 12 }),
                                    indicatorsContainer: (base) => ({ ...base, height: 32 }),
                                    dropdownIndicator: (base) => ({ ...base, padding: "0 4px", color: "#94a3b8", "svg": { width: 12, height: 12 } }),
                                    clearIndicator: (base) => ({ ...base, padding: "0 2px", color: "#94a3b8", "svg": { width: 12, height: 12 } }),
                                    indicatorSeparator: () => ({ display: "none" }),
                                    placeholder: (base) => ({ ...base, color: "#94a3b8", fontSize: 12 }),
                                    singleValue: (base) => ({ ...base, fontSize: 12, color: "#1e293b" }),
                                    menu: (base) => ({ ...base, zIndex: 9999, fontSize: 12, borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.12)" }),
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                    option: (base, state) => ({ ...base, fontSize: 12, padding: "6px 10px", backgroundColor: state.isSelected ? "#1e293b" : state.isFocused ? "#f1f5f9" : "#fff", color: state.isSelected ? "#fff" : "#334155" }),
                                }}
                            />
                        </div>
                        <div style={{ flex: "1 1 0", minWidth: 150 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Production Order</label>
                            <Select
                                options={productionOrderNumbers.map((n) => {
                                    const val = typeof n === "string" ? n : (n.production_order_number || n);
                                    return { value: val, label: val };
                                })}
                                value={selectedProductionNo ? { value: selectedProductionNo, label: selectedProductionNo } : null}
                                onChange={(opt) => setSelectedProductionNo(opt ? opt.value : "")}
                                isClearable isSearchable placeholder="Production Order"
                                menuPortalTarget={document.body} menuPosition="fixed"
                                styles={{
                                    control: (base, state) => ({ ...base, minHeight: 32, height: 32, fontSize: 12, borderColor: state.isFocused ? "#64748b" : "#e2e8f0", borderRadius: 6, boxShadow: "none", cursor: "pointer", "&:hover": { borderColor: "#94a3b8" } }),
                                    valueContainer: (base) => ({ ...base, padding: "0 8px", height: 32 }),
                                    input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: 12 }),
                                    indicatorsContainer: (base) => ({ ...base, height: 32 }),
                                    dropdownIndicator: (base) => ({ ...base, padding: "0 4px", color: "#94a3b8", "svg": { width: 12, height: 12 } }),
                                    clearIndicator: (base) => ({ ...base, padding: "0 2px", color: "#94a3b8", "svg": { width: 12, height: 12 } }),
                                    indicatorSeparator: () => ({ display: "none" }),
                                    placeholder: (base) => ({ ...base, color: "#94a3b8", fontSize: 12 }),
                                    singleValue: (base) => ({ ...base, fontSize: 12, color: "#1e293b" }),
                                    menu: (base) => ({ ...base, zIndex: 9999, fontSize: 12, borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.12)" }),
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                    option: (base, state) => ({ ...base, fontSize: 12, padding: "6px 10px", backgroundColor: state.isSelected ? "#1e293b" : state.isFocused ? "#f1f5f9" : "#fff", color: state.isSelected ? "#fff" : "#334155" }),
                                }}
                            />
                        </div>
                        <div style={{ flex: "1 1 0", minWidth: 150 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>From</label>
                            <DmyDateInput
                                value={selectedProdStartDate}
                                onChange={e => setSelectedProdStartDate(e.target.value)}
                                style={{ width: "100%", height: 32, fontSize: 12, padding: "0 8px", border: "1px solid #e2e8f0", borderRadius: 6, color: selectedProdStartDate ? "#1e293b" : "#94a3b8", boxSizing: "border-box", outline: "none", cursor: "pointer" }}
                            />
                        </div>
                        <div style={{ flex: "1 1 0", minWidth: 150 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>To</label>
                            <DmyDateInput
                                value={selectedProdEndDate}
                                onChange={e => setSelectedProdEndDate(e.target.value)}
                                style={{ width: "100%", height: 32, fontSize: 12, padding: "0 8px", border: "1px solid #e2e8f0", borderRadius: 6, color: selectedProdEndDate ? "#1e293b" : "#94a3b8", boxSizing: "border-box", outline: "none", cursor: "pointer" }}
                            />
                        </div>
                        <div style={{ flex: "1 1 0", minWidth: 150 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Status</label>
                            <Select
                                isMulti
                                options={statusOptions}
                                value={selectedStatuses}
                                onChange={(selected) => setSelectedStatuses(selected || [])}
                                placeholder="Select Statuses..."
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={{
                                    control: (base, state) => ({ ...base, minHeight: 32, fontSize: 12, borderColor: state.isFocused ? "#64748b" : "#e2e8f0", borderRadius: 6, boxShadow: "none", cursor: "pointer", "&:hover": { borderColor: "#94a3b8" } }),
                                    valueContainer: (base) => ({ ...base, padding: "0 8px", minHeight: 32 }),
                                    input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: 12 }),
                                    dropdownIndicator: (base) => ({ ...base, padding: "0 4px", color: "#94a3b8", "svg": { width: 12, height: 12 } }),
                                    clearIndicator: (base) => ({ ...base, padding: "0 2px", color: "#94a3b8", "svg": { width: 12, height: 12 } }),
                                    multiValue: (base) => ({ ...base, backgroundColor: '#e2e8f0', borderRadius: 4 }),
                                    multiValueLabel: (base) => ({ ...base, fontSize: '11px', padding: '2px 4px', color: '#1e293b' }),
                                    multiValueRemove: (base) => ({ ...base, padding: '0 2px', ':hover': { backgroundColor: '#cbd5e1', color: '#0f172a' } }),
                                    menu: (base) => ({ ...base, zIndex: 9999, fontSize: 12, borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.12)" }),
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                    option: (base, state) => ({ ...base, fontSize: 12, padding: "6px 10px", backgroundColor: state.isSelected ? "#1e293b" : state.isFocused ? "#f1f5f9" : "#fff", color: state.isSelected ? "#fff" : "#334155" }),
                                }}
                            />
                        </div>

                    </div>
                </div>

                {showModal && (
                    <div style={styles.modalBackdrop}>
                        <style>{`.hide-scrollbar::-webkit-scrollbar { display: none; }`}</style>

                        <div style={{ ...styles.modal, width: "90%", maxWidth: "1600px", height: "90vh", maxHeight: "90vh", padding: 0, fontSize: "13px" }}>

                            <div style={{ ...styles.modalHeader, padding: "10px 20px", display: "flex", alignItems: "center", gap: 12 }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 700, fontSize: "1.05rem", color: "#1e293b", whiteSpace: "nowrap" }}>
                                        {isConfirmMode ? "Confirmation" : "Confirmation"} : {modalData.production_order_number || ""}
                                    </div>
                                </div>
                                {modalData.confirmationId && (
                                    <div style={{ fontWeight: 700, fontSize: "1.05rem", color: "#1e293b", whiteSpace: "nowrap" }}>
                                        Confirmation ID : {modalData.confirmationId}
                                    </div>
                                )}
                            </div>

                            <div className="hide-scrollbar" style={{ ...styles.scrollableContent, padding: "16px 20px" }}>

                                <div style={{ marginBottom: 16 }}>

                                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                                        <label style={{ ...mLabel, marginBottom: 0, whiteSpace: "nowrap", minWidth: 100 }}>Product Type</label>
                                        <div style={{ width: 220, flexShrink: 0 }}>
                                            <input style={{ ...mInput, background: "#f1f5f9" }} value={modalData.productType || ""} readOnly />
                                        </div>
                                    </div>

                                    <div style={{ marginBottom: 10, border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
                                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                            <thead>
                                                <tr>
                                                    <th style={{ ...soThStyle }}>Sales Order</th>
                                                    <th style={{ ...soThStyle }}>Item</th>
                                                    <th style={{ ...soThStyle }}>Product Code</th>
                                                    <th style={{ ...soThStyle }}>Description</th>
                                                    <th style={{ ...soThStyle }}>Req. Date</th>
                                                    <th style={{ ...soThStyle, textAlign: "right" }}>Qty</th>
                                                    <th style={{ ...soThStyle }}>Range</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(viewSalesOrderRows.length > 0 ? viewSalesOrderRows : [{
                                                    id: null,
                                                    sales_order_id: modalData.salesOrderId,
                                                    so_item_no: modalData.soItemId,
                                                    customer_name: modalData.customerName,
                                                    customer_po_ref: modalData.customerPoRef,
                                                    product_code: modalData.productCode || modalData.product_code,
                                                    product_description: modalData.productDescription || modalData.product_description,
                                                    requirement_date: modalData.requirement_date,
                                                    so_qty: modalData.soQty,
                                                    so_range: modalData.range,
                                                    characteristics: modalData.class_details || [],
                                                }]).map((row, idx) => (
                                                    <tr key={row.id || idx} style={{ background: idx % 2 ? "#fafbfc" : "#fff" }}>
                                                        <td style={{ ...soTdStyle }}>{row.sales_order_id || ""}</td>
                                                        <td style={{ ...soTdStyle }}>{row.so_item_no || ""}</td>
                                                        <td style={{ ...soTdStyle }}>{row.product_code || ""}</td>
                                                        <td style={{ ...soTdStyle, wordBreak: "break-word" }}>{row.product_description || ""}</td>
                                                        <td style={{ ...soTdStyle }}>{formatDMY(row.requirement_date)}</td>
                                                        <td style={{ ...soTdStyle, textAlign: "right" }}>
                                                            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 6 }}>
                                                                <span>{row.so_qty || ""}</span>
                                                                <span
                                                                    onClick={() => { setActiveCharRow(row); setShowClassModal(true); }}
                                                                    style={{ cursor: "pointer", color: "#151616", fontSize: 14, userSelect: "none", flexShrink: 0 }}
                                                                >▶</span>
                                                            </div>
                                                        </td>
                                                        <td style={{ ...soTdStyle }}>{row.so_range || row.range || ""}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 12px", marginBottom: 10 }}>
                                        <div style={{ display: "flex", alignItems: "flex-end", gap: 8, flexWrap: "wrap" }}>
                                            {[
                                                { key: "construction", opts: poFilterMasterData.constructions, label: "Construction" },
                                                { key: "code", opts: poFilterMasterData.codes, label: "Code" },
                                                { key: "cableType", opts: poFilterMasterData.cableTypes, label: "Cable Type" },
                                                { key: "category", opts: poFilterMasterData.categories, label: "Category" },
                                                { key: "noOfCores", opts: poFilterMasterData.cores, label: "Cores" },
                                                { key: "color", opts: poFilterMasterData.colours, label: "Color" },
                                                { key: "nominalCrossSection", opts: poFilterMasterData.nominalCrossSections, label: "Cross Section" },
                                                { key: "referenceOfStandard", opts: poFilterMasterData.referenceOdStandards, label: "Std. Ref" },
                                                { key: "voltage", opts: poFilterMasterData.voltages, label: "Voltage" },
                                            ].map(({ key, label }) => (
                                                <div key={key} style={{ minWidth: 110, flex: "1 1 100px", maxWidth: 155 }}>
                                                    <label style={{ fontSize: 10, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 3, whiteSpace: "nowrap" }}>{label}</label>
                                                    <input
                                                        style={{ ...mInput, background: "#f1f5f9", fontSize: 11, height: 28 }}
                                                        value={modalData[key] || ""}
                                                        readOnly
                                                        placeholder={label}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div> */}

                                    <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "10px 10px", marginBottom: 10 }}>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={mLabel}>Product Code</label>
                                            {!isConfirmMode && !isEditMode && !isFieldVisible('product_code') ? <MaskedValue /> : (
                                            <input style={{ ...mInput, background: "#f1f5f9" }} value={modalData.header_product_code || ""} readOnly />
                                            )}
                                        </div>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={mLabel}>Description</label>
                                            {!isConfirmMode && !isEditMode && !isFieldVisible('description') ? <MaskedValue /> : (
                                            <input style={{ ...mInput, background: "#f1f5f9" }} value={modalData.productDescription || ""} readOnly placeholder="Auto-filled" />
                                            )}
                                        </div>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={mLabel}>UOM</label>
                                            {!isConfirmMode && !isEditMode && !isFieldVisible('uom') ? <MaskedValue /> : (
                                            <input style={{ ...mInput, background: "#f1f5f9" }} value={modalData.header_uom || ""} readOnly placeholder="Auto-filled" />
                                            )}
                                        </div>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={mLabel}>Order Qty</label>
                                            {!isConfirmMode && !isEditMode && !isFieldVisible('order_qty') ? <MaskedValue /> : (
                                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                <input style={{ ...mInput, flex: 1, background: "#f1f5f9" }} value={modalData.quantity || ""} readOnly />
                                                {(modalData.class_details || []).length > 0 && (
                                                    <span onClick={() => toggleProductCodeExpansion(modalData.header_product_code)} title="View Order Qty Characteristics" style={{ cursor: "pointer", color: "#151616", fontSize: 14, userSelect: "none", flexShrink: 0 }}>▶</span>
                                                )}
                                            </div>
                                            )}
                                        </div>

                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={mLabel}>Range</label>
                                            {!isConfirmMode && !isEditMode && !isFieldVisible('range') ? <MaskedValue /> : (
                                            <input style={{ ...mInput, background: "#f1f5f9" }} value={modalData.range || ""} readOnly placeholder="Auto-filled" />
                                            )}
                                        </div>

                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={mLabel}>Produced</label>
                                            {!isConfirmMode && !isEditMode && !isFieldVisible('produced') ? <MaskedValue /> : (
                                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                <input
                                                    style={{ ...mInput, flex: 1, background: "#f1f5f9" }}
                                                    type="number"
                                                    value={outputProduced || deliveredActual || (modalData.total_produced_qty || "0")}
                                                    readOnly={true}
                                                />
                                                {outputBatchCharacteristics.length > 0 && (
                                                    <span onClick={() => setShowProducedCharModal(true)} title="View Produced Characteristics" style={{ cursor: "pointer", color: "#151616", fontSize: 14, userSelect: "none", flexShrink: 0 }}>▶</span>
                                                )}
                                            </div>
                                            )}
                                        </div>

                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={mLabel}>Pending</label>
                                            {!isConfirmMode && !isEditMode && !isFieldVisible('pending') ? <MaskedValue /> : (
                                            <input
                                                style={{ ...mInput, background: "#f1f5f9" }}
                                                value={Math.max(0, (parseFloat(modalData.quantity) || 0) - (parseFloat(modalData.total_produced_qty) || 0) - (parseFloat(outputProduced || deliveredActual) || 0)).toFixed(2)}
                                                readOnly
                                            />
                                            )}
                                        </div>
                                    </div>

                                    {/* ── ROW 2: Upper Tol. (%) | Under Tol. (%) | Tolerance Range | Qty in M | Qty in Kg ──
                                        Snapshotted from po_header at the moment this confirmation was created.
                                        Entered (and editable) on the Production Order screen, not here. Uses
                                        the same 7-column grid as the row above so each field keeps the same
                                        width (only 5 of the 7 slots are used). */}
                                    <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "10px 10px", marginBottom: 10 }}>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={mLabel}>Upper Tol. (%)</label>
                                            {!isConfirmMode && !isEditMode && !isFieldVisible('upper_tolerance_percent') ? <MaskedValue /> : (
                                            <input
                                                style={{ ...mInput, background: "#f1f5f9" }}
                                                value={modalData.upper_tolerance_percent !== undefined && modalData.upper_tolerance_percent !== null && modalData.upper_tolerance_percent !== "" ? `${modalData.upper_tolerance_percent}` : ""}
                                                readOnly
                                                placeholder="—"
                                            />
                                            )}
                                        </div>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={mLabel}>Under Tol. (%)</label>
                                            {!isConfirmMode && !isEditMode && !isFieldVisible('under_tolerance_percent') ? <MaskedValue /> : (
                                            <input
                                                style={{ ...mInput, background: "#f1f5f9" }}
                                                value={modalData.under_tolerance_percent !== undefined && modalData.under_tolerance_percent !== null && modalData.under_tolerance_percent !== "" ? `${modalData.under_tolerance_percent}` : ""}
                                                readOnly
                                                placeholder="—"
                                            />
                                            )}
                                        </div>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={mLabel}>Tolerance Range</label>
                                            {!isConfirmMode && !isEditMode && !isFieldVisible('tolerance_range') ? <MaskedValue /> : (
                                            <input style={{ ...mInput, background: "#f1f5f9" }} value={modalData.tolerance_range || ""} readOnly placeholder="—" />
                                            )}
                                        </div>

                                        {/* Conversion Factor — Qty in M / Qty in Kg. Digizura requirement:
                                            every conductor-stage order qty shown in BOTH M and Kg, snapshotted
                                            from po_header (derived from productdetails.conversionLengthM /
                                            conversionWeightKg for this product code) at confirm time. */}
                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={mLabel}>Qty in M</label>
                                            {!isConfirmMode && !isEditMode && !isFieldVisible('qty_in_m') ? <MaskedValue /> : (
                                            <input style={{ ...mInput, background: "#f1f5f9" }} value={modalData.qty_in_m ?? ""} readOnly placeholder="—" />
                                            )}
                                        </div>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={mLabel}>Qty in Kg</label>
                                            {!isConfirmMode && !isEditMode && !isFieldVisible('qty_in_kg') ? <MaskedValue /> : (
                                            <input style={{ ...mInput, background: "#f1f5f9" }} value={modalData.qty_in_kg ?? ""} readOnly placeholder="—" />
                                            )}
                                        </div>
                                    </div>

                                    <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", marginBottom: 8, padding: "6px 10px", background: "#f8fafc", borderRadius: 4, borderLeft: "3px solid #cbd5e1" }}>
                                        Planned
                                    </div>
                                    <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: "10px 14px", marginBottom: 16 }}>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={mLabel}>Prod. Start Date</label>
                                            {!isConfirmMode && !isEditMode && !isFieldVisible('prod_start_date') ? <MaskedValue /> : (
                                            <DmyDateInput style={{ ...mInput, background: "#f1f5f9" }} value={modalData.productionStartDate || ""} readOnly />
                                            )}
                                        </div>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={mLabel}>Prod. Start Time</label>
                                            {!isConfirmMode && !isEditMode && !isFieldVisible('prod_start_time') ? <MaskedValue /> : (
                                            <input type="time" style={{ ...mInput, background: "#f1f5f9" }} value={startTime || ""} readOnly />
                                            )}
                                        </div>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={mLabel}>Prod. End Date</label>
                                            {!isConfirmMode && !isEditMode && !isFieldVisible('prod_end_date') ? <MaskedValue /> : (
                                            <DmyDateInput style={{ ...mInput, background: "#f1f5f9" }} value={modalData.productionEndDate || ""} readOnly />
                                            )}
                                        </div>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={mLabel}>Prod. End Time</label>
                                            {!isConfirmMode && !isEditMode && !isFieldVisible('prod_end_time') ? <MaskedValue /> : (
                                            <input type="time" style={{ ...mInput, background: "#f1f5f9" }} value={endTime || ""} readOnly />
                                            )}
                                        </div>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={mLabel}>Created By</label>
                                            {!isConfirmMode && !isEditMode && !isFieldVisible('created_by') ? <MaskedValue /> : (
                                            <input style={{ ...mInput, background: "#f1f5f9" }} value={modalData.createdBy || ""} readOnly />
                                            )}
                                        </div>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={mLabel}>Released By</label>
                                            {!isConfirmMode && !isEditMode && !isFieldVisible('released_by') ? <MaskedValue /> : (
                                            <input style={{ ...mInput, background: "#f1f5f9" }} value={modalData.releasedBy || ""} readOnly />
                                            )}
                                        </div>
                                    </div>

                                    <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", marginBottom: 8, padding: "6px 10px", background: "#f8fafc", borderRadius: 4, borderLeft: "3px solid #22c55e" }}>
                                        Actual
                                    </div>

                                    <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: "10px 14px", marginBottom: 10 }}>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={mLabel}>Actual Start Date</label>
                                            {!isConfirmMode && !isEditMode && !isFieldVisible('actual_start_date') ? <MaskedValue /> : (
                                            <DmyDateInput
                                                style={{ ...mInput, background: "#f1f5f9" }}
                                                value={genActualStartDate}
                                                readOnly={true} 
                                            />
                                            )}
                                        </div>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={mLabel}>Actual Start Time</label>
                                            {!isConfirmMode && !isEditMode && !isFieldVisible('actual_start_time') ? <MaskedValue /> : (
                                            <input
                                                type="time"
                                                style={{ ...mInput, background: "#f1f5f9" }}
                                                value={genActualStartTime}
                                                readOnly={true} 
                                            />
                                            )}
                                        </div>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={mLabel}>Actual End Date</label>
                                            {!isConfirmMode && !isEditMode && !isFieldVisible('actual_end_date') ? <MaskedValue /> : (
                                            <DmyDateInput
                                                style={{ ...mInput, background: "#f1f5f9" }}
                                                value={genActualEndDate}
                                                readOnly={true} 
                                            />
                                            )}
                                        </div>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={mLabel}>Actual End Time</label>
                                            {!isConfirmMode && !isEditMode && !isFieldVisible('actual_end_time') ? <MaskedValue /> : (
                                            <input
                                                type="time"
                                                style={{ ...mInput, background: "#f1f5f9" }}
                                                value={genActualEndTime}
                                                readOnly={true} 
                                            />
                                            )}
                                        </div>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={mLabel}>Operator Name {(isConfirmMode || isEditMode) && <span style={{ color: "#dc2626" }}>*</span>}</label>
                                            {!isConfirmMode && !isEditMode && !isFieldVisible('operator_name') ? <MaskedValue /> : (
                                            <input
                                                style={{
                                                    ...mInput,
                                                    background: (isConfirmMode || isEditMode) ? "#fff" : "#f1f5f9",
                                                    ...(confirmAttemptedSave && (isConfirmMode || isEditMode) && !operatorName.trim()
                                                        ? { borderColor: "#dc2626", background: "#fef2f2" }
                                                        : {}),
                                                }}
                                                value={operatorName}
                                                onChange={e => setOperatorName(e.target.value)}
                                                readOnly={!(isConfirmMode || isEditMode)}
                                                placeholder="Enter operator name"
                                            />
                                            )}
                                            {confirmAttemptedSave && (isConfirmMode || isEditMode) && !operatorName.trim() && (
                                                <span style={{ color: "#dc2626", fontSize: 11 }}>Required</span>
                                            )}
                                        </div>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={mLabel}>QC Checked By</label>
                                            {!isConfirmMode && !isEditMode && !isFieldVisible('qc_checked_by') ? <MaskedValue /> : (
                                            <input
                                                style={{ ...mInput, background: ((isConfirmMode && modalData.release_status === "In Quality") || (!isConfirmMode && isEditMode)) ? "#fff" : "#f1f5f9" }}
                                                value={checkedBy}
                                                onChange={e => setCheckedBy(e.target.value)}
                                                readOnly={!((isConfirmMode && modalData.release_status === "In Quality") || (!isConfirmMode && isEditMode))}
                                                placeholder={(isConfirmMode && modalData.release_status === "In Quality") ? "Enter inspector name" : "Filled during inspection"}
                                            />
                                            )}
                                        </div>
                                    </div>

                                </div>

                                <CharacteristicsModal
                                    modalVisible={showClassModal}
                                    selectedCharacteristics={(activeCharRow ? activeCharRow.characteristics : modalData.class_details) || []}
                                    handleChange={() => { }}
                                    handleClose={() => { setShowClassModal(false); setActiveCharRow(null); }}
                                    isEditing={false}
                                />

                                {[
                                    { key: "machine_time", label: "Machine Time" },
                                    { key: "material_details", label: "Material Details" },
                                    { key: "process_parameter", label: "Process Parameter" },
                                    { key: "machine_parameter", label: "Machine Parameter" },
                                    { key: "inspection_parameter", label: "Inspection Parameter" },
                                    { key: "special_remarks", label: "Special Remarks" },
                                    { key: "documents", label: "Docs" },
                                    { key: "logs", label: "Change Log" },
                                ].map(({ key, label }) => (
                                    <div key={key} style={{ marginBottom: 6, border: "1px solid #e2e8f0", borderRadius: 6, overflow: "hidden" }}>
                                        <div
                                            onClick={() => toggleSection(key)}
                                            style={{
                                                display: "flex", justifyContent: "space-between", alignItems: "center",
                                                padding: "8px 14px", cursor: "pointer",
                                                background: expandedSections[key] ? "#f1f5f9" : "#f8fafc",
                                                borderBottom: expandedSections[key] ? "1px solid #e2e8f0" : "none",
                                                userSelect: "none",
                                            }}
                                        >
                                            <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{label}</span>
                                            <span style={{ fontSize: 12, color: "#64748b", transition: "transform 0.2s", display: "inline-block", transform: expandedSections[key] ? "rotate(180deg)" : "rotate(0deg)" }}>▼</span>
                                        </div>

                                        {expandedSections[key] && (
                                            <div style={{ padding: "12px 14px" }}>

                                                {/* SPECIAL REMARKS — always read-only here */}
                                                {key === "special_remarks" && (
                                                    <textarea
                                                        rows={4}
                                                        readOnly
                                                        value={modalData.specialRemarks || ""}
                                                        placeholder="No special remarks"
                                                        style={{
                                                            width: "100%", fontSize: 12, padding: "8px 10px",
                                                            border: "1px solid #dee2e6", borderRadius: 4,
                                                            background: "#f1f5f9", color: "#1e293b",
                                                            boxSizing: "border-box", resize: "vertical",
                                                            outline: "none", fontFamily: "inherit",
                                                        }}
                                                    />
                                                )}

                                                {/* MATERIAL DETAILS */}
                                                {key === "material_details" && (
                                                    <div>
                                                        <div style={{ marginBottom: 16 }}>
                                                            <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", marginBottom: 8, padding: "6px 10px", background: "#f1f5f9", borderRadius: 4, borderLeft: "3px solid #3b82f6" }}>Input</div>
                                                            <ComponentsTable
                                                                resetKey={modalData.production_order_number}
                                                                rows={bomRows.map(r => {
                                                                    const key2 = `${r.item}-${r.product_code}`;
                                                                    const rowId = r.id || key2;
                                                                    return {
                                                                        ...r,
                                                                        storage_location: componentStorageLocations[key2] || r.storage_location || "",
                                                                        _selectedBatches: selectedBatches[rowId] || [],
                                                                    };
                                                                })}
                                                                baseUnit={modalData.base_unit}
                                                                actualQty={actualQty}
                                                                componentAttributes={componentAttributes}
                                                                storageBins={storageLocations.length > 0 ? storageLocations : storageBins}
                                                                isViewMode={!isConfirmMode && !isEditMode}
                                                                onStorageLocationChange={(productCode, item, val) => {
                                                                    const key2 = `${item}-${productCode}`;
                                                                    setComponentStorageLocations(prev => ({ ...prev, [key2]: val }));
                                                                    setBomRows(prev => prev.map(r =>
                                                                        r.product_code === productCode && r.item === item ? { ...r, storage_location: val } : r
                                                                    ));
                                                                }}
                                                                onConsumeChange={(productCode, val) => {
                                                                    setBomRows(prev => prev.map(r => r.product_code === productCode ? { ...r, consumed: val } : r));
                                                                }}
                                                                scrapHeadings={scrapHeadings.map(h => h.scrap_name)}
                                                                onScrapChange={(productCode, field, val) => {
                                                                    setBomRows(prev => prev.map(r => {
                                                                        if (r.product_code !== productCode) return r;
                                                                        return scrapHeadings.length > 0
                                                                            ? { ...r, scraps: { ...(r.scraps || {}), [field]: val } }
                                                                            : { ...r, [field]: val };
                                                                    }));
                                                                }}
                                                                onBatchChange={async (productCode, selectedArr) => {
                                                                    const row = bomRows.find(r => r.product_code === productCode);
                                                                    if (!row) return;
                                                                    const rowId = row.id || `${row.item}-${productCode}`;
                                                                    setSelectedBatches(prev => ({ ...prev, [rowId]: selectedArr }));

                                                                    if (selectedArr.length > 0 && selectedArr[0].storage_location) {
                                                                        const key2 = `${row.item}-${productCode}`;
                                                                        setComponentStorageLocations(prev => ({ ...prev, [key2]: selectedArr[0].storage_location }));
                                                                        setBomRows(prev => prev.map(r =>
                                                                            r.product_code === productCode && String(r.item) === String(row.item)
                                                                                ? { ...r, storage_location: selectedArr[0].storage_location, component_batch: selectedArr.map(b => b.batch_number).join(",") }
                                                                                : r
                                                                        ));
                                                                    }
                                                                    if (selectedArr.length > 0) {
                                                                        try {
                                                                            const firstBatch = selectedArr[0].batch_number;
                                                                            const res = await axios.get(
                                                                                `${PURCHASE_API}/confirmation/component-attributes-by-batch`,
                                                                                { params: { batchNumber: firstBatch, productCode } }
                                                                            );
                                                                            if (res.data.success && res.data.data && res.data.data.length > 0) {
                                                                                setComponentAttributes(prev => {
                                                                                    const existing = [...(prev[productCode] || [])];
                                                                                    const fetched = res.data.data; 
                                                                                    const merged = existing.map(attr => {
                                                                                        const match = fetched.find(f => f.characteristic === attr.characteristic);
                                                                                        if (match && match.actual_value !== null && match.actual_value !== undefined && match.actual_value !== "") {
                                                                                            return { ...attr, actual_value: match.actual_value };
                                                                                        }
                                                                                        return attr;
                                                                                    });
                                                                                    fetched.forEach(f => {
                                                                                        if (!merged.find(m => m.characteristic === f.characteristic)) {
                                                                                            merged.push({ characteristic: f.characteristic, value: f.value, actual_value: f.actual_value || "" });
                                                                                        }
                                                                                    });
                                                                                    return { ...prev, [productCode]: merged };
                                                                                });
                                                                            }
                                                                        } catch (err) {
                                                                            console.error("Failed to auto-fill component actuals:", err);
                                                                        }
                                                                    }
                                                                }}
                                                                onComponentAttributeChange={(productCode, characteristic, value) => {
                                                                    setComponentAttributes(prev => {
                                                                        const existing = [...(prev[productCode] || [])];
                                                                        const idx = existing.findIndex(a => a.characteristic === characteristic);
                                                                        if (idx >= 0) existing[idx] = { ...existing[idx], actual_value: value };
                                                                        return { ...prev, [productCode]: existing };
                                                                    });
                                                                }}
                                                                batchData={dashboardData.reduce((acc, d) => {
                                                                    if (!acc[d.product_code]) acc[d.product_code] = [];
                                                                    acc[d.product_code].push(d);
                                                                    return acc;
                                                                }, {})}
                                                            />
                                                        </div>

                                                        {/* OUTPUT SECTION */}
                                                        <div>
                                                            <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", marginBottom: 8, padding: "6px 10px", background: "#f0fdf4", borderRadius: 4, borderLeft: "3px solid #22c55e" }}>Output</div>
                                                            <div style={{ overflowX: "auto", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff" }}>
                                                                <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", minWidth: 1150 }}>
                                                                    <colgroup>
                                                                        <col style={{ width: 45 }} />    {/* Item */}
                                                                        <col style={{ width: 100 }} />   {/* Product Type */}
                                                                        <col style={{ width: 130 }} />   {/* Product Code */}
                                                                        <col style={{ width: "20%" }} /> {/* Description */}
                                                                        <col style={{ width: 55 }} />    {/* UOM */}
                                                                        <col style={{ width: 80 }} />    {/* Total Qty */}
                                                                        <col style={{ width: 160 }} />   {/* Produced */}
                                                                        <col style={{ width: 90 }} />  {/* Setup Scrap */}
                                                                        <col style={{ width: 90 }} />  {/* Process Scrap */}
                                                                        <col style={{ width: 100 }} /> {/* Wirebreak Scrap */}
                                                                        <col style={{ width: 150 }} />   {/* Storage Location */}
                                                                        <col style={{ width: 130 }} />   {/* Batch Number */}
                                                                        <col style={{ width: 80 }} />    {/* Status */}
                                                                    </colgroup>
                                                                    <thead>
                                                                        <tr>
                                                                            {[
                                                                                { h: "Item", align: "center" },
                                                                                { h: "Product Type" },
                                                                                { h: "Product Code" },
                                                                                { h: "Description" },
                                                                                { h: "UOM" },
                                                                                { h: "Total Qty", align: "right" },
                                                                                { h: "Produced", align: "right" },
                                                                                { h: "Setup Scrap", align: "right" },
                                                                                { h: "Process Scrap", align: "right" },
                                                                                { h: "Wirebreak Scrap", align: "right" },
                                                                                { h: "Storage Location", required: true },
                                                                                { h: "Batch Number" },
                                                                                { h: "Status", align: "center" },
                                                                            ].map(({ h, align, required }) => (
                                                                                <th key={h} style={{ background: "#f8fafc", fontSize: 12, fontWeight: 600, padding: "9px 10px", borderBottom: "2px solid #e5e7eb", color: "#1e293b", whiteSpace: "nowrap", textAlign: align || "left" }}>{h}{required && (isConfirmMode || isEditMode) && <span style={{ color: "#dc2626" }}> *</span>}</th>
                                                                            ))}
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        <tr>
                                                                            <td style={{ fontSize: 12, padding: "8px 10px", borderBottom: "1px solid #f1f5f9", color: "#475569", textAlign: "center", fontWeight: 600 }}>1</td>
                                                                            <td style={{ fontSize: 12, padding: "8px 10px", borderBottom: "1px solid #f1f5f9", color: "#334155" }}>{modalData.productType || "FG"}</td>
                                                                            <td style={{ fontSize: 12, padding: "8px 10px", borderBottom: "1px solid #f1f5f9", color: "#1e293b", fontWeight: 600 }}>
                                                                                {modalData.header_product_code ? (
                                                                                    <Link
                                                                                        to={`/cable/product/product-code?view=${encodeURIComponent(modalData.header_product_code)}&returnTo=${encodeURIComponent(location.pathname + location.search)}`}
                                                                                        style={{ color: "#2563eb", fontWeight: 500, textDecoration: "underline", textUnderlineOffset: 3 }}
                                                                                        onMouseEnter={e => e.currentTarget.style.color = "#1d4ed8"}
                                                                                        onMouseLeave={e => e.currentTarget.style.color = "#2563eb"}
                                                                                    >
                                                                                        {modalData.header_product_code}
                                                                                    </Link>
                                                                                ) : ""}
                                                                            </td>
                                                                            <td style={{ fontSize: 12, padding: "8px 10px", borderBottom: "1px solid #f1f5f9", color: "#334155", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={modalData.productDescription}>{modalData.productDescription || ""}</td>
                                                                            <td style={{ fontSize: 12, padding: "8px 10px", borderBottom: "1px solid #f1f5f9", color: "#334155" }}>{modalData.header_uom || ""}</td>
                                                                            <td style={{ fontSize: 12, padding: "8px 10px", borderBottom: "1px solid #f1f5f9", color: "#0f172a", fontWeight: 600, textAlign: "right" }}>
                                                                                {(() => {
                                                                                    // Fresh production-table data (no specific saved confirmation open)
                                                                                    // shows what's still PENDING, not the full order qty. A saved
                                                                                    // confirmation (modalData.confirmationId set) keeps showing its
                                                                                    // own recorded quantity, untouched.
                                                                                    const displayQty = (!modalData.confirmationId && modalData.pending_qty !== undefined && modalData.pending_qty !== "" && modalData.pending_qty !== null)
                                                                                        ? modalData.pending_qty
                                                                                        : modalData.quantity;
                                                                                    return (displayQty !== undefined && displayQty !== null && displayQty !== "") ? parseFloat(parseFloat(displayQty).toFixed(2)) : "";
                                                                                })()}
                                                                            </td>
                                                                            <td style={{ fontSize: 12, padding: "8px 10px", borderBottom: "1px solid #f1f5f9", textAlign: "right" }}>
                                                                                {isFinishedGoodType ? (
                                                                                    <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "flex-end" }}>
                                                                                        <input
                                                                                            type="number"
                                                                                            style={{ flex: 1, minWidth: 70, padding: "4px 6px", fontSize: 12, border: "1px solid #d1d5db", borderRadius: 4, height: 28, boxSizing: "border-box", textAlign: "right", background: "#f1f5f9" }}
                                                                                            value={outputProduced || (fgTotalProduced > 0 ? fgTotalProduced : "")}
                                                                                            placeholder="0"
                                                                                            readOnly
                                                                                        />
                                                                                        <button
                                                                                            title="Enter Drum/Coil batches"
                                                                                            onClick={() => setShowFgBatchModal(true)}
                                                                                            style={{ border: "none", background: "transparent", cursor: "pointer", padding: 2 }}
                                                                                        >
                                                                                            <span style={{ display: "inline-block", transition: "transform 0.2s", transform: showFgBatchModal ? "rotate(90deg)" : "rotate(0deg)" }}>▶</span>
                                                                                        </button>
                                                                                    </div>
                                                                                ) : (isConfirmMode || isEditMode) ? (
                                                                                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                                                        <input
                                                                                            type="number"
                                                                                            disabled={hasScrapGroupLogic}
                                                                                            style={{
                                                                                                flex: 1, minWidth: 70, padding: "4px 6px", fontSize: 12, border: "1px solid #d1d5db", borderRadius: 4, height: 28, boxSizing: "border-box", textAlign: "right",
                                                                                                background: hasScrapGroupLogic ? "#f1f5f9" : "#fff",
                                                                                                color: hasScrapGroupLogic ? "#94a3b8" : "#1e293b",
                                                                                                cursor: hasScrapGroupLogic ? "not-allowed" : "text",
                                                                                            }}
                                                                                            value={outputProduced}
                                                                                            placeholder="0"
                                                                                            onChange={(e) => {
                                                                                                if (hasScrapGroupLogic) return;
                                                                                                const val = e.target.value;
                                                                                                setOutputProduced(val);
                                                                                                setDeliveredActual(val);
                                                                                            }}
                                                                                            onKeyDown={(e) => {
                                                                                                if (e.key === "Enter") {
                                                                                                    const val = outputProduced;
                                                                                                    if (!val || parseFloat(val) <= 0) {
                                                                                                        toast.warn("Enter a valid produced qty first");
                                                                                                        return;
                                                                                                    }
                                                                                                    if (outputBatchCharacteristics.length === 0 && producedCharacteristics.length > 0) {
                                                                                                        setOutputBatchCharacteristics(producedCharacteristics.map(c => ({ ...c, value: "" })));
                                                                                                    }
                                                                                                    if (producedCharacteristics.length > 0 || outputBatchCharacteristics.length > 0) {
                                                                                                        setShowOutputCharModal(true);
                                                                                                    }
                                                                                                }
                                                                                            }}
                                                                                        />
                                                                                        {(producedCharacteristics.length > 0 || outputBatchCharacteristics.length > 0) && (
                                                                                            <button
                                                                                                title="Enter batch characteristics (or press Enter in qty field)"
                                                                                                onClick={() => {
                                                                                                    if (!outputProduced || parseFloat(outputProduced) <= 0) {
                                                                                                        toast.warn("Enter a produced qty first");
                                                                                                        return;
                                                                                                    }
                                                                                                    if (outputBatchCharacteristics.length === 0) {
                                                                                                        setOutputBatchCharacteristics(producedCharacteristics.map(c => ({ ...c, value: "" })));
                                                                                                    }
                                                                                                    setShowOutputCharModal(true);
                                                                                                }}
                                                                                                style={{ flexShrink: 0, fontSize: 10, padding: "2px 7px", border: "1px solid #cbd5e1", borderRadius: 4, background: outputBatchCharacteristics.some(c => c.value) ? "#dbeafe" : "#f8fafc", color: outputBatchCharacteristics.some(c => c.value) ? "#1d4ed8" : "#475569", cursor: "pointer", display: "flex", alignItems: "center", gap: 3, whiteSpace: "nowrap", height: 28 }}
                                                                                            >
                                                                                                <span style={{ display: "inline-block", transition: "transform 0.2s", transform: showOutputCharModal ? "rotate(90deg)" : "rotate(0deg)" }}>▶</span>
                                                                                            </button>
                                                                                        )}
                                                                                    </div>
                                                                                ) : (
                                                                                    <span style={{ fontWeight: 600 }}>{outputProduced || deliveredActual || ""}</span>
                                                                                )}
                                                                            </td>
                                                                            {/* Scrap Columns — Setup/Process/Wirebreak Scrap are always
                                                                                captured here at the order/output level, for every process
                                                                                (not just group-driven ones), since Input never carries scrap. */}
                                                                            <>
                                                                                    <td style={{ fontSize: 12, padding: "8px 10px", borderBottom: "1px solid #f1f5f9", textAlign: "right" }}>
                                                                                        {(isConfirmMode || isEditMode) ? (
                                                                                            <input
                                                                                                type="number"
                                                                                                style={{ width: "100%", minWidth: 70, padding: "4px 6px", fontSize: 12, border: "1px solid #d1d5db", borderRadius: 4, height: 28, boxSizing: "border-box", textAlign: "right" }}
                                                                                                value={outputSetupScrap}
                                                                                                placeholder="0"
                                                                                                onChange={(e) => setOutputSetupScrap(e.target.value)}
                                                                                            />
                                                                                        ) : (
                                                                                            <span style={{ fontWeight: 600 }}>{outputSetupScrap || ""}</span>
                                                                                        )}
                                                                                    </td>
                                                                                    <td style={{ fontSize: 12, padding: "8px 10px", borderBottom: "1px solid #f1f5f9", textAlign: "right" }}>
                                                                                        {(isConfirmMode || isEditMode) ? (
                                                                                            <input
                                                                                                type="number"
                                                                                                style={{ width: "100%", minWidth: 70, padding: "4px 6px", fontSize: 12, border: "1px solid #d1d5db", borderRadius: 4, height: 28, boxSizing: "border-box", textAlign: "right" }}
                                                                                                value={outputProcessScrap}
                                                                                                placeholder="0"
                                                                                                onChange={(e) => setOutputProcessScrap(e.target.value)}
                                                                                            />
                                                                                        ) : (
                                                                                            <span style={{ fontWeight: 600 }}>{outputProcessScrap || ""}</span>
                                                                                        )}
                                                                                    </td>
                                                                                    <td style={{ fontSize: 12, padding: "8px 10px", borderBottom: "1px solid #f1f5f9", textAlign: "right" }}>
                                                                                        {(isConfirmMode || isEditMode) ? (
                                                                                            <input
                                                                                                type="number"
                                                                                                style={{ width: "100%", minWidth: 80, padding: "4px 6px", fontSize: 12, border: "1px solid #d1d5db", borderRadius: 4, height: 28, boxSizing: "border-box", textAlign: "right" }}
                                                                                                value={outputWirebreakScrap}
                                                                                                placeholder="0"
                                                                                                onChange={(e) => setOutputWirebreakScrap(e.target.value)}
                                                                                            />
                                                                                        ) : (
                                                                                            <span style={{ fontWeight: 600 }}>{outputWirebreakScrap || ""}</span>
                                                                                        )}
                                                                                    </td>
                                                                                </>
                                                                            <td style={{ fontSize: 12, padding: "8px 10px", borderBottom: "1px solid #f1f5f9" }}>
                                                                                {(isConfirmMode || isEditMode) ? (
                                                                                    <>
                                                                                    <Select
                                                                                        options={(storageLocations.length > 0 ? storageLocations : storageBins).map(b => ({ value: getStorageLocationName(b), label: getStorageLocationLabel(b) }))}
                                                                                        value={outputStorageBin ? { value: outputStorageBin, label: getStorageLocationLabel((storageLocations.length > 0 ? storageLocations : storageBins).find(b => getStorageLocationName(b) === outputStorageBin) || outputStorageBin) } : null}
                                                                                        placeholder="Select Location"
                                                                                        isClearable
                                                                                        noOptionsMessage={() => "No locations"}
                                                                                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                                                                        menuPosition="fixed"
                                                                                        onChange={(opt) => { const v = opt ? opt.value : ""; setOutputStorageBin(v); setSelectedHeaderBin(v); }}
                                                                                        styles={compactSelectStyles({ invalid: confirmAttemptedSave && !outputStorageBin })}
                                                                                    />
                                                                                    {confirmAttemptedSave && !outputStorageBin && (
                                                                                        <div style={{ color: "#dc2626", fontSize: 11, marginTop: 2 }}>Required</div>
                                                                                    )}
                                                                                    </>
                                                                                ) : (
                                                                                    <span>{getStorageLocationLabel(storageLocations.find(s => s.storage_location === (outputStorageBin || selectedHeaderBin)) || (outputStorageBin || selectedHeaderBin || ""))}</span>
                                                                                )}
                                                                            </td>
                                                                            <td style={{ fontSize: 12, padding: "8px 10px", borderBottom: "1px solid #f1f5f9", color: "#94a3b8", fontStyle: "italic" }}>
                                                                                {isFinishedGoodType
                                                                                    ? "See Produced"
                                                                                    : modalData.batch_no
                                                                                        ? <span style={{ color: "#1e293b", fontWeight: 600, fontStyle: "normal" }}>{modalData.batch_no}</span>
                                                                                        : isConfirmMode && outputProduced && batchFactor > 1
                                                                                            ? <span style={{ color: "#1e293b", fontWeight: 600, fontStyle: "normal" }}>{Array.from({ length: batchFactor }, (_, i) => `${modalData.production_order_number}-${i + 1}`).join(", ")}</span>
                                                                                            : isConfirmMode ? "Auto-generated on save" : "—"}
                                                                            </td>
                                                                            <td style={{ fontSize: 12, padding: "8px 10px", borderBottom: "1px solid #f1f5f9", textAlign: "center" }}>
                                                                                {derivedInspectionStatus === "Passed" && (
                                                                                    <span style={{ display: "inline-block", padding: "3px 12px", borderRadius: 12, fontSize: 11, fontWeight: 700, background: "#dcfce7", color: "#16a34a" }}>Passed</span>
                                                                                )}
                                                                                {derivedInspectionStatus === "NC" && (
                                                                                    <span style={{ display: "inline-block", padding: "3px 12px", borderRadius: 12, fontSize: 11, fontWeight: 700, background: "#fee2e2", color: "#dc2626" }}>NC</span>
                                                                                )}
                                                                                {!derivedInspectionStatus && (
                                                                                    <span style={{ color: "#94a3b8", fontSize: 11 }}>—</span>
                                                                                )}
                                                                            </td>
                                                                        </tr>
                                                                        {outputAttributes.length > 0 && (
                                                                            <tr>
                                                                                <td colSpan={11} style={{ padding: "0 0 6px 40px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                                                                                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                                                                        <thead>
                                                                                            <tr>
                                                                                                <th style={{ background: "#f1f5f9", fontSize: 11, fontWeight: 600, padding: "5px 10px", borderBottom: "1px solid #e2e8f0", color: "#64748b", width: 36, textAlign: "center" }}>Sl</th>
                                                                                                <th style={{ background: "#f1f5f9", fontSize: 11, fontWeight: 600, padding: "5px 10px", borderBottom: "1px solid #e2e8f0", color: "#64748b" }}>Text Parameter</th>
                                                                                                <th style={{ background: "#f1f5f9", fontSize: 11, fontWeight: 600, padding: "5px 10px", borderBottom: "1px solid #e2e8f0", color: "#64748b" }}>Job TP</th>
                                                                                                <th style={{ background: "#f1f5f9", fontSize: 11, fontWeight: 600, padding: "5px 10px", borderBottom: "1px solid #e2e8f0", color: "#64748b", width: 220 }}>Actuals</th>
                                                                                            </tr>
                                                                                        </thead>
                                                                                        <tbody>
                                                                                            {outputAttributes.map((attr, aIdx) => (
                                                                                                <tr key={aIdx}>
                                                                                                    <td style={{ fontSize: 11, padding: "5px 10px", borderBottom: "1px solid #f1f5f9", color: "#94a3b8", textAlign: "center", fontWeight: 600, background: "#f8fafc" }}>{aIdx + 1}</td>
                                                                                                    <td style={{ fontSize: 11, padding: "5px 10px", borderBottom: "1px solid #f1f5f9", color: "#475569", background: "#f8fafc" }}>{attr.characteristic}</td>
                                                                                                    <td style={{ fontSize: 11, padding: "5px 10px", borderBottom: "1px solid #f1f5f9", color: "#1e293b", fontWeight: 500, background: "#f8fafc" }}>{attr.value || ""}</td>
                                                                                                    <td style={{ fontSize: 11, padding: "5px 10px", borderBottom: "1px solid #f1f5f9", background: "#f8fafc" }}>
                                                                                                        {(isConfirmMode || isEditMode) ? (
                                                                                                            <input
                                                                                                                type="text"
                                                                                                                style={{ width: "100%", padding: "3px 6px", fontSize: 11, border: "1px solid #d1d5db", borderRadius: 4, height: 26, boxSizing: "border-box", background: "#fff" }}
                                                                                                                value={outputActuals[attr.characteristic] || ""}
                                                                                                                placeholder="Enter actuals"
                                                                                                                onChange={(e) => setOutputActuals(prev => ({ ...prev, [attr.characteristic]: e.target.value }))}
                                                                                                            />
                                                                                                        ) : (
                                                                                                            <span style={{ color: outputActuals[attr.characteristic] ? "#1e293b" : "#94a3b8", fontSize: 11 }}>{outputActuals[attr.characteristic] || "—"}</span>
                                                                                                        )}
                                                                                                    </td>
                                                                                                </tr>
                                                                                            ))}
                                                                                        </tbody>
                                                                                    </table>
                                                                                </td>
                                                                            </tr>
                                                                        )}
                                                                    </tbody>
                                                                </table>
                                                            </div>

                                                            {/* Group-driven Output capture — only shown when this
                                                                production order's process has
                                                                process_configuration.group_id set. Adds No. of
                                                                Bobbins / Length / Weight / Bobbin No (manual entry,
                                                                one row per bobbin — click "+ Add" for another).
                                                                Setup/Process/Wirebreak Scrap are NOT captured here —
                                                                they moved to the Output table's Scrap column(s) above,
                                                                at the order level (not per-bobbin). */}
                                                            {hasScrapGroupLogic && (
                                                                <div style={{ marginTop: 12, borderRadius: 8, border: "1px solid #e5e7eb", overflow: "hidden", background: "#fff" }}>
                                                                    {/* PLANNED — read-only, sourced from the Production
                                                                        Order's own Bobbins table (po_material_details).
                                                                        Mirrors the Machine Time Planned/Actual layout. */}
                                                                    <div>
                                                                        <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", background: "#f3f4f6", padding: "5px 12px", borderRadius: "4px 4px 0 0", display: "inline-block", letterSpacing: "0.04em", textTransform: "uppercase" }}>
                                                                            Planned
                                                                        </div>
                                                                        <div style={{ overflowX: "auto", background: "#fff" }}>
                                                                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                                                                <thead>
                                                                                    <tr>
                                                                                        {["No. of Spools/Baskets", "Length (M) per Spools/Baskets", "Weight (Kg) per Spools/Baskets", "Spools/Baskets Name"].map((h) => (
                                                                                            <th key={h} style={{ background: "#f8fafc", fontSize: 12, fontWeight: 600, padding: "9px 10px", borderBottom: "2px solid #e5e7eb", color: "#1e293b", whiteSpace: "nowrap", textAlign: "left" }}>{h}</th>
                                                                                        ))}
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {plannedBobbinRows.length === 0 ? (
                                                                                        <tr>
                                                                                            <td colSpan={4} style={{ fontSize: 12, padding: "10px", textAlign: "center", color: "#94a3b8" }}>
                                                                                                No planned bobbins found on the Production Order.
                                                                                            </td>
                                                                                        </tr>
                                                                                    ) : (
                                                                                        plannedBobbinRows.map((row, pIdx) => (
                                                                                            <tr key={pIdx}>
                                                                                                {["no_of_bobbins", "length", "weight", "bobbin_no"].map((fKey) => (
                                                                                                    <td key={fKey} style={{ fontSize: 12, padding: "8px 10px", borderBottom: "1px solid #f1f5f9", textAlign: "left", color: "#334155" }}>
                                                                                                        {row[fKey] || row[fKey] === 0 ? row[fKey] : "—"}
                                                                                                    </td>
                                                                                                ))}
                                                                                            </tr>
                                                                                        ))
                                                                                    )}
                                                                                </tbody>
                                                                            </table>
                                                                        </div>
                                                                    </div>

                                                                    <div style={{ borderTop: "2px solid #e2e8f0" }} />

                                                                    {/* ACTUAL — editable, entered here at Confirmation. */}
                                                                    <div style={{ padding: "10px 10px 0" }}>
                                                                        <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", background: "#f3f4f6", padding: "5px 12px", borderRadius: 4, display: "inline-block", letterSpacing: "0.04em", textTransform: "uppercase", marginBottom: 8 }}>
                                                                            Actual
                                                                        </div>
                                                                        {(isConfirmMode || isEditMode) && (
                                                                            <div>
                                                                                <button
                                                                                    onClick={addGroupRow}
                                                                                    style={{ marginBottom: 8, marginLeft: 8, padding: "5px 12px", fontSize: 12, fontWeight: 600, border: "1px solid #d1d5db", borderRadius: 6, background: "#f8fafc", cursor: "pointer" }}
                                                                                >
                                                                                    + Add
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                    <div style={{ overflowX: "auto", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff" }}>
                                                                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                                                            <thead>
                                                                                <tr>
                                                                                    {["No. of Spools/Baskets", "Length (M) per Spools/Baskets", "Weight (Kg) per Spools/Baskets", "Spools/Baskets Name", ""].map((h) => (
                                                                                        <th key={h} style={{ background: "#f8fafc", fontSize: 12, fontWeight: 600, padding: "9px 10px", borderBottom: "2px solid #e5e7eb", color: "#1e293b", whiteSpace: "nowrap", textAlign: h ? "left" : "center" }}>{h}</th>
                                                                                    ))}
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {outputGroupRows.map((row, rIdx) => {
                                                                                    return (
                                                                                        <tr key={rIdx}>
                                                                                            {[
                                                                                                { key: "no_of_bobbins", type: "number" },
                                                                                                { key: "length", type: "number" },
                                                                                                { key: "weight", type: "number" },
                                                                                                { key: "bobbin_no", type: "text" },
                                                                                            ].map(({ key: fKey, type }) => (
                                                                                                <td key={fKey} style={{ fontSize: 12, padding: "8px 10px", borderBottom: "1px solid #f1f5f9", textAlign: "left" }}>
                                                                                                    {(isConfirmMode || isEditMode) ? (
                                                                                                        <input
                                                                                                            type={type}
                                                                                                            style={{
                                                                                                                width: "100%", minWidth: 90, padding: "4px 6px", fontSize: 12,
                                                                                                                border: "1px solid #d1d5db", borderRadius: 4, height: 28, boxSizing: "border-box",
                                                                                                                textAlign: "left",
                                                                                                            }}
                                                                                                            value={row[fKey] ?? ""}
                                                                                                            placeholder={type === "number" ? "0" : ""}
                                                                                                            onChange={(e) => updateGroupRowField(rIdx, fKey, e.target.value)}
                                                                                                        />
                                                                                                    ) : (
                                                                                                        <span style={{ fontWeight: 600 }}>{row[fKey] || "—"}</span>
                                                                                                    )}
                                                                                                </td>
                                                                                            ))}
                                                                                            <td style={{ padding: "6px 8px", borderBottom: "1px solid #f1f5f9", textAlign: "center" }}>
                                                                                                {(isConfirmMode || isEditMode) && outputGroupRows.length > 1 && (
                                                                                                    <button
                                                                                                        title="Remove row"
                                                                                                        onClick={() => removeGroupRow(rIdx)}
                                                                                                        style={{ border: "none", background: "transparent", color: "#dc2626", cursor: "pointer", fontSize: 14 }}
                                                                                                    >
                                                                                                        ×
                                                                                                    </button>
                                                                                                )}
                                                                                            </td>
                                                                                        </tr>
                                                                                    );
                                                                                })}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* PROCESS PARAMETER */}
                                                {key === "process_parameter" && (
                                                    processParameters.length === 0 ? (
                                                        <div style={{ textAlign: "center", color: "#94a3b8", fontSize: 13, padding: "30px 0" }}>No process parameters found for this routing.</div>
                                                    ) : (() => {
                                                        const hasSubProcess = processParameters.some(r => r.sub_process_name && r.sub_process_name.toString().trim() !== "");
                                                        const ppTh = { background: "#f8fafc", fontSize: 12, fontWeight: 600, padding: "9px 12px", borderBottom: "2px solid #e5e7eb", color: "#1e293b", whiteSpace: "nowrap", textAlign: "left" };
                                                        const ppTd = { fontSize: 12, padding: "8px 12px", borderBottom: "1px solid #f1f5f9", color: "#334155" };
                                                        return (
                                                            <div style={{ overflowX: "auto", borderRadius: 8, border: "1px solid #e5e7eb" }}>
                                                                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: hasSubProcess ? 750 : 680 }}>
                                                                    <thead>
                                                                        <tr>
                                                                            <th style={{ ...ppTh, textAlign: "center", width: 40 }}>Sl</th>
                                                                            <th style={ppTh}>Process</th>
                                                                            {hasSubProcess && <th style={ppTh}>Subprocess</th>}
                                                                            <th style={ppTh}>Parameter</th>
                                                                            <th style={ppTh}>UOM</th>
                                                                            <th style={ppTh}>Specified Value</th>
                                                                            <th style={ppTh}>Actuals</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {processParameters.map((row, idx) => {
                                                                            const ppKey = `${row.process_name || ""}||${row.parameter_name || ""}`;
                                                                            return (
                                                                                <tr key={idx} style={{ background: idx % 2 === 0 ? "#fff" : "#fafbfc" }}>
                                                                                    <td style={{ ...ppTd, textAlign: "center", fontWeight: 600, color: "#64748b" }}>{idx + 1}</td>
                                                                                    <td style={ppTd}>{row.process_name || ""}</td>
                                                                                    {hasSubProcess && <td style={ppTd}>{row.sub_process_name || ""}</td>}
                                                                                    <td style={{ ...ppTd, fontWeight: 500 }}>{row.parameter_name || ""}</td>
                                                                                    <td style={{ ...ppTd, color: "#64748b" }}>{row.parameter_uom || ""}</td>
                                                                                    <td style={{ ...ppTd, fontWeight: 600, color: "#1e293b" }}>{row.parameter_value ?? ""}</td>
                                                                                    <td style={ppTd}>
                                                                                        {(isConfirmMode || isEditMode) ? (
                                                                                            <input
                                                                                                type="text"
                                                                                                style={{ width: "100%", padding: "3px 6px", fontSize: 12, border: "1px solid #d1d5db", borderRadius: 4, height: 28, boxSizing: "border-box", background: "#fff" }}
                                                                                                value={processParamActuals[ppKey] || ""}
                                                                                                placeholder="Enter actuals"
                                                                                                onChange={(e) => setProcessParamActuals(prev => ({ ...prev, [ppKey]: e.target.value }))}
                                                                                            />
                                                                                        ) : (
                                                                                            <span style={{ color: processParamActuals[ppKey] ? "#1e293b" : "#94a3b8", fontSize: 12 }}>{processParamActuals[ppKey] || "—"}</span>
                                                                                        )}
                                                                                    </td>
                                                                                </tr>
                                                                            );
                                                                        })}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        );
                                                    })()
                                                )}

                                                {/* MACHINE PARAMETER */}
                                                {key === "machine_parameter" && (
                                                    machineParameters.length === 0 ? (
                                                        <div style={{ textAlign: "center", color: "#94a3b8", fontSize: 13, padding: "30px 0" }}>No machine parameters found for this routing.</div>
                                                    ) : (() => {
                                                        const hasSubProcess = machineParameters.some(r => r.sub_process_name && r.sub_process_name.toString().trim() !== "");
                                                        const mpTh = { background: "#f8fafc", fontSize: 12, fontWeight: 600, padding: "9px 12px", borderBottom: "2px solid #e5e7eb", color: "#1e293b", whiteSpace: "nowrap", textAlign: "left" };
                                                        const mpTd = { fontSize: 12, padding: "8px 12px", borderBottom: "1px solid #f1f5f9", color: "#334155" };
                                                        return (
                                                            <div style={{ overflowX: "auto", borderRadius: 8, border: "1px solid #e5e7eb" }}>
                                                                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: hasSubProcess ? 750 : 680 }}>
                                                                    <thead>
                                                                        <tr>
                                                                            <th style={{ ...mpTh, textAlign: "center", width: 40 }}>Sl</th>
                                                                            <th style={mpTh}>Process</th>
                                                                            {hasSubProcess && <th style={mpTh}>Subprocess</th>}
                                                                            <th style={mpTh}>Workcenter</th>
                                                                            <th style={mpTh}>Parameter</th>
                                                                            <th style={mpTh}>UOM</th>
                                                                            <th style={mpTh}>Specified Value</th>
                                                                            <th style={mpTh}>Actuals</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {machineParameters.map((row, idx) => {
                                                                            const mpKey = `${row.workcenter_name || ""}||${row.parameter_name || ""}`;
                                                                            return (
                                                                                <tr key={idx} style={{ background: idx % 2 === 0 ? "#fff" : "#fafbfc" }}>
                                                                                    <td style={{ ...mpTd, textAlign: "center", fontWeight: 600, color: "#64748b" }}>{idx + 1}</td>
                                                                                    <td style={mpTd}>{row.process_name || ""}</td>
                                                                                    {hasSubProcess && <td style={mpTd}>{row.sub_process_name || ""}</td>}
                                                                                    <td style={mpTd}>{row.workcenter_name || ""}</td>
                                                                                    <td style={{ ...mpTd, fontWeight: 500 }}>{row.parameter_name || ""}</td>
                                                                                    <td style={{ ...mpTd, color: "#64748b" }}>{row.parameter_uom || ""}</td>
                                                                                    <td style={{ ...mpTd, fontWeight: 600, color: "#1e293b" }}>{row.parameter_value ?? ""}</td>
                                                                                    <td style={mpTd}>
                                                                                        {(isConfirmMode || isEditMode) ? (
                                                                                            <input
                                                                                                type="text"
                                                                                                style={{ width: "100%", padding: "3px 6px", fontSize: 12, border: "1px solid #d1d5db", borderRadius: 4, height: 28, boxSizing: "border-box", background: "#fff" }}
                                                                                                value={machineParamActuals[mpKey] || ""}
                                                                                                placeholder="Enter actuals"
                                                                                                onChange={(e) => setMachineParamActuals(prev => ({ ...prev, [mpKey]: e.target.value }))}
                                                                                            />
                                                                                        ) : (
                                                                                            <span style={{ color: machineParamActuals[mpKey] ? "#1e293b" : "#94a3b8", fontSize: 12 }}>{machineParamActuals[mpKey] || "—"}</span>
                                                                                        )}
                                                                                    </td>
                                                                                </tr>
                                                                            );
                                                                        })}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        );
                                                    })()
                                                )}

                                                {/* MACHINE TIME */}
                                                {key === "machine_time" && (
                                                    <OperationsTable
                                                        rows={routingRows}
                                                        actualDates={actualDates}
                                                        setActualDates={setActualDates}
                                                        actualActivities={actualActivities}
                                                        setActualActivities={setActualActivities}
                                                        actualActivityDates={actualActivityDates}
                                                        setActualActivityDates={setActualActivityDates}
                                                        actualIdle={actualIdle}
                                                        setActualIdle={setActualIdle}
                                                        isViewMode={!isConfirmMode && !isEditMode}
                                                        onHeaderDateChange={handleGeneralActualChange}
                                                        qty={modalData.quantity}
                                                        baseUnit={modalData.base_unit}
                                                    />
                                                )}

                                                {/* INSPECTION PARAMETER */}
                                                {key === "inspection_parameter" && (
                                                    inspectionParameters.length === 0 ? (
                                                        <div style={{ textAlign: "center", color: "#94a3b8", fontSize: 13, padding: "30px 0" }}>
                                                            No inspection characteristics found for this routing.
                                                        </div>
                                                    ) : (() => {
                                                        const hasSubProcess = inspectionParameters.some(r => r.sub_process_name && r.sub_process_name.toString().trim() !== "");
                                                        const ipTh = { background: "#f8fafc", fontSize: 12, fontWeight: 600, padding: "9px 12px", borderBottom: "2px solid #e5e7eb", color: "#1e293b", whiteSpace: "nowrap", textAlign: "left" };
                                                        const ipTd = { fontSize: 12, padding: "8px 12px", borderBottom: "1px solid #f1f5f9", color: "#334155" };
                                                        return (
                                                            <div style={{ overflowX: "auto", borderRadius: 8, border: "1px solid #e5e7eb" }}>
                                                                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: hasSubProcess ? 1000 : 900 }}>
                                                                    <thead>
                                                                        <tr>
                                                                            <th style={{ ...ipTh, textAlign: "center", width: 40 }}>Sl</th>
                                                                            <th style={{ ...ipTh }}>Batch Number</th>
                                                                            <th style={ipTh}>Process</th>
                                                                            {hasSubProcess && <th style={ipTh}>Sub Process</th>}
                                                                            <th style={ipTh}>Characteristic</th>
                                                                            <th style={ipTh}>UOM</th>
                                                                            <th style={ipTh}>Specified Value</th>
                                                                            <th style={ipTh}>Actuals</th>
                                                                            <th style={ipTh}>Checked</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {inspectionParameters.map((ip, idx) => {
                                                                            const ipKey = `${ip.process_name || ""}||${ip.characteristic || ""}`;
                                                                            return (
                                                                                <tr key={idx} style={{ background: idx % 2 === 0 ? "#fff" : "#fafbfc" }}>
                                                                                    <td style={{ ...ipTd, textAlign: "center", fontWeight: 600, color: "#64748b" }}>{idx + 1}</td>
                                                                                    <td style={{ ...ipTd, fontWeight: 600, color: "#2563eb" }}>{modalData.batch_no || <span style={{ color: "#94a3b8", fontStyle: "italic", fontWeight: 400, fontSize: 11 }}>Pending</span>}</td>
                                                                                    <td style={ipTd}>{ip.process_name || ""}</td>
                                                                                    {hasSubProcess && <td style={ipTd}>{ip.sub_process_name || ""}</td>}
                                                                                    <td style={{ ...ipTd, fontWeight: 500, color: "#1e293b" }}>{ip.characteristic || ""}</td>
                                                                                    <td style={{ ...ipTd, color: "#64748b" }}>{ip.uom || ""}</td>
                                                                                    <td style={{ ...ipTd, fontWeight: 600, color: "#1e293b" }}>{ip.standard_value ?? ""}</td>
                                                                                    <td style={ipTd}>
                                                                                        {((isConfirmMode && modalData.release_status === "In Quality") || (!isConfirmMode && isEditMode)) ? (
                                                                                            <input
                                                                                                type="text"
                                                                                                style={{ width: "100%", padding: "3px 6px", fontSize: 12, border: "1px solid #d1d5db", borderRadius: 4, height: 28, boxSizing: "border-box", background: "#fff" }}
                                                                                                value={inspectionParamActuals[ipKey] || ""}
                                                                                                placeholder="Enter actuals"
                                                                                                onChange={(e) => {
                                                                                                    const newActual = e.target.value;
                                                                                                    setInspectionParamActuals(prev => ({ ...prev, [ipKey]: newActual }));
                                                                                                    const autoChecked = getAutoCheckedValue(ip.standard_value, newActual);
                                                                                                    if (autoChecked) {
                                                                                                        setInspectionChecked(prev => ({ ...prev, [ipKey]: autoChecked }));
                                                                                                    } else {
                                                                                                        // actual cleared / not determinable -> clear auto value, but keep any manual override untouched otherwise
                                                                                                        setInspectionChecked(prev => ({ ...prev, [ipKey]: "" }));
                                                                                                    }
                                                                                                }}
                                                                                            />
                                                                                        ) : (
                                                                                            <span style={{ color: !inspectionParamActuals[ipKey] ? "#94a3b8" : undefined, fontSize: 11 }}>
                                                                                                {inspectionParamActuals[ipKey] || "—"}
                                                                                            </span>
                                                                                        )}
                                                                                    </td>
                                                                                    <td style={ipTd}>
                                                                                        {(() => {
                                                                                            // eslint-disable-next-line no-unused-vars
                                                                                            const hasActual = !!(inspectionParamActuals[ipKey] || "").toString().trim();
                                                                                            const checkedVal = inspectionChecked[ipKey] || "";
                                                                                            if ((isConfirmMode && modalData.release_status === "In Quality") || (!isConfirmMode && isEditMode)) {
                                                                                                return (
                                                                                                    <select
                                                                                                        value={checkedVal}
                                                                                                        onChange={(e) => setInspectionChecked(prev => ({ ...prev, [ipKey]: e.target.value }))}
                                                                                                        style={{
                                                                                                            width: "100%", padding: "3px 6px", fontSize: 12, height: 28, borderRadius: 4, boxSizing: "border-box",
                                                                                                            border: `1px solid ${checkedVal === "Passed" ? "#86efac" : checkedVal === "NC" ? "#fca5a5" : "#d1d5db"}`,
                                                                                                            background: checkedVal === "Passed" ? "#f0fdf4" : checkedVal === "NC" ? "#fef2f2" : "#fff",
                                                                                                            color: checkedVal === "Passed" ? "#16a34a" : checkedVal === "NC" ? "#dc2626" : "#64748b",
                                                                                                            cursor: "pointer",
                                                                                                            fontWeight: checkedVal ? 600 : 400,
                                                                                                        }}
                                                                                                    >
                                                                                                        <option value="">Select</option>
                                                                                                        <option value="Passed">Passed</option>
                                                                                                        <option value="NC">NC</option>
                                                                                                    </select>
                                                                                                );
                                                                                            }
                                                                                            if (!checkedVal) return <span style={{ color: "#94a3b8", fontSize: 11 }}>—</span>;
                                                                                            return (
                                                                                                <span style={{
                                                                                                    display: "inline-block", padding: "2px 10px", borderRadius: 12, fontSize: 11, fontWeight: 600,
                                                                                                    background: checkedVal === "Passed" ? "#dcfce7" : "#fee2e2",
                                                                                                    color: checkedVal === "Passed" ? "#16a34a" : "#dc2626"
                                                                                                }}>{checkedVal}</span>
                                                                                            );
                                                                                        })()}
                                                                                    </td>
                                                                                </tr>
                                                                            );
                                                                        })}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        );
                                                    })()
                                                )}

                                                {/* DOCS */}
                                                {key === "documents" && (
                                                    <div>
                                                        {modalData.production_order_number && (
                                                            <div style={{ marginBottom: 20 }}>
                                                                <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", marginBottom: 10 }}>
                                                                    Generated Documents
                                                                </div>
                                                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 6, padding: "10px 14px" }}>
                                                                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                                                        <span style={{ fontSize: 22 }}>📄</span>
                                                                        <div>
                                                                            <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>Job Order</div>
                                                                            <div style={{ fontSize: 11, color: "#64748b" }}>
                                                                                Order: {modalData.production_order_number}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div style={{ display: "flex", gap: 8 }}>
                                                                        <button
                                                                            onClick={() => {
                                                                                const cid = modalData.confirmationId ? `?confirmationId=${modalData.confirmationId}` : "";
                                                                                window.open(`/job-order/${modalData.production_order_number}${cid}`, "_blank");
                                                                            }}
                                                                            style={{
                                                                                padding: "6px 14px", fontSize: 12, fontWeight: 600, borderRadius: 5, cursor: "pointer",
                                                                                border: "1.5px solid #3b82f6", background: "#eff6ff", color: "#2563eb",
                                                                            }}
                                                                        >
                                                                            👁 Preview
                                                                        </button>
                                                                        <button
                                                                            onClick={() => {
                                                                                const cid = modalData.confirmationId ? `&confirmationId=${modalData.confirmationId}` : "";
                                                                                window.open(`/job-order/${modalData.production_order_number}?download=true${cid}`, "_blank");
                                                                            }}
                                                                            style={{
                                                                                padding: "6px 14px", fontSize: 12, fontWeight: 600, borderRadius: 5, cursor: "pointer",
                                                                                border: "1.5px solid #22c55e", background: "#f0fdf4", color: "#16a34a",
                                                                            }}
                                                                        >
                                                                            ⬇ Download
                                                                        </button>
                                                                        <button
                                                                            onClick={() => {
                                                                                const cid = modalData.confirmationId ? `&confirmationId=${modalData.confirmationId}` : "";
                                                                                window.open(`/job-order/${modalData.production_order_number}?print=true${cid}`, "_blank");
                                                                            }}
                                                                            style={{
                                                                                padding: "6px 14px", fontSize: 12, fontWeight: 600, borderRadius: 5, cursor: "pointer",
                                                                                border: "1.5px solid #f59e0b", background: "#fffbeb", color: "#d97706",
                                                                            }}
                                                                        >
                                                                            🖨 Print
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                        <div style={{ padding: "12px", border: "1px solid #dee2e6", borderRadius: "6px" }}>
                                                            <label style={{ ...styles.compactLabel, color: "#0f172a" }}>Uploaded Documents</label>
                                                            <div style={{ marginTop: 8 }}>
                                                                <input
                                                                    type="file"
                                                                    multiple
                                                                    style={{ fontSize: "12px", padding: "6px", height: "30px" }}
                                                                    onChange={(e) => {
                                                                        console.log("Selected files:", e.target.files);
                                                                        alert("Files selected (UI only, no upload implemented).");
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* CHANGE LOG */}
                                                {key === "logs" && (
                                                    <div style={styles.tableContainer}>
                                                        <table className="table" style={styles.erpTable}>
                                                            <thead>
                                                                <tr>
                                                                    <th style={styles.erpTh}>Date & Time</th>
                                                                    <th style={styles.erpTh}>User</th>
                                                                    <th style={styles.erpTh}>Action</th>
                                                                    <th style={styles.erpTh}>Status</th>
                                                                    <th style={styles.erpTh}>Field</th>
                                                                    <th style={styles.erpTh}>Old Value</th>
                                                                    <th style={styles.erpTh}>New Value</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {changeLogs.length > 0 ? (
                                                                    changeLogs.map((log, i) => (
                                                                        <tr key={i}>
                                                                            <td style={styles.erpTd}>{formatChangeLogDateTime(log.created_at)}</td>
                                                                            <td style={styles.erpTd}>{log.updated_by || "User"}</td>
                                                                            <td style={styles.erpTd}>{log.action_type}</td>
                                                                            <td style={styles.erpTd}>{log.doc_status}</td>
                                                                            <td style={styles.erpTd}>{log.field_name || '-'}</td>
                                                                            <td style={{ ...styles.erpTd, maxWidth: '150px', wordWrap: 'break-word' }}>{log.old_value || '-'}</td>
                                                                            <td style={{ ...styles.erpTd, fontWeight: 600, maxWidth: '150px', wordWrap: 'break-word' }}>{log.new_value || '-'}</td>
                                                                        </tr>
                                                                    ))
                                                                ) : (
                                                                    <tr>
                                                                        <td colSpan="7" style={{ ...styles.erpTd, textAlign: "center", color: "#6c757d" }}>No confirmation history found.</td>
                                                                    </tr>
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}

                                            </div>
                                        )}
                                    </div>
                                ))}

                                {showProducedCharModal && (
                                    <CharacteristicsModal
                                        modalVisible={showProducedCharModal}
                                        selectedCharacteristics={outputBatchCharacteristics}
                                        isEditing={false}
                                        handleChange={() => { }}
                                        handleClose={() => setShowProducedCharModal(false)}
                                    />
                                )}

                            </div>

                            <div style={{ ...styles.modalFooter, padding: "10px 20px" }}>
                                <button
                                    onClick={closeDetailsModal}
                                    style={{ background: "#e2e8f0", border: "none", padding: "6px 18px", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 500 }}
                                >
                                    Close
                                </button>
                                {isConfirmMode && modalData.release_status === "In Quality" && !isFinishedGoodType && canConvert && (
                                    <button
                                        onClick={() => saveInspection(modalData.production_order_number)}
                                        style={{
                                            padding: "6px 18px", fontSize: 13, fontWeight: 600,
                                            background: "#0f766e", border: "none", color: "white",
                                            borderRadius: 6, cursor: "pointer",
                                        }}
                                    >
                                        Inspect
                                    </button>
                                )}
                                {isConfirmMode && modalData.release_status !== "In Quality" && canEdit && (
                                    <button
                                        onClick={() => confirmBatch(modalData.production_order_number)}
                                        style={{
                                            padding: "6px 18px", fontSize: 13, fontWeight: 600,
                                            background: "#050505",
                                            border: "none", color: "white", borderRadius: 6,
                                            cursor: "pointer",
                                        }}
                                    >
                                        Confirm
                                    </button>
                                )}
                                {!isConfirmMode && !isEditMode && canEdit && (
                                    <button
                                        onClick={() => setIsEditMode(true)}
                                        style={{
                                            padding: "6px 18px", fontSize: 13, fontWeight: 600,
                                            background: "#2563eb", border: "none", color: "white",
                                            borderRadius: 6, cursor: "pointer",
                                        }}
                                    >
                                        Edit
                                    </button>
                                )}
                                {!isConfirmMode && isEditMode && canEdit && (
                                    <button
                                        onClick={() => saveActuals(modalData.production_order_number)}
                                        style={{
                                            padding: "6px 18px", fontSize: 13, fontWeight: 600,
                                            background: "#16a34a", border: "none", color: "white",
                                            borderRadius: 6, cursor: "pointer",
                                        }}
                                    >
                                        Save
                                    </button>
                                )}
                            </div>

                        </div>
                    </div>
                )}

                {/* ── LISTING TABLE ── */}
                <div style={{ background: "#ffffff", borderRadius: 10, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", overflow: "hidden" }}>
                    <div style={{ overflowX: "auto", width: "100%" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 800 }}>
                            <thead>
                                <tr style={{ background: "#f8fafc" }}>
                                    <th style={styles.erpTh}>Production Order</th>
                                    <th style={styles.erpTh}>Confirmation ID</th>
                                    <th style={styles.erpTh}>Product Code</th>
                                    <th style={styles.erpTh}>Description</th>
                                    <th style={styles.erpTh}>Workcenter</th>
                                    <th style={{ ...styles.erpTh, textAlign: "right" }}>Order Qty</th>
                                    <th style={{ ...styles.erpTh, textAlign: "right" }}>Produced</th>
                                    <th style={styles.erpTh}>Start Date</th>
                                    <th style={styles.erpTh}>End Date</th>
                                    <th style={styles.erpTh}>Status</th>
                                    <th style={{ ...styles.erpTh, textAlign: "center" }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {orders.filter((o) => {
                                    const pType = o.productType || o.product_type || "";
                                    if (listingFilters.productType && pType !== listingFilters.productType.value) return false;

                                    const wName = o.workcenterName || o.workcenter_name || o.workcenterDescription || o.workcenter_description || "";
                                    if (selectedWorkcenter && wName !== selectedWorkcenter) return false;

                                    const startDateRaw = o.startDate || o.start_date || o.production_start_date || "";
                                    if (startDateRaw) {
                                        const sDate = String(startDateRaw).substring(0, 10);
                                        if (selectedProdStartDate && sDate < selectedProdStartDate) return false;
                                        if (selectedProdEndDate && sDate > selectedProdEndDate) return false;
                                    } else if (selectedProdStartDate || selectedProdEndDate) {
                                        return false;
                                    }

                                    if (selectedStatuses && selectedStatuses.length > 0) {
                                        const allowedStatuses = selectedStatuses.map(s => s.value.toLowerCase());
                                        const targetStatus = (o.release_status || "Pending").toLowerCase();
                                        if (!allowedStatuses.includes(targetStatus)) return false;
                                    }

                                    return true;
                                }).flatMap((o) => {
                                    // ── FLATTEN: one row per (production_order_number, confirmation_id)
                                    // + one extra "open" row (confirmation_id = null) for whatever qty
                                    // is still left to confirm on this PO. Example: order qty 1000,
                                    // confirmed 400 + 400 → 2 confirmation rows + 1 open row of 200.
                                    // Fully confirmed (0 remaining) → no open row, just the confirmations.
                                    const orderQty = parseFloat(o.order_qty || o.quantity || 0);
                                    const confirmations = o.confirmations || [];
                                    const confirmedTotal = confirmations.reduce((s, c) => s + parseFloat(c.quantity || 0), 0);
                                    const remainingQty = Math.max(0, orderQty - confirmedTotal);

                                    const rows = confirmations.map((c) => ({
                                        ...o,
                                        confirmation_id: c.confirmation_id,
                                        row_qty: c.quantity,
                                        row_batch_no: c.batch_no,
                                        row_status: c.release_status,
                                    }));

                                    if (o.release_status === "Released" && (remainingQty > 0.0001 || confirmations.length === 0)) {
                                        rows.push({
                                            ...o,
                                            confirmation_id: null,
                                            row_qty: remainingQty,
                                            row_batch_no: null,
                                            row_status: o.release_status,
                                        });
                                    }

                                    return rows;
                                }).map((o, i) => (
                                    <tr
                                        key={`${o.production_order_number}-${o.confirmation_id || "open"}-${i}`}
                                        style={{ background: i % 2 === 0 ? "#fff" : "#fafbfc" }}
                                        onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"}
                                        onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? "#fff" : "#fafbfc"}
                                    >
                                        <td style={{ ...styles.erpTd, fontWeight: 600, color: "#1e293b" }}>
                                            {o.production_order_number ? (
                                                <Link
                                                    to={`/cable/production-order?pod=${encodeURIComponent(o.production_order_number)}&returnTo=${encodeURIComponent(location.pathname + location.search)}`}
                                                    style={{ color: "#2563eb", fontWeight: 600, textDecoration: "underline", textUnderlineOffset: 3 }}
                                                    onMouseEnter={e => e.currentTarget.style.color = "#1d4ed8"}
                                                    onMouseLeave={e => e.currentTarget.style.color = "#2563eb"}
                                                >
                                                    {o.production_order_number}
                                                </Link>
                                            ) : (
                                                o.production_order_number
                                            )}
                                        </td>
                                        <td style={styles.erpTd}>
                                            {o.confirmation_id ? (
                                                <span
                                                    onClick={() => openDetails(o.production_order_number, o.confirmation_id)}
                                                    style={{ color: "#2563eb", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3, fontWeight: 600 }}
                                                    onMouseEnter={e => e.currentTarget.style.color = "#1d4ed8"}
                                                    onMouseLeave={e => e.currentTarget.style.color = "#2563eb"}
                                                >
                                                    {o.confirmation_id}
                                                </span>
                                            ) : (
                                                <span style={{ color: "#94a3b8", fontStyle: "italic" }}></span>
                                            )}
                                        </td>
                                        <td style={styles.erpTd}>
                                            {o.header_product_code ? (
                                                <Link
                                                    to={`/cable/product/product-code?view=${encodeURIComponent(o.header_product_code)}&returnTo=${encodeURIComponent(location.pathname + location.search)}`}
                                                    style={{ color: "#2563eb", fontWeight: 500, textDecoration: "underline", textUnderlineOffset: 3 }}
                                                    onMouseEnter={e => e.currentTarget.style.color = "#1d4ed8"}
                                                    onMouseLeave={e => e.currentTarget.style.color = "#2563eb"}
                                                >
                                                    {o.header_product_code}
                                                </Link>
                                            ) : ""}
                                        </td>
                                        <td style={{ ...styles.erpTd, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={o.productDescription || o.description || ""}>
                                            {o.product_description || o.description || ""}
                                        </td>
                                        <td style={styles.erpTd}>
                                            {(o.workcenterName || o.workcenter_name || o.workcenterDescription || o.workcenter_description) ? (
                                                <Link
                                                    to={`/cable/master-data/workcenter?view=${encodeURIComponent(o.workcenterName || o.workcenter_name || o.workcenterDescription || o.workcenter_description)}&returnTo=${encodeURIComponent(location.pathname + location.search)}`}
                                                    style={{ color: "#2563eb", fontWeight: 500, textDecoration: "underline", textUnderlineOffset: 3 }}
                                                    onMouseEnter={e => e.currentTarget.style.color = "#1d4ed8"}
                                                    onMouseLeave={e => e.currentTarget.style.color = "#2563eb"}
                                                >
                                                    {o.workcenterName || o.workcenter_name || o.workcenterDescription || o.workcenter_description}
                                                </Link>
                                            ) : "-"}
                                        </td>
                                        <td style={{ ...styles.erpTd, textAlign: "right", fontWeight: 600 }}>{o.order_qty || o.quantity || ""}</td>
                                        <td style={{ ...styles.erpTd, textAlign: "right", fontWeight: 600, color: o.confirmation_id ? "#16a34a" : "#94a3b8" }}>
                                            {o.confirmation_id ? (o.row_qty ?? "") : ""}
                                        </td>
                                        <td style={styles.erpTd}>
                                            {formatDMY(o.startDate || o.start_date || o.production_start_date || "")}
                                        </td>
                                        <td style={styles.erpTd}>
                                            {formatDMY(o.endDate || o.end_date || o.production_end_date || "")}
                                        </td>
                                        <td style={styles.erpTd}>{o.confirmation_id ? (o.row_status || "In Quality") : o.release_status}</td>
                                        <td style={{ ...styles.erpTd, textAlign: "center" }}>
                                            {(() => {
                                                const pType = (o.productType || o.product_type || "").trim().toUpperCase();
                                                const isFG = pType === "FINISHED GOODS";
                                                return (
                                            <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                                                {o.confirmation_id ? (
                                                    <>
                                                        <button
                                                            style={{ padding: "5px 14px", fontSize: 12, fontWeight: 600, border: "1.5px solid #cbd5e1", borderRadius: 6, background: "#fff", color: "#334155", cursor: "pointer", transition: "all 0.15s" }}
                                                            onMouseEnter={e => { e.currentTarget.style.background = "#1e293b"; e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = "#1e293b"; }}
                                                            onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#334155"; e.currentTarget.style.borderColor = "#cbd5e1"; }}
                                                            onClick={() => openDetails(o.production_order_number, o.confirmation_id)}
                                                        >
                                                            View
                                                        </button>
                                                        {!isFG && o.row_status !== "In Stock" && o.row_status !== "NC" && canConvert && (
                                                            <button
                                                                style={{ padding: "5px 14px", fontSize: 12, fontWeight: 600, border: "1.5px solid #0f766e", borderRadius: 6, background: "#0f766e", color: "#fff", cursor: "pointer", transition: "all 0.15s" }}
                                                                onClick={() => openDetails(o.production_order_number, o.confirmation_id, "inspect")}
                                                            >
                                                                Inspect
                                                            </button>
                                                        )}
                                                    </>
                                                ) : (
                                                    <>
                                                        <button
                                                            style={{ padding: "5px 14px", fontSize: 12, fontWeight: 600, border: "1.5px solid #cbd5e1", borderRadius: 6, background: "#fff", color: "#334155", cursor: "pointer", transition: "all 0.15s" }}
                                                            onMouseEnter={e => { e.currentTarget.style.background = "#1e293b"; e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = "#1e293b"; }}
                                                            onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#334155"; e.currentTarget.style.borderColor = "#cbd5e1"; }}
                                                            onClick={() => openDetails(o.production_order_number)}
                                                        >
                                                            View
                                                        </button>
                                                        {(() => {
                                                            const rs = (o.release_status || "");
                                                            const isConfirmEnabled = rs === "Released" || (rs === "In Quality" && !isFG);
                                                            const label = (rs === "In Quality" && !isFG) ? "Inspect" : "Confirm";
                                                            const bgColor = (rs === "In Quality" && !isFG) ? "#0f766e" : "#050505";
                                                            // "Inspect" is the CONVERT action (Quality release step);
                                                            // "Confirm" is the CREATE action (new confirmation record).
                                                            const hasActionPermission = label === "Inspect" ? canConvert : canEdit;
                                                            if (!hasActionPermission) return null;

                                                            return (
                                                                <button
                                                                    disabled={!isConfirmEnabled}
                                                                    style={{
                                                                        padding: "5px 14px",
                                                                        fontSize: 12,
                                                                        fontWeight: 600,
                                                                        border: isConfirmEnabled ? `1.5px solid ${bgColor}` : "1.5px solid #e2e8f0",
                                                                        borderRadius: 6,
                                                                        background: isConfirmEnabled ? bgColor : "#f1f5f9",
                                                                        color: isConfirmEnabled ? "#fff" : "#94a3b8",
                                                                        cursor: isConfirmEnabled ? "pointer" : "not-allowed",
                                                                        transition: "all 0.15s"
                                                                    }}
                                                                    onMouseEnter={e => {
                                                                        if (isConfirmEnabled) {
                                                                            e.currentTarget.style.opacity = "0.85";
                                                                        }
                                                                    }}
                                                                    onMouseLeave={e => {
                                                                        if (isConfirmEnabled) {
                                                                            e.currentTarget.style.opacity = "1";
                                                                        }
                                                                    }}
                                                                    onClick={() => isConfirmEnabled && openConfirm(o.production_order_number)}
                                                                >
                                                                    {label}
                                                                </button>
                                                            );
                                                        })()}
                                                    </>
                                                )}
                                            </div>
                                                );
                                            })()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <CharacteristicsModal
                    modalVisible={showOutputCharModal}
                    selectedCharacteristics={outputBatchCharacteristics}
                    isEditing={true}
                    handleChange={(idx, val) => {
                        const updated = [...outputBatchCharacteristics];
                        updated[idx] = { ...updated[idx], value: val };
                        setOutputBatchCharacteristics(updated);
                    }}
                    handleClose={() => {
                        const factorRow = outputBatchCharacteristics.find(
                            c => (c.characteristic || "").toLowerCase() === "factor"
                        );
                        if (factorRow && factorRow.value) {
                            const parsed = Math.max(1, parseInt(factorRow.value, 10) || 1);
                            setBatchFactor(parsed);
                        }
                        setShowOutputCharModal(false);
                    }}
                    batchFactor={batchFactor}
                    onFactorChange={(val) => setBatchFactor(Math.max(1, parseInt(val) || 1))}
                />

                {showFgBatchModal && (() => {
                    const fgIsEditable = isConfirmMode || isEditMode;
                    // Column order = exactly the characteristic columns as fetched
                    // from the characteristics table (No. of Drum/Coil, Length of
                    // the Drum/Coil, Batch Number, ...). "Batch Number" is one of
                    // those fetched characteristics — it is rendered read-only /
                    // auto-generated instead of a user-editable input. Nothing is
                    // added or duplicated.
                    const fgColumns = fgDisplayColumns.map((c) => ({
                        type: (c || "").toLowerCase().includes("batch") ? "batch" : "char",
                        key: c
                    }));

                    return (
                    <div style={styles.modalBackdrop} onClick={() => setShowFgBatchModal(false)}>
                        <div style={{ ...styles.modal, width: 720, maxWidth: "95%" }} onClick={(e) => e.stopPropagation()}>
                            <div style={{ ...styles.modalHeader, justifyContent: "flex-end" }}>
                                <button onClick={() => setShowFgBatchModal(false)} style={{ border: "none", background: "transparent", fontSize: 18, cursor: "pointer" }}>×</button>
                            </div>
                            <div style={{ padding: "12px 20px" }}>
                                <div style={{ maxHeight: "50vh", overflowY: "auto" }}>
                                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                    <thead>
                                        <tr>
                                            {fgColumns.map((c, i) => (
                                                <th key={i} style={{ fontSize: 12, fontWeight: 600, padding: "6px 8px", borderBottom: "1px solid #e5e7eb", textAlign: "left", color: "#1e293b", position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
                                                    {c.type === "batch" ? "Batch Number" : c.key}
                                                </th>
                                            ))}
                                            {fgIsEditable && <th style={{ width: 36, borderBottom: "1px solid #e5e7eb", position: "sticky", top: 0, background: "#fff", zIndex: 1 }} />}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {/* Already produced / saved batches — read-only, greyed out */}
                                        {savedOutputBatches.filter(b => !modalData.confirmationId || b.confirmation_id === modalData.confirmationId).map((b, i) => (
                                            <tr key={`saved-${i}`}>
                                                {fgColumns.map((c, ci) => {
                                                    if (c.type === "batch") {
                                                        return (
                                                            <td key={ci} style={{ padding: "6px 8px", borderBottom: "1px solid #f1f5f9" }}>
                                                                <input
                                                                    type="text"
                                                                    readOnly
                                                                    disabled
                                                                    value={b.batch_number || ""}
                                                                    style={{ width: "100%", padding: "4px 6px", fontSize: 12, border: "1px solid #e5e7eb", borderRadius: 4, background: "#f1f5f9", color: "#64748b", cursor: "not-allowed" }}
                                                                />
                                                            </td>
                                                        );
                                                    }
                                                    const match = (b.characteristics || []).find((ch) => ch.characteristic === c.key);
                                                    return (
                                                        <td key={ci} style={{ padding: "6px 8px", borderBottom: "1px solid #f1f5f9" }}>
                                                            <input
                                                                type="text"
                                                                readOnly
                                                                disabled
                                                                value={match ? match.value : ""}
                                                                style={{ width: "100%", padding: "4px 6px", fontSize: 12, border: "1px solid #e5e7eb", borderRadius: 4, background: "#f1f5f9", color: "#64748b", cursor: "not-allowed" }}
                                                            />
                                                        </td>
                                                    );
                                                })}
                                                {fgIsEditable && <td style={{ padding: "6px 8px", borderBottom: "1px solid #f1f5f9" }} />}
                                            </tr>
                                        ))}

                                        {/* New entries being produced now — editable */}
                                        {fgIsEditable && fgBatchRows.map((row, idx) => (
                                            <tr key={`new-${idx}`}>
                                                {fgColumns.map((c, ci) => {
                                                    if (c.type === "batch") {
                                                        return (
                                                            <td key={ci} style={{ fontSize: 12, padding: "6px 8px", borderBottom: "1px solid #f1f5f9", color: "#94a3b8", fontStyle: "italic" }}>
                                                                Auto-generated on save
                                                            </td>
                                                        );
                                                    }
                                                    const isToleranceCol = isFgOverToleranceCol(c.key) || isFgUnderToleranceCol(c.key);
                                                    const isDrumCol = c.key === fgDrumColumn;
                                                    const isLengthCol = c.key === fgLengthColumn;
                                                    return (
                                                        <td key={ci} style={{ padding: "6px 8px", borderBottom: "1px solid #f1f5f9" }}>
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                readOnly={isToleranceCol}
                                                                disabled={isToleranceCol}
                                                                style={{
                                                                    width: "100%", padding: "4px 6px", fontSize: 12, borderRadius: 4,
                                                                    border: isToleranceCol ? "1px solid #e5e7eb" : "1px solid #d1d5db",
                                                                    background: isToleranceCol ? "#f1f5f9" : "#fff",
                                                                    color: isToleranceCol ? "#64748b" : "inherit",
                                                                    cursor: isToleranceCol ? "not-allowed" : "text",
                                                                }}
                                                                placeholder="Enter value"
                                                                value={row[c.key] ?? ""}
                                                                onChange={(e) => {
                                                                    if (isToleranceCol) return;
                                                                    const updated = [...fgBatchRows];
                                                                    updated[idx] = { ...updated[idx], [c.key]: e.target.value };
                                                                    setFgBatchRows(updated);
                                                                }}
                                                                onBlur={(e) => {
                                                                    if (!fgToleranceEnabled) return;
                                                                    const raw = parseFloat(e.target.value);
                                                                    if (isNaN(raw)) return;

                                                                    // ── No. of Drum/Coil: never let this row push the
                                                                    // running total past what the order defines.
                                                                    if (isDrumCol) {
                                                                        const otherRowsSum = fgDrumSum(
                                                                            fgBatchRows.filter((_, i2) => i2 !== idx)
                                                                        );
                                                                        const remaining = Math.max(0, fgRemainingDrumCount - otherRowsSum);
                                                                        if (raw > remaining) {
                                                                            toast.warn(
                                                                                `Only ${remaining} Drum/Coil remaining to produce (order requires ${fgOrderDrumCount} total, ${fgAlreadyProducedDrums} already produced).`
                                                                            );
                                                                            const updated = [...fgBatchRows];
                                                                            updated[idx] = { ...updated[idx], [c.key]: String(remaining) };
                                                                            setFgBatchRows(updated);
                                                                        }
                                                                        return;
                                                                    }

                                                                    // ── Length of the Drum/Coil: must sit inside the
                                                                    // order's Range (Over Tolerance – Under Tolerance).
                                                                    if (isLengthCol && fgToleranceBounds.over !== "" && fgToleranceBounds.under !== "") {
                                                                        const lower = parseFloat(fgToleranceBounds.over);
                                                                        const upper = parseFloat(fgToleranceBounds.under);
                                                                        if (raw < lower || raw > upper) {
                                                                            toast.warn(`Length must be between ${lower} and ${upper} (this order's Range).`);
                                                                            const clamped = raw < lower ? lower : upper;
                                                                            const updated = [...fgBatchRows];
                                                                            updated[idx] = { ...updated[idx], [c.key]: String(clamped) };
                                                                            setFgBatchRows(updated);
                                                                        }
                                                                    }
                                                                }}
                                                            />
                                                        </td>
                                                    );
                                                })}
                                                <td style={{ padding: "6px 8px", borderBottom: "1px solid #f1f5f9", textAlign: "center" }}>
                                                    {fgBatchRows.length > 1 && (
                                                        <button
                                                            title="Remove row"
                                                            onClick={() => setFgBatchRows(fgBatchRows.filter((_, i) => i !== idx))}
                                                            style={{ border: "none", background: "transparent", color: "#dc2626", cursor: "pointer", fontSize: 14 }}
                                                        >
                                                            ×
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}

                                        {savedOutputBatches.length === 0 && !fgIsEditable && fgBatchRows.length === 0 && (
                                            <tr>
                                                <td colSpan={fgColumns.length} style={{ textAlign: "center", padding: 16, color: "#94a3b8", fontStyle: "italic", fontSize: 12 }}>
                                                    No batches produced yet.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                                </div>

                                {fgIsEditable && (
                                    <button
                                        onClick={() => setFgBatchRows([...fgBatchRows, Object.fromEntries(fgDisplayColumns.map(c => [c, fgDefaultValueFor(c)]))])}
                                        style={{ marginTop: 10, padding: "5px 12px", fontSize: 12, fontWeight: 600, border: "1px solid #d1d5db", borderRadius: 6, background: "#f8fafc", cursor: "pointer" }}
                                    >
                                        + Add Row
                                    </button>
                                )}
                            </div>
                            <div style={styles.modalFooter}>
                                <button
                                    onClick={() => setShowFgBatchModal(false)}
                                    style={{ background: "#e2e8f0", border: "none", padding: "6px 18px", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 500 }}
                                >
                                    Close
                                </button>
                                {fgIsEditable && (
                                    <button
                                        onClick={() => setShowFgBatchModal(false)}
                                        style={{ background: "#2563eb", color: "#fff", border: "none", padding: "6px 18px", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600 }}
                                    >
                                        Save
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                    );
                })()}

                <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
            </div>
        </Layout>
    );
};

const styles = {
    compactLabel: { display: "block", fontSize: "12px", fontWeight: 600, marginBottom: "3px", color: "#475569" },
    tableContainer: { background: "#ffffff", borderRadius: 8, padding: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", overflowX: "auto" },
    erpTable: { width: "100%", tableLayout: "auto", borderCollapse: "collapse", minWidth: "1200px" },
    erpTh: { background: "#f8fafc", fontSize: 13, fontWeight: 600, padding: "10px 12px", borderBottom: "1px solid #e5e7eb", color: "#1e293b", whiteSpace: "nowrap" },
    erpTd: { fontSize: 13, padding: "10px 12px", borderBottom: "1px solid #f1f5f9", color: "#334155", verticalAlign: "middle" },
    modalBackdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050, padding: '20px' },
    scrollableContent: { flex: 1, overflowY: "auto", padding: "0 16px", scrollbarWidth: "none", msOverflowStyle: "none" },
    modal: { background: '#fff', borderRadius: '8px', width: '90%', maxWidth: '1200px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: "16px", fontSize: "14px" },
    modalHeader: { padding: '15px 20px', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    modalFooter: { padding: '15px 20px', borderTop: '1px solid #ddd', display: 'flex', justifyContent: 'flex-end', gap: '10px' },
};

export default Confirmation;